import { useState, useCallback, useMemo, useEffect } from "react";
import { supabase } from "./supabase";

// ─── CONSTANTES PAR DÉFAUT ───────────────────────────────────────────────────
const DEFAULT_CAT_DEPENSES = ["Loyer","Salaires","Charges sociales (CGSS)","Fournisseurs","Achats marchandises","TVA","Impôts et taxes","Abonnements","Remboursement emprunt","Charges fixes","Frais bancaires","Divers dépenses"];
const DEFAULT_CAT_RECETTES = ["Ventes / CA","Règlement client","Acompte client","Subvention","Prêt / Apport","Remboursement","Divers recettes"];
const STATUTS_FACTURE = ["En attente","Relancé","Réglé","Litigieux","Hors relance"];
const ETAPES_RELANCE = ["Relevé de compte","1ère relance","2ème relance","Mise en demeure"];
const TYPES_ACTION = ["Relevé envoyé","Relance envoyée","Appel téléphonique","Email","Mise en demeure","Paiement reçu","Note interne","Rappel planifié","Import données","Autre"];
const FREQUENCES = ["Mensuel","Trimestriel","Annuel"];

const fmt = n => new Intl.NumberFormat("fr-FR",{style:"currency",currency:"EUR",maximumFractionDigits:2}).format(n||0);
const fmtDate = d => d ? new Date(d).toLocaleDateString("fr-FR") : "-";
const calcRetard=d=>{if(!d) return 0;const today=new Date();today.setHours(0,0,0,0);const ech=new Date(d);ech.setHours(0,0,0,0);return Math.max(0,Math.floor((today-ech)/(1000*60*60*24)));};
// eslint-disable-next-line no-unused-vars
const ancienneteLabel = {"a_echoir":"À échoir","1_30j":"Échu 1-30j","31_60j":"Échu 31-60j","61_90j":"Échu 61-90j","plus_90j":"Échu +90j"};
const ancienneteOrder = {a_echoir:0,"1_30j":1,"31_60j":2,"61_90j":3,"plus_90j":4};

// ─── PARSER BALANCE ÂGÉE EBP ─────────────────────────────────────────────────
// Supporte deux formats :
// Format 1 (ancien) : AccountNumber;FullName;JJ/MM/AA;...;tranches
// Format 2 (EBP export) : DataDonnées_de_l_échéancier_AccountNumber;...;Text45-49
// Les deux formats peuvent avoir plusieurs lignes par document → on les additionne
function parseBalanceAgeeEBP(text, societeId, entrepriseId) {
  const rawLines = text.trim().split(/\r?\n/);
  if (rawLines.length < 2) return { creances: [], factures: [] };
  const sep = rawLines[0].includes(";") ? ";" : ",";
  const headers = rawLines[0].split(sep).map(h=>h.replace(/^"|"$/g,"").trim());
  // eslint-disable-next-line no-unused-vars
  const isEBPFormat = headers[0].includes("DataDonnées") || headers[0].includes("AccountNumber");

  const clientsMap = {};
  const docMap = {};
  const allFactures = []; // groupement par compte+document pour additionner les montants
  const today = new Date();
  today.setHours(0,0,0,0);

  const parseMt = v => {
    const s=(v||"").replace(/[\s\u00a0\u202f]/g,"").replace(",",".");
    return s===""?0:parseFloat(s)||0;
  };

  const parseDate = (v, delaiJours=30) => {
    if(!v||!String(v).trim()) return {dateFac:null,dateEch:null};
    const raw=String(v).trim();
    let dateFac=null;
    if(/^\d{4,6}$/.test(raw)){
      // Format numérique Excel
      const n=parseInt(raw);
      const d=new Date(Date.UTC(1899,11,30));d.setUTCDate(d.getUTCDate()+n);
      dateFac=d.toISOString().split("T")[0];
    } else {
      const p=raw.split(/[/-]/);
      if(p.length!==3) return {dateFac:null,dateEch:null};
      const y=p[2].length===2?"20"+p[2]:p[2];
      dateFac=`${y}-${p[1].padStart(2,"0")}-${p[0].padStart(2,"0")}`;
    }
    const d=new Date(dateFac);
    if(isNaN(d.getTime())) return {dateFac:null,dateEch:null};
    d.setDate(d.getDate()+delaiJours);
    return {dateFac, dateEch:d.toISOString().split("T")[0]};
  };

  const ancsEBP=["a_echoir","1_30j","31_60j","61_90j","plus_90j"];

  for (const line of rawLines.slice(1)) {
    const cols = line.split(sep).map(c=>c.replace(/^"|"$/g,"").trim());
    if(!cols[0]||!cols[0].startsWith("411")) continue;
    const nomClient = cols[1];
    if(!nomClient) continue;
    const numeroCompte = cols[0];
    const numDoc = cols[5]||cols[4]||"";
    if(!numDoc) continue;

    let montant=0, anc="plus_90j";
    for(let i=0;i<=4;i++){
      const m=parseMt(cols[8+i]||"");
      if(m!==0){montant=m;anc=ancsEBP[i];break;}
    }
    if(montant===0) continue;

    const key=`${numeroCompte}__${numDoc}`;
    if(docMap[key]){
      // Additionner le montant pour ce document
      docMap[key].montant+=montant;
    } else {
      const {dateFac:df,dateEch:de}=parseDate(cols[2],30);
      docMap[key]={
        numeroCompte,nomClient,dateFac:df,dateEch:de,
        numDoc,numPiece:cols[4]||"",
        label:cols[6]||nomClient,journal:cols[3]||"VE",moyen:cols[7]||"",
        montant,anc
      };
    }
  }

  for(const entry of Object.values(docMap)){
    const {numeroCompte,nomClient,dateFac,dateEch,numDoc,numPiece,label,journal,moyen,montant,anc}=entry;
    let joursRetard=0,anciennete=anc;
    if(dateEch){
      const ech=new Date(dateEch);ech.setHours(0,0,0,0);
      joursRetard=Math.max(0,Math.floor((today-ech)/(1000*60*60*24)));
      anciennete=joursRetard===0?"a_echoir":joursRetard<=30?"1_30j":joursRetard<=60?"31_60j":joursRetard<=90?"61_90j":"plus_90j";
    }
    const isAvoir=montant<0;
    if(!clientsMap[numeroCompte]) clientsMap[numeroCompte]={
      numero_compte:numeroCompte,nom_client:nomClient,societe_id:societeId,
      entreprise_id:entrepriseId,date_import:new Date().toISOString().split("T")[0],
      statut:"En attente",etape_relance:"Relevé de compte",solde:0
    };
    clientsMap[numeroCompte].solde+=montant;
    allFactures.push({
      numero_compte:numeroCompte,nom_client:nomClient,
      numero_piece:numPiece,numero_document:numDoc,
      libelle:label,journal,moyen_paiement:moyen,
      date_facture:dateFac,date_ecriture:dateFac,date_echeance:dateEch,
      date_import:new Date().toISOString().split("T")[0],
      montant,anciennete,jours_retard:joursRetard,
      statut:"En attente",montant_regle:0,solde_restant:montant,
      societe_id:societeId,entreprise_id:entrepriseId,is_avoir:isAvoir
    });
  }

  const creances=Object.values(clientsMap).map(c=>({...c,etape_relance:"Relevé de compte",solde:Math.abs(c.solde)}));
  return {creances,factures:allFactures};
}

// ─── PARSER CSV FACTURES (import enrichi) ────────────────────────────────────
// ─── PARSER CSV FACTURES ─────────────────────────────────────────────────────
// Colonnes attendues : Date facture ; N° facture ; N° compte ; Client ; Montant ; Date d'echeance
function parseFacturesCSV(text, societeId, entrepriseId) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if(lines.length < 2) return [];
  const sep = lines[0].includes(";") ? ";" : ",";
  const headers = lines[0].split(sep).map(h=>h.replace(/^"|"$/g,"").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,""));
  const get = (cols,key) => { const i=headers.findIndex(h=>h.includes(key)); return i>=0?(cols[i]||"").replace(/^"|"$/g,"").trim():""; };
  const parseMt = v => parseFloat((v||"").replace(/\s/g,"").replace(",",".").replace(/[^0-9.-]/g,""))||0;
  const parseDate = v => { if(!v) return null; const p=v.trim().split(/[/-]/); if(p.length!==3) return null; if(p[2].length===4) return `${p[2]}-${p[1].padStart(2,"0")}-${p[0].padStart(2,"0")}`; return `20${p[2]}-${p[1].padStart(2,"0")}-${p[0].padStart(2,"0")}`; };
  const today=new Date().toISOString().split("T")[0];
  const result = [];
  for(const line of lines.slice(1)){
    const cols = line.split(sep);
    const numDoc = get(cols,"n° fac")||get(cols,"n°fac")||get(cols,"num fac")||get(cols,"facture")||get(cols,"piece")||get(cols,"num");
    const montant = parseMt(get(cols,"montant")||get(cols,"total")||get(cols,"amount"));
    if(!numDoc||montant===0) continue;
    const numCompte = (get(cols,"compte")||"").replace(/\s/g,"");
    if(numCompte && !numCompte.startsWith("411")) continue;
    const dateFac = parseDate(get(cols,"date fac")||get(cols,"date facture")||get(cols,"date_fac"));
    const dateEch = parseDate(get(cols,"echeance")||get(cols,"ech")||get(cols,"due")||get(cols,"date d")||get(cols,"date e"));
    const joursRetard = dateEch ? Math.max(0,Math.floor((new Date(today)-new Date(dateEch))/(1000*60*60*24))) : 0;
    const anciennete = joursRetard===0?"a_echoir":joursRetard<=30?"1_30j":joursRetard<=60?"31_60j":joursRetard<=90?"61_90j":"plus_90j";
    const nomClient = get(cols,"client")||get(cols,"nom")||"";
    result.push({
      numero_document:numDoc, numero_piece:numDoc, numero_compte:numCompte||null,
      nom_client:nomClient, libelle:nomClient||numDoc,
      date_facture:dateFac, date_echeance:dateEch,
      montant, anciennete, jours_retard:joursRetard,
      statut:"En attente", montant_regle:0, solde_restant:montant,
      societe_id:societeId, entreprise_id:entrepriseId,
      is_avoir:montant<0, date_import:today
    });
  }
  return result;
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  sidebar:{width:220,background:"#1F2937",minHeight:"100vh",display:"flex",flexDirection:"column",flexShrink:0},
  main:{flex:1,background:"#F9FAFB",minHeight:"100vh",overflow:"auto"},
  card:{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:24},
  cardSm:{background:"#fff",border:"1px solid #E5E7EB",borderRadius:10,padding:16},
  input:{border:"1px solid #D1D5DB",borderRadius:8,padding:"9px 12px",fontSize:13,width:"100%",fontFamily:"inherit",background:"#fff",color:"#111827",outline:"none"},
  inputSm:{border:"1px solid #D1D5DB",borderRadius:6,padding:"6px 10px",fontSize:12,fontFamily:"inherit",background:"#fff",color:"#111827",outline:"none"},
  btnPrimary:{background:"#F97316",color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"},
  btnSecondary:{background:"#fff",color:"#374151",border:"1px solid #D1D5DB",borderRadius:8,padding:"9px 16px",fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"inherit"},
  btnDanger:{background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer",fontFamily:"inherit"},
  btnGreen:{background:"#F0FDF4",color:"#16A34A",border:"1px solid #BBF7D0",borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer",fontFamily:"inherit"},
  btnFilter:{background:"#F3F4F6",color:"#374151",border:"1px solid #E5E7EB",borderRadius:6,padding:"6px 12px",fontSize:12,fontWeight:500,cursor:"pointer",fontFamily:"inherit"},
  th:{padding:"10px 14px",fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.8,textAlign:"left",background:"#F9FAFB",borderBottom:"1px solid #E5E7EB"},
  td:{padding:"11px 14px",fontSize:13,color:"#374151",borderBottom:"1px solid #F3F4F6"},
  label:{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:5,display:"block"},
  modal:{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"},
  modalBox:{background:"#fff",borderRadius:16,padding:32,maxWidth:560,width:"100%",maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"},
};

const tag = (type) => {
  const map={"En attente":{bg:"#FEF3C7",c:"#92400E"},"Relancé":{bg:"#FED7AA",c:"#C2410C"},"Réglé":{bg:"#D1FAE5",c:"#065F46"},"Litigieux":{bg:"#EDE9FE",c:"#5B21B6"},"RECETTE":{bg:"#D1FAE5",c:"#065F46"},"DÉPENSE":{bg:"#FEE2E2",c:"#991B1B"},"administrateur":{bg:"#DBEAFE",c:"#1E40AF"},"utilisateur":{bg:"#FEF3C7",c:"#92400E"},"a_echoir":{bg:"#DBEAFE",c:"#1E40AF"},"1_30j":{bg:"#FEF3C7",c:"#92400E"},"31_60j":{bg:"#FED7AA",c:"#C2410C"},"61_90j":{bg:"#FEE2E2",c:"#991B1B"},"plus_90j":{bg:"#FEE2E2",c:"#7F1D1D"},"Confirmé":{bg:"#D1FAE5",c:"#065F46"},"Incertain":{bg:"#FEF3C7",c:"#92400E"}};
  const s=map[type]||{bg:"#F3F4F6",c:"#374151"};
  return {display:"inline-block",padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:s.bg,color:s.c};
};

function FilterBar({children}){return <div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",flexWrap:"wrap",gap:10,alignItems:"flex-end"}}>{children}</div>;}
function FF({label,children}){return <div style={{display:"flex",flexDirection:"column",gap:3}}><span style={{fontSize:10,fontWeight:600,color:"#9CA3AF",textTransform:"uppercase",letterSpacing:0.5}}>{label}</span>{children}</div>;}
function Modal({title,onClose,children}){return <div style={S.modal} onClick={e=>{if(e.target===e.currentTarget)onClose();}}><div style={S.modalBox}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}><div style={{fontSize:16,fontWeight:700,color:"#111827"}}>{title}</div><button onClick={onClose} style={{...S.btnFilter,padding:"4px 10px"}}>✕</button></div>{children}</div></div>;}

// ─── APP PRINCIPALE ───────────────────────────────────────────────────────────
// ─── COMPOSANT CARNET DE BORD ────────────────────────────────────────────────
function CarnetDeBord({taches,societes,isAdmin,user,tacheOnglet,setTacheOnglet,tacheFiltres,setTacheFiltres,showNewTache,setShowNewTache,newTache,setNewTache,addTache,updateTacheStatut,deleteTache,TYPES_ACTION,S,utilisateurs,creances,clientSearch,setClientSearch,showClientList,setShowClientList,onSyncHistorique,selectedSociete}){
  const today=new Date().toISOString().split("T")[0];
  const tachesMesSociete=taches.filter(t=>{
    // Profil utilisateur : ses tâches uniquement
    if(!isAdmin&&t.assignee_nom!==user?.nom) return false;
    // Filtre société globale (sidebar)
    if(selectedSociete&&selectedSociete!=="all"&&t.societe_id&&t.societe_id!==selectedSociete) return false;
    // Profil utilisateur : limité à sa société
    if(!isAdmin&&user?.societe_id&&t.societe_id&&t.societe_id!==user.societe_id) return false;
    return true;
  });
  const tachesFiltrees=tachesMesSociete.filter(t=>{
    if(tacheFiltres.societe&&t.societe_id!==tacheFiltres.societe) return false;
    if(tacheFiltres.user&&t.assignee_nom!==tacheFiltres.user) return false;
    if(tacheFiltres.type&&t.type_action!==tacheFiltres.type) return false;
    return true;
  });
  const actives=tachesFiltrees.filter(t=>t.statut!=="Terminé").sort((a,b)=>(a.date_prevue||"").localeCompare(b.date_prevue||""));
  const terminees=tachesFiltrees.filter(t=>t.statut==="Terminé").sort((a,b)=>(b.date_prevue||"").localeCompare(a.date_prevue||""));
  const enRetard=actives.filter(t=>t.date_prevue<today).length;
  const aujourdhui=actives.filter(t=>t.date_prevue===today).length;
  const getStyle=(t)=>{
    if(t.statut==="Terminé") return {bg:"#D1FAE5",color:"#065F46",label:"Terminé"};
    if(t.date_prevue<today) return {bg:"#FEE2E2",color:"#DC2626",label:"En retard"};
    if(t.date_prevue===today) return {bg:"#FEF3C7",color:"#D97706",label:"Aujourd'hui"};
    return {bg:"#F3F4F6",color:"#6B7280",label:"À faire"};
  };
  const renderTache=(t)=>{
    const st=getStyle(t);
    return(
      <div key={t.id} style={{display:"grid",gridTemplateColumns:"2.2fr 1fr 1fr 1fr 1fr 80px",gap:0,padding:"11px 16px",borderBottom:"1px solid #F3F4F6",alignItems:"center",background:t.date_prevue<today&&t.statut!=="Terminé"?"#FFF5F5":"#fff"}}>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:"#111827",textDecoration:t.statut==="Terminé"?"line-through":"none"}}>{t.titre}</div>
          <div style={{fontSize:11,color:"#9CA3AF",marginTop:2}}>
            {t.origine==="historique"?"📋 Depuis historique":t.origine==="planifiée"?"📅 Planifiée":"✏️ Manuelle"}
            {t.client_nom?` · ${t.client_nom}`:""}
            {t.description?<span title={t.description}> — {t.description.slice(0,50)}{t.description.length>50?"...":""}</span>:null}
          </div>
        </div>
        <div style={{fontSize:12,color:"#6B7280"}}>{societes.find(s=>s.id===t.societe_id)?.nom||"—"}</div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <div style={{width:22,height:22,borderRadius:"50%",background:"#DBEAFE",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#1E40AF"}}>{(t.assignee_nom||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
          <span style={{fontSize:12,color:"#6B7280"}}>{t.assignee_nom||"—"}</span>
        </div>
        <div style={{fontSize:12,color:st.color,fontWeight:t.date_prevue<=today&&t.statut!=="Terminé"?600:400}}>
          {t.date_prevue?new Date(t.date_prevue).toLocaleDateString("fr-FR"):"—"}
        </div>
        <div style={{fontSize:11,padding:"3px 8px",borderRadius:20,background:st.bg,color:st.color,fontWeight:600,display:"inline-block"}}>{st.label}</div>
        <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
          {t.statut!=="Terminé"&&<button className="bh" title="Marquer terminé" onClick={()=>updateTacheStatut(t.id,"Terminé")} style={{background:"none",border:"none",cursor:"pointer",color:"#16A34A",padding:4,fontSize:16}}>✓</button>}
          <button className="bh" title="Supprimer" onClick={()=>deleteTache(t.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#DC2626",padding:4,fontSize:14}}>✕</button>
        </div>
      </div>
    );
  };
  return(
    <div style={{padding:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:"#111827"}}>Carnet de bord</div>
          <div style={{fontSize:13,color:"#6B7280"}}>Suivi des actions réalisées et à réaliser</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="bh" onClick={onSyncHistorique} style={{...S.btnFilter,fontSize:12,display:"flex",alignItems:"center",gap:6}}>↻ Importer l'historique</button>
          <button className="bh" onClick={()=>setShowNewTache(true)} style={{...S.btnPrimary,display:"flex",alignItems:"center",gap:6}}>+ Nouvelle tâche</button>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
        {[{label:"En retard",val:enRetard,color:"#DC2626",bg:"#FEE2E2"},{label:"Aujourd'hui",val:aujourdhui,color:"#D97706",bg:"#FEF3C7"},{label:"À venir",val:Math.max(0,actives.length-enRetard-aujourdhui),color:"#374151",bg:"#F9FAFB"},{label:"Terminées",val:terminees.length,color:"#16A34A",bg:"#D1FAE5"}].map(k=>(
          <div key={k.label} style={{background:k.bg,borderRadius:10,padding:"12px 16px"}}>
            <div style={{fontSize:11,color:k.color,fontWeight:600,marginBottom:4}}>{k.label}</div>
            <div style={{fontSize:24,fontWeight:700,color:k.color}}>{k.val}</div>
          </div>
        ))}
      </div>
      {isAdmin&&<div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        <select style={S.input} value={tacheFiltres.societe} onChange={e=>setTacheFiltres(p=>({...p,societe:e.target.value}))}>
          <option value="">Toutes les sociétés</option>
          {societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}
        </select>
        <select style={S.input} value={tacheFiltres.user} onChange={e=>setTacheFiltres(p=>({...p,user:e.target.value}))}>
          <option value="">Tous les utilisateurs</option>
          {utilisateurs.map(u=><option key={u} value={u}>{u}</option>)}
        </select>
        <select style={S.input} value={tacheFiltres.type} onChange={e=>setTacheFiltres(p=>({...p,type:e.target.value}))}>
          <option value="">Tous les types</option>
          {TYPES_ACTION.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        {(tacheFiltres.societe||tacheFiltres.user||tacheFiltres.type)&&<button className="bh" onClick={()=>setTacheFiltres({societe:"",user:"",type:""})} style={{...S.btnFilter,color:"#DC2626"}}>✕ Reset</button>}
      </div>}
      <div style={{display:"flex",gap:0,marginBottom:0,borderBottom:"2px solid #E5E7EB"}}>
        {[{id:"actives",label:`Actives (${actives.length})`},{id:"terminees",label:`Terminées (${terminees.length})`}].map(o=>(
          <button key={o.id} className="bh" onClick={()=>setTacheOnglet(o.id)} style={{padding:"10px 20px",fontSize:13,fontWeight:tacheOnglet===o.id?600:400,color:tacheOnglet===o.id?"#F97316":"#6B7280",background:"none",border:"none",borderBottom:tacheOnglet===o.id?"3px solid #F97316":"3px solid transparent",cursor:"pointer",marginBottom:-2}}>
            {o.label}
          </button>
        ))}
      </div>
      <div style={{background:"#fff",border:"1px solid #E5E7EB",borderTop:"none",borderRadius:"0 0 12px 12px",overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"2.2fr 1fr 1fr 1fr 1fr 80px",padding:"8px 16px",background:"#F9FAFB",borderBottom:"1px solid #E5E7EB"}}>
          {["TÂCHE / CLIENT","SOCIÉTÉ","ASSIGNÉE À","DATE PRÉVUE","STATUT",""].map((h,i)=><span key={i} style={{fontSize:11,fontWeight:600,color:"#6B7280"}}>{h}</span>)}
        </div>
        {(tacheOnglet==="actives"?actives:terminees).length===0
          ?<div style={{textAlign:"center",padding:32,color:"#9CA3AF",fontSize:14}}>{tacheOnglet==="actives"?"Aucune tâche active 🎉":"Aucune tâche terminée"}</div>
          :(tacheOnglet==="actives"?actives:terminees).map(renderTache)
        }
      </div>
      {showNewTache&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999}}>
        <div style={{background:"#fff",borderRadius:16,padding:28,width:560,maxWidth:"95vw",boxShadow:"0 8px 32px rgba(0,0,0,0.15)"}}>
          <div style={{fontSize:16,fontWeight:700,color:"#111827",marginBottom:20}}>Nouvelle tâche</div>
          <div style={{marginBottom:12}}><label style={S.label}>Titre *</label><input style={S.input} placeholder="Ex: Rappeler M. Dupont..." value={newTache.titre} onChange={e=>setNewTache(p=>({...p,titre:e.target.value}))} /></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={S.label}>Type d'action</label>
              <select style={S.input} value={newTache.type_action} onChange={e=>setNewTache(p=>({...p,type_action:e.target.value}))}>
                {TYPES_ACTION.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Date prévue *</label><input type="date" style={S.input} value={newTache.date_prevue} onChange={e=>setNewTache(p=>({...p,date_prevue:e.target.value}))} /></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            {isAdmin&&<div><label style={S.label}>Assigner à</label>
              <select style={S.input} value={newTache.assignee_nom} onChange={e=>setNewTache(p=>({...p,assignee_nom:e.target.value}))}>
                <option value="">Moi-même</option>
                {utilisateurs.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>}
            <div><label style={S.label}>Société</label>
              <select style={S.input} value={newTache.societe_id||""} onChange={e=>setNewTache(p=>({...p,societe_id:e.target.value}))}>
                <option value="">Toutes</option>
                {societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            </div>
          </div>
          <div style={{marginBottom:12,position:"relative"}}>
            <label style={S.label}>Client concerné (optionnel)</label>
            <input style={S.input} placeholder="Rechercher un client..." value={clientSearch}
              onChange={e=>{setClientSearch(e.target.value);setShowClientList(true);setNewTache(p=>({...p,client_nom:e.target.value,creance_id:null}));}}
              onFocus={()=>setShowClientList(true)}
              autoComplete="off"
            />
            {showClientList&&clientSearch.length>=2&&(()=>{
              const societeFiltre=newTache.societe_id;
              const matches=creances.filter(c=>c.nom_client?.toLowerCase().includes(clientSearch.toLowerCase())&&(!societeFiltre||c.societe_id===societeFiltre)).slice(0,8);
              if(!matches.length) return null;
              return <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid #E5E7EB",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",zIndex:999,maxHeight:220,overflowY:"auto"}}>
                {matches.map(c=>{
                  const soc=societes.find(s=>s.id===c.societe_id);
                  const autresSoc=creances.filter(x=>x.nom_client===c.nom_client&&x.id!==c.id);
                  return <div key={c.id} style={{padding:"8px 12px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #F3F4F6",display:"flex",justifyContent:"space-between",alignItems:"center"}}
                    className="rh"
                    onClick={()=>{
                      setClientSearch(c.nom_client);
                      setNewTache(p=>({...p,client_nom:c.nom_client,creance_id:c.id,societe_id:autresSoc.length===0?c.societe_id:p.societe_id}));
                      setShowClientList(false);
                    }}>
                    <span>{c.nom_client}</span>
                    <span style={{fontSize:11,color:"#9CA3AF"}}>{soc?.nom||""}{autresSoc.length>0?" ⚠️ multi-entités":""}</span>
                  </div>;
                })}
              </div>;
            })()}
          </div>
          {newTache.creance_id&&(()=>{
            const cre=creances.find(c=>c.id===newTache.creance_id);
            const autresSoc=creances.filter(c=>c.nom_client===cre?.nom_client&&c.id!==cre?.id);
            if(autresSoc.length===0) return null;
            return <div style={{marginBottom:12,padding:"8px 12px",background:"#FEF3C7",borderRadius:8,fontSize:12,color:"#D97706",border:"1px solid #FDE68A"}}>
              ⚠️ Ce client existe dans plusieurs sociétés — sélectionnez la bonne société ci-dessus
            </div>;
          })()}
          <div style={{marginBottom:20}}><label style={S.label}>Description</label><textarea style={{...S.input,height:60,resize:"vertical"}} placeholder="Détails..." value={newTache.description} onChange={e=>setNewTache(p=>({...p,description:e.target.value}))} /></div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button className="bh" onClick={()=>setShowNewTache(false)} style={S.btnFilter}>Annuler</button>
            <button className="bh" onClick={()=>{
              if(!newTache.assignee_nom) setNewTache(p=>({...p,assignee_nom:user?.nom||""}));
              addTache();
            }} style={S.btnPrimary}>Créer la tâche</button>
          </div>
        </div>
      </div>}
    </div>
  );
}

// ─── COMPOSANT STATISTIQUES ──────────────────────────────────────────────────
function StatistiquesPage({isAdmin,user,societes,histoGlobal,taches,loading,
  periode,setPeriode,dateDeb,setDateDeb,dateFin,setDateFin,onLoad,S,selectedSociete,creances}){

  const today=new Date().toISOString().split("T")[0];

  // Calculer les dates selon la période
  const getDates=()=>{
    const now=new Date();
    if(periode==="semaine"){
      const d=new Date(now);d.setDate(d.getDate()-7);
      return {deb:d.toISOString().split("T")[0],fin:today};
    }
    if(periode==="mois"){
      const d=new Date(now);d.setMonth(d.getMonth()-1);
      return {deb:d.toISOString().split("T")[0],fin:today};
    }
    if(periode==="trimestre"){
      const d=new Date(now);d.setMonth(d.getMonth()-3);
      return {deb:d.toISOString().split("T")[0],fin:today};
    }
    return {deb:dateDeb,fin:dateFin};
  };
  const {deb,fin}=getDates();

  // Déterminer les créances de la société sélectionnée
  const creanceIdsSoc = selectedSociete&&selectedSociete!=="all"
    ? (creances||[]).filter(c=>c.societe_id===selectedSociete).map(c=>c.id)
    : null; // null = toutes

  // Filtrer l'historique sur la période ET la société
  const histoPeriode=histoGlobal.filter(h=>{
    const d=h.created_at?.split("T")[0]||h.date_action||"";
    if(deb&&d<deb) return false;
    if(fin&&d>fin) return false;
    if(!isAdmin&&h.auteur!==user?.nom) return false;
    // Filtre société via creance_id
    if(creanceIdsSoc&&!creanceIdsSoc.includes(h.creance_id)) return false;
    return true;
  });

  // Tâches sur la période ET la société
  const tachesPeriode=taches.filter(t=>{
    const d=t.created_at?.split("T")[0]||"";
    if(deb&&d<deb) return false;
    if(fin&&d>fin) return false;
    if(!isAdmin&&t.assignee_nom!==user?.nom) return false;
    if(selectedSociete&&selectedSociete!=="all"&&t.societe_id!==selectedSociete) return false;
    return true;
  });

  // Grouper par auteur
  const auteurs=isAdmin
    ? [...new Set(histoPeriode.map(h=>h.auteur).filter(Boolean))]
    : [user?.nom||""];

  const getStats=(auteur)=>{
    const histo=histoPeriode.filter(h=>h.auteur===auteur);
    const tachesUser=tachesPeriode.filter(t=>t.assignee_nom===auteur);
    const releves=histo.filter(h=>h.type_action==="Relevé envoyé").length;
    const relances=histo.filter(h=>h.type_action==="Relance envoyée").length;
    const appels=histo.filter(h=>h.type_action==="Appel téléphonique").length;
    const med=histo.filter(h=>h.type_action==="Mise en demeure").length;
    const rapprochements=histo.filter(h=>h.type_action==="Paiement reçu").length;
    const notes=histo.filter(h=>h.type_action==="Note interne"&&!h.annotation?.startsWith("[PLANIFIÉ]")).length;
    return {
      releves, relances, appels, med, rapprochements, notes,
      total:releves+relances+appels+med+rapprochements+notes,
      tachesTerminees:tachesUser.filter(t=>t.statut==="Terminé").length,
      tachesTotal:tachesUser.length,
    };
  };

  // Évolution par semaine sur la période
  const getEvolution=()=>{
    const weeks={};
    histoPeriode.forEach(h=>{
      const d=new Date(h.created_at?.split("T")[0]||h.date_action||today);
      const lundi=new Date(d);lundi.setDate(d.getDate()-d.getDay()+1);
      const key=lundi.toISOString().split("T")[0];
      if(!weeks[key]) weeks[key]={semaine:key,releves:0,relances:0,appels:0,rapprochements:0};
      if(h.type_action==="Relevé envoyé") weeks[key].releves++;
      if(h.type_action==="Relance envoyée") weeks[key].relances++;
      if(h.type_action==="Appel téléphonique") weeks[key].appels++;
      if(h.type_action==="Paiement reçu") weeks[key].rapprochements++;
    });
    return Object.values(weeks).sort((a,b)=>a.semaine.localeCompare(b.semaine));
  };

  const evolution=getEvolution();
  const maxEvo=Math.max(...evolution.map(e=>e.releves+e.relances+e.appels+e.rapprochements),1);

  const KPI=({label,val,color,bg})=>(
    <div style={{background:bg||"#F9FAFB",borderRadius:10,padding:"12px 16px",minWidth:100}}>
      <div style={{fontSize:11,color:color||"#6B7280",fontWeight:600,marginBottom:4}}>{label}</div>
      <div style={{fontSize:26,fontWeight:700,color:color||"#111827"}}>{val}</div>
    </div>
  );

  const COLORS={"Relevé envoyé":"#6366F1","Relance envoyée":"#F97316","Appel téléphonique":"#0EA5E9","Mise en demeure":"#DC2626","Paiement reçu":"#16A34A","Note interne":"#9CA3AF"};

  return(
    <div style={{padding:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:700,color:"#111827"}}>Statistiques d'activité</div>
          <div style={{fontSize:13,color:"#6B7280"}}>
            {isAdmin
              ?(selectedSociete&&selectedSociete!=="all"
                ?`${societes.find(s=>s.id===selectedSociete)?.nom||""} — tous les utilisateurs`
                :"Vue consolidée — toutes les sociétés")
              :"Mes statistiques"}
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {["semaine","mois","trimestre","custom"].map(p=>(
            <button key={p} className="bh" onClick={()=>setPeriode(p)}
              style={{...S.btnFilter,fontSize:12,padding:"5px 12px",
                background:periode===p?"#111827":"",color:periode===p?"#fff":"#374151",fontWeight:periode===p?600:400}}>
              {p==="semaine"?"7 jours":p==="mois"?"30 jours":p==="trimestre"?"90 jours":"Personnalisé"}
            </button>
          ))}
          {periode==="custom"&&<>
            <input type="date" value={dateDeb} onChange={e=>setDateDeb(e.target.value)} style={{...S.input,width:140,fontSize:12}} />
            <input type="date" value={dateFin} onChange={e=>setDateFin(e.target.value)} style={{...S.input,width:140,fontSize:12}} />
          </>}
          <button className="bh" onClick={onLoad} style={{...S.btnPrimary,fontSize:12,padding:"5px 14px"}}>
            {loading?"⏳ Chargement...":"↻ Actualiser"}
          </button>
        </div>
      </div>

      {loading&&<div style={{textAlign:"center",padding:40,color:"#9CA3AF"}}>Chargement des données...</div>}

      {!loading&&histoPeriode.length===0&&<div style={{textAlign:"center",padding:40,color:"#9CA3AF",fontSize:14}}>
        Aucune activité sur cette période.<br/>
        <span style={{fontSize:12}}>Cliquez sur "Actualiser" pour charger les données.</span>
      </div>}

      {!loading&&histoPeriode.length>0&&<>

        {/* KPIs globaux */}
        <div style={{display:"flex",gap:10,marginBottom:24,flexWrap:"wrap"}}>
          <KPI label="Total actions" val={histoPeriode.length} color="#111827" bg="#F9FAFB" />
          <KPI label="Relevés envoyés" val={histoPeriode.filter(h=>h.type_action==="Relevé envoyé").length} color="#6366F1" bg="#EEF2FF" />
          <KPI label="Relances" val={histoPeriode.filter(h=>h.type_action==="Relance envoyée").length} color="#F97316" bg="#FFF7ED" />
          <KPI label="Appels" val={histoPeriode.filter(h=>h.type_action==="Appel téléphonique").length} color="#0EA5E9" bg="#E0F2FE" />
          <KPI label="Rapprochements" val={histoPeriode.filter(h=>h.type_action==="Paiement reçu").length} color="#16A34A" bg="#D1FAE5" />
          <KPI label="Mises en demeure" val={histoPeriode.filter(h=>h.type_action==="Mise en demeure").length} color="#DC2626" bg="#FEE2E2" />
        </div>

        {/* Évolution hebdomadaire */}
        {evolution.length>1&&<div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:20,marginBottom:24}}>
          <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:16}}>Évolution de l'activité</div>
          <div style={{display:"flex",gap:4,alignItems:"flex-end",height:120}}>
            {evolution.map(e=>{
              const total=e.releves+e.relances+e.appels+e.rapprochements;
              const h=Math.max(4,Math.round((total/maxEvo)*100));
              const date=new Date(e.semaine).toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit"});
              return <div key={e.semaine} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{fontSize:10,color:"#6B7280",fontWeight:500}}>{total}</div>
                <div style={{width:"100%",height:h,background:"linear-gradient(180deg,#6366F1,#F97316)",borderRadius:"4px 4px 0 0"}}></div>
                <div style={{fontSize:9,color:"#9CA3AF",textAlign:"center"}}>{date}</div>
              </div>;
            })}
          </div>
          <div style={{display:"flex",gap:12,marginTop:12,flexWrap:"wrap"}}>
            {[["Relevés","#6366F1"],["Relances","#F97316"],["Appels","#0EA5E9"],["Rapprochements","#16A34A"]].map(([l,c])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#6B7280"}}>
                <div style={{width:10,height:10,borderRadius:2,background:c}}></div>{l}
              </div>
            ))}
          </div>
        </div>}

        {/* Tableau par utilisateur */}
        <div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",background:"#F9FAFB",borderBottom:"1px solid #E5E7EB",fontSize:14,fontWeight:600,color:"#111827"}}>
            {isAdmin?"Activité par utilisateur":"Mon activité"}
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{background:"#111827"}}>
                  {["Utilisateur","Total","Relevés","Relances","Appels","MED","Rapproch.","Tâches faites","Taux tâches"].map(h=>(
                    <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:11,fontWeight:600,color:"#fff",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {auteurs.length===0&&<tr><td colSpan={9} style={{padding:24,textAlign:"center",color:"#9CA3AF"}}>Aucune activité trouvée</td></tr>}
                {auteurs.map((auteur,idx)=>{
                  const st=getStats(auteur);
                  const taux=st.tachesTotal>0?Math.round((st.tachesTerminees/st.tachesTotal)*100):null;
                  return <tr key={auteur} style={{background:idx%2===0?"#fff":"#F9FAFB",borderTop:"1px solid #F3F4F6"}}>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"#111827"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:28,height:28,borderRadius:"50%",background:"#DBEAFE",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#1E40AF"}}>
                          {auteur.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                        </div>
                        {auteur}
                      </div>
                    </td>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:"#111827"}}>{st.total}</td>
                    <td style={{padding:"10px 12px"}}><span style={{fontSize:12,padding:"2px 8px",borderRadius:20,background:"#EEF2FF",color:"#6366F1",fontWeight:600}}>{st.releves}</span></td>
                    <td style={{padding:"10px 12px"}}><span style={{fontSize:12,padding:"2px 8px",borderRadius:20,background:"#FFF7ED",color:"#F97316",fontWeight:600}}>{st.relances}</span></td>
                    <td style={{padding:"10px 12px"}}><span style={{fontSize:12,padding:"2px 8px",borderRadius:20,background:"#E0F2FE",color:"#0EA5E9",fontWeight:600}}>{st.appels}</span></td>
                    <td style={{padding:"10px 12px"}}><span style={{fontSize:12,padding:"2px 8px",borderRadius:20,background:st.med>0?"#FEE2E2":"#F3F4F6",color:st.med>0?"#DC2626":"#9CA3AF",fontWeight:600}}>{st.med}</span></td>
                    <td style={{padding:"10px 12px"}}><span style={{fontSize:12,padding:"2px 8px",borderRadius:20,background:"#D1FAE5",color:"#16A34A",fontWeight:600}}>{st.rapprochements}</span></td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"#374151"}}>{st.tachesTerminees} / {st.tachesTotal}</td>
                    <td style={{padding:"10px 12px"}}>
                      {taux!==null
                        ?<div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div style={{flex:1,height:6,borderRadius:3,background:"#E5E7EB"}}>
                              <div style={{width:`${taux}%`,height:"100%",borderRadius:3,background:taux>=80?"#16A34A":taux>=50?"#F97316":"#DC2626"}}></div>
                            </div>
                            <span style={{fontSize:11,color:"#6B7280",minWidth:30}}>{taux}%</span>
                          </div>
                        :<span style={{fontSize:11,color:"#9CA3AF"}}>—</span>
                      }
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Répartition par type d'action */}
        <div style={{marginTop:24,background:"#fff",border:"1px solid #E5E7EB",borderRadius:12,padding:20}}>
          <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:16}}>Répartition par type d'action</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {Object.entries(COLORS).map(([type,color])=>{
              const count=histoPeriode.filter(h=>h.type_action===type).length;
              if(count===0) return null;
              const pct=Math.round((count/histoPeriode.length)*100);
              return <div key={type} style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:140,fontSize:12,color:"#374151",textAlign:"right",flexShrink:0}}>{type}</div>
                <div style={{flex:1,height:18,background:"#F3F4F6",borderRadius:9}}>
                  <div style={{width:`${pct}%`,height:"100%",background:color,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"flex-end",paddingRight:6}}>
                    {pct>10&&<span style={{fontSize:10,color:"#fff",fontWeight:700}}>{pct}%</span>}
                  </div>
                </div>
                <div style={{width:30,fontSize:12,fontWeight:600,color:"#374151"}}>{count}</div>
              </div>;
            })}
          </div>
        </div>

      </>}
    </div>
  );
}

function App() {
  // ── AUTH & DONNÉES ──────────────────────────────────────────────────────────
  const [user,setUser]=useState(null);
  const [entreprise,setEntreprise]=useState(null);
  const [societes,setSocietes]=useState([]);
  const [selectedSociete,setSelectedSociete]=useState("all");
  const [page,setPage]=useState("dashboard");
  const [transactions,setTransactions]=useState([]);
  const [previsionnel,setPrevisionnel]=useState([]);
  const [creances,setCreances]=useState([]);
  const [factures,setFactures]=useState([]);
  const [recurrents,setRecurrents]=useState([]);
  const [users,setUsers]=useState([]);
  const [clients,setClients]=useState([]); // fiches clients
  const [comptesBancaires,setComptesBancaires]=useState([]);
  const [categoriesCustom,setCategoriesCustom]=useState({recettes:[],depenses:[]});
  const [loading,setLoading]=useState(false);
  const [toast,setToast]=useState(null);
  const [setupMode,setSetupMode]=useState(false);

  // ── FORMULAIRES ─────────────────────────────────────────────────────────────
  const [loginForm,setLoginForm]=useState({email:"",mot_de_passe:""});
  const [loginError,setLoginError]=useState("");
  const [setupForm,setSetupForm]=useState({nom:"",seuil_critique:2000,seuil_vigilance:5000});
  const [newTx,setNewTx]=useState({date:"",libelle:"",type:"RECETTE",categorie:"Ventes / CA",montant:"",societe_id:"",compte_bancaire_id:""});
  const [newPrev,setNewPrev]=useState({date:"",libelle:"",type:"DÉPENSE",categorie:"Divers dépenses",montant:"",statut:"Confirmé",societe_id:""});
  const [newRecurrent,setNewRecurrent]=useState({libelle:"",type:"DÉPENSE",categorie:"Divers dépenses",montant:"",frequence:"Mensuel",date_debut:"",date_fin:"",societe_id:""});
  const [newUser,setNewUser]=useState({nom:"",email:"",mot_de_passe:"",role:"utilisateur",societe_id:null});
  const [newSociete,setNewSociete]=useState({nom:"",code:"",email_recouvrement:"",signature_email:"",logo_url:""});
  const [editSociete,setEditSociete]=useState(null); // {id, ...champs} pour modification
  const [newCompte,setNewCompte]=useState({libelle:"",banque:"",iban:"",numero_compte_comptable:"",societe_id:"",principal:false});
  const [newCatRecette,setNewCatRecette]=useState("");
  const [newCatDepense,setNewCatDepense]=useState("");

  // ── UI STATES ────────────────────────────────────────────────────────────────
  const [selectedCreance,setSelectedCreance]=useState(null);
  const [newAnnotation,setNewAnnotation]=useState({type_action:"Note interne",annotation:"",montant_regle:0});
  const [rappelActif,setRappelActif]=useState(false);
  const [rappelMode,setRappelMode]=useState("jours"); // "jours" | "date"
  const [rappelJours,setRappelJours]=useState(2);
  const [rappelDate,setRappelDate]=useState("");
  const [rappelTitre,setRappelTitre]=useState("");
  const [taches,setTaches]=useState([]);
  const [showNewTache,setShowNewTache]=useState(false);
  const [newTache,setNewTache]=useState({titre:"",type_action:"Appel téléphonique",date_prevue:"",assignee_id:"",assignee_nom:"",societe_id:"",client_nom:"",description:"",origine:"manuelle",creance_id:null});
  const [tacheOnglet,setTacheOnglet]=useState("actives"); // "actives" | "terminees"
  const [tacheFiltres,setTacheFiltres]=useState({societe:"",user:"",type:""});
  const [statsPeriode,setStatsPeriode]=useState("mois"); // "semaine"|"mois"|"trimestre"|"custom"
  const [statsDateDeb,setStatsDateDeb]=useState("");
  const [statsDateFin,setStatsDateFin]=useState("");
  const [statsHistoGlobal,setStatsHistoGlobal]=useState([]);
  const [statsLoading,setStatsLoading]=useState(false);
  const [importProgress,setImportProgress]=useState(null); // {step,total,current,label}
  const [importsLog,setImportsLog]=useState([]);
  const [clientSearch,setClientSearch]=useState("");
  const [showClientList,setShowClientList]=useState(false);
  const [historique,setHistorique]=useState([]);
  const [editTx,setEditTx]=useState(null);
  const [importResult,setImportResult]=useState(null);
  const [prevTab,setPrevTab]=useState("liste");
  const [editRecurrent,setEditRecurrent]=useState(null);
  const [showRecurrentModal,setShowRecurrentModal]=useState(null); // transaction à convertir en récurrent
  const [newRecurrentFromTx,setNewRecurrentFromTx]=useState({frequence:"Mensuel",date_fin:""});
  const [regles,setRegles]=useState([]); // règles de catégorisation
  const [newRegle,setNewRegle]=useState({mot_cle:"",categorie:"Divers dépenses",type:"DÉPENSE"});
  const [configTab,setConfigTab]=useState("societes");
  const [dragOver,setDragOver]=useState(false);
  const [dragOverBalance,setDragOverBalance]=useState(false);
  const [dragOverFac,setDragOverFac]=useState(false);
  const [societeImportTx,setSocieteImportTx]=useState("");
  const [societeImportBal,setSocieteImportBal]=useState("");
  const [societeImportFac,setSocieteImportFac]=useState("");
  const [showFicheClient,setShowFicheClient]=useState(null); // modal fiche client
  const [editFicheClient,setEditFicheClient]=useState(null);
  const [showPrevReglement,setShowPrevReglement]=useState(null); // modal prévision règlement
  const [newPrevReglement,setNewPrevReglement]=useState({date:"",montant:""});
  const [sortCre,setSortCre]=useState({col:"nom_client",dir:"asc"});

  // ── FILTRES ──────────────────────────────────────────────────────────────────
  const [fDashPeriode,setFDashPeriode]=useState(""); // eslint-disable-line no-unused-vars
  const [dashDateDeb,setDashDateDeb]=useState("");
  const [dashDateFin,setDashDateFin]=useState("");
  const [fCreClient,setFCreClient]=useState("");
  const [fCreAnciennete,setFCreAnciennete]=useState("");
  const [fCreMontantOp,setFCreMontantOp]=useState("");
  const [fCreMontant,setFCreMontant]=useState("");
  const [fCreStatut,setFCreStatut]=useState("");
  const [fRelAnciennete,setFRelAnciennete]=useState("");
  const [fRelClient,setFRelClient]=useState("");
  const [fRelSociete,setFRelSociete]=useState("");
  const [fRelStatut,setFRelStatut]=useState("");
  const [sortRel,setSortRel]=useState({col:"nom_client",dir:"asc"});
  const [showReglementModal,setShowReglementModal]=useState(null); // facture à régler
  const [montantPartiel,setMontantPartiel]=useState("");
  const [notesFacture,setNotesFacture]=useState({}); // {factureId: [{texte,auteur,date}]}
  const [showNotesFacture,setShowNotesFacture]=useState(null); // factureId affiché
  const [showReleveModal,setShowReleveModal]=useState(null);
  const [showFacturesReglees,setShowFacturesReglees]=useState({});
  const [sortFac,setSortFac]=useState({col:"date_facture",dir:"asc"}); // {creanceId: bool} // creance pour relevé
  const [emailCC,setEmailCC]=useState(""); // destinataires supplémentaires
  const [emailPJ,setEmailPJ]=useState(null); // pièce jointe {name, base64, type}
  const [dashModal,setDashModal]=useState(null); // {type, items} modal détail dashboard
  const [sendingEmail,setSendingEmail]=useState(false); // état envoi email relance
  const [showRapprochement,setShowRapprochement]=useState(null); // transaction à rapprocher
  const [showRapprochementCreance,setShowRapprochementCreance]=useState(null);
  const [showAvoirModal,setShowAvoirModal]=useState(null);
  // eslint-disable-next-line no-unused-vars
  const [inclureHorsRelance,setInclureHorsRelance]=useState(false);
  const [rapprochSearch,setRapprochSearch]=useState(""); // recherche client dans rapprochement
  const [rapprochSelected,setRapprochSelected]=useState(null); // créance sélectionnée
  const [rapprochFacs,setRapprochFacs]=useState({}); // {factureId: montantARégler}
  const [newNoteFacture,setNewNoteFacture]=useState("");
  const [fTxDate1,setFTxDate1]=useState("");
  const [fTxDate2,setFTxDate2]=useState("");
  const [fTxType,setFTxType]=useState("");
  const [fTxCat,setFTxCat]=useState("");
  const [fTxSoc,setFTxSoc]=useState("");
  const [fTxCompte,setFTxCompte]=useState("");
  const [fTxLibelle,setFTxLibelle]=useState("");
  const [fTxMontantOp,setFTxMontantOp]=useState("");
  const [fTxMontant,setFTxMontant]=useState("");

  const showToast=(msg,type="success")=>{setToast({msg,type});setTimeout(()=>setToast(null),3500);};

  // ── CATÉGORIES (défaut + custom) ─────────────────────────────────────────────
  const catsRecettes = useMemo(()=>[...DEFAULT_CAT_RECETTES,...(categoriesCustom.recettes||[])],[categoriesCustom]);
  const catsDepenses = useMemo(()=>[...DEFAULT_CAT_DEPENSES,...(categoriesCustom.depenses||[])],[categoriesCustom]);

  useEffect(()=>{checkSetup();},[]);// eslint-disable-line react-hooks/exhaustive-deps

  async function checkSetup(){
    // Vérifier si une session Supabase Auth existe déjà
    const {data:{session}}=await supabase.auth.getSession();
    if(session){
      // Session active — récupérer le profil
      const {data:profil}=await supabase.from("utilisateurs").select("*").eq("auth_id",session.user.id).limit(1);
      if(profil&&profil.length>0){
        setUser(profil[0]);
        if(profil[0].societe_id) setSelectedSociete(profil[0].societe_id);
        await loadAll(profil[0].entreprise_id);
        return;
      }
    }
    const {data}=await supabase.from("entreprises").select("id").limit(1);
    if(!data||data.length===0) setSetupMode(true);
  }

  async function createEntreprise(){
    if(!setupForm.nom){showToast("Nom obligatoire","error");return;}
    const {data,error}=await supabase.from("entreprises").insert([{nom:setupForm.nom,seuil_critique:Number(setupForm.seuil_critique),seuil_vigilance:Number(setupForm.seuil_vigilance)}]).select();
    if(error||!data){showToast("Erreur création","error");return;}
    await supabase.from("utilisateurs").insert([{entreprise_id:data[0].id,email:"admin@admin.com",mot_de_passe:"admin123",role:"administrateur",nom:"Administrateur"}]);
    setSetupMode(false);
    showToast("Espace créé — connectez-vous avec admin@admin.com / admin123");
  }

  async function handleLogin(){
    setLoginError("");
    // Auth Supabase native — mot de passe hashé, session sécurisée
    const {data:authData,error:authError}=await supabase.auth.signInWithPassword({
      email:loginForm.email,
      password:loginForm.mot_de_passe
    });
    if(authError||!authData.user){setLoginError("Email ou mot de passe incorrect");return;}
    // Récupérer le profil utilisateur dans notre table
    const {data:profil,error:profilError}=await supabase.from("utilisateurs").select("*").eq("auth_id",authData.user.id).limit(1);
    if(profilError||!profil||profil.length===0){
      // Fallback : chercher par email (migration progressive)
      const {data:profilEmail}=await supabase.from("utilisateurs").select("*").eq("email",loginForm.email).limit(1);
      if(!profilEmail||profilEmail.length===0){setLoginError("Profil utilisateur introuvable");await supabase.auth.signOut();return;}
      setUser(profilEmail[0]);
      if(profilEmail[0].societe_id) setSelectedSociete(profilEmail[0].societe_id);
      await loadAll(profilEmail[0].entreprise_id);
      return;
    }
    setUser(profil[0]);
    if(profil[0].societe_id) setSelectedSociete(profil[0].societe_id);
    await loadAll(profil[0].entreprise_id);
  }

  async function loadAll(eid){
    setLoading(true);
    try{
      const [entR,socR,txR,prevR,creR,facR,usrR,recR,cliR,cptR,catR,regR,tachR,logR]=await Promise.all([
        supabase.from("entreprises").select("*").eq("id",eid).single(),
        supabase.from("societes").select("*").eq("entreprise_id",eid).eq("actif",true),
        supabase.from("transactions").select("*").eq("entreprise_id",eid).order("created_at",{ascending:false}),
        supabase.from("previsionnel").select("*").eq("entreprise_id",eid).order("date",{ascending:true}),
        supabase.from("creances").select("*").eq("entreprise_id",eid).order("created_at",{ascending:false}),
        supabase.from("factures").select("*").eq("entreprise_id",eid).order("date_echeance",{ascending:true}),
        supabase.from("utilisateurs").select("id,nom,email,role,societe_id").eq("entreprise_id",eid),
        supabase.from("recurrents").select("*").eq("entreprise_id",eid).order("created_at",{ascending:false}),
        supabase.from("clients").select("*").eq("entreprise_id",eid),
        supabase.from("comptes_bancaires").select("*").eq("entreprise_id",eid),
        supabase.from("categories_custom").select("*").eq("entreprise_id",eid).limit(1),
        supabase.from("regles_categorisation").select("*").eq("entreprise_id",eid).order("created_at",{ascending:true}),
        supabase.from("taches").select("*").eq("entreprise_id",eid).order("date_prevue",{ascending:true}),
        supabase.from("imports_log").select("*").eq("entreprise_id",eid).order("created_at",{ascending:false}).limit(50),
      ]);
      if(entR.data) setEntreprise(entR.data);
      if(socR.data) setSocietes(socR.data);
      if(txR.data) setTransactions(txR.data);
      if(prevR.data) setPrevisionnel(prevR.data);
      if(creR.data) setCreances(creR.data);
      if(facR.data) setFactures(facR.data);
      if(usrR.data) setUsers(usrR.data);
      if(recR.data) setRecurrents(recR.data);
      if(cliR.data) setClients(cliR.data);
      if(cptR.data) setComptesBancaires(cptR.data);
      if(catR.data&&catR.data.length>0) setCategoriesCustom({recettes:catR.data[0].recettes||[],depenses:catR.data[0].depenses||[]});
      if(regR.data) setRegles(regR.data);
      if(tachR?.data) setTaches(tachR.data);
      if(logR?.data) setImportsLog(logR.data);
    }catch(e){showToast("Erreur chargement","error");}
    setLoading(false);
  }

  // ── DONNÉES FILTRÉES BASE ────────────────────────────────────────────────────
  const baseTx=useMemo(()=>selectedSociete==="all"?transactions:transactions.filter(t=>t.societe_id===selectedSociete),[transactions,selectedSociete]);
  const baseCre=useMemo(()=>selectedSociete==="all"?creances:creances.filter(c=>c.societe_id===selectedSociete),[creances,selectedSociete]);
  const baseFac=useMemo(()=>{
    const today=new Date();today.setHours(0,0,0,0);
    return (selectedSociete==="all"?factures:factures.filter(f=>f.societe_id===selectedSociete)).map(f=>{
      // Recalculer le retard dynamiquement depuis date_echeance si disponible
      // Pour les balances EBP, date_echeance = date_facture → utiliser anciennete comme base
      let jr=0,anc=f.anciennete||"a_echoir";
      if(f.date_echeance&&f.date_echeance!==f.date_facture){
        // Vraie date d'échéance distincte → calcul exact
        const ech=new Date(f.date_echeance);ech.setHours(0,0,0,0);
        jr=Math.max(0,Math.floor((today-ech)/(1000*60*60*24)));
        anc=jr===0?"a_echoir":jr<=30?"1_30j":jr<=60?"31_60j":jr<=90?"61_90j":"plus_90j";
      } else if(f.date_echeance){
        // date_echeance = date_facture (balance EBP) → recalculer depuis date_facture
        const ech=new Date(f.date_echeance);ech.setHours(0,0,0,0);
        jr=Math.max(0,Math.floor((today-ech)/(1000*60*60*24)));
        anc=jr===0?"a_echoir":jr<=30?"1_30j":jr<=60?"31_60j":jr<=90?"61_90j":"plus_90j";
      }
      return {...f,jours_retard:jr,anciennete:anc};
    });
  },[factures,selectedSociete]);
  const basePrev=useMemo(()=>selectedSociete==="all"?previsionnel:previsionnel.filter(p=>p.societe_id===selectedSociete),[previsionnel,selectedSociete]);

  const matchPeriode=(dateStr,periode)=>{if(!periode)return true;if(!dateStr)return false;const yyyy=periode.slice(0,4),mm=periode.slice(5,7);if(dateStr.includes("-"))return dateStr.startsWith(periode);return dateStr.slice(3,5)===mm&&dateStr.slice(6,10)===yyyy;};

  // ── FILTRE DASHBOARD ─────────────────────────────────────────────────────────
  const filteredTxDash=useMemo(()=>{
    let r=baseTx;
    if(fDashPeriode) r=r.filter(t=>matchPeriode(t.date,fDashPeriode));
    return r;
  },[baseTx,fDashPeriode]);

  // ── FILTRE CRÉANCES ──────────────────────────────────────────────────────────
  const filteredCre=useMemo(()=>{
    let r=baseCre;
    if(fCreClient) r=r.filter(c=>c.nom_client.toLowerCase().includes(fCreClient.toLowerCase()));
    if(fCreStatut) r=r.filter(c=>c.statut===fCreStatut);
    if(fCreAnciennete){
      r=r.filter(c=>{
        const facs=factures.filter(f=>f.creance_id===c.id&&!f.is_avoir&&f.statut!=="Réglé");
        const maxAnc=facs.reduce((m,f)=>(ancienneteOrder[f.anciennete]||0)>(ancienneteOrder[m]||0)?f.anciennete:m,"a_echoir");
        return maxAnc===fCreAnciennete;
      });
    }
    if(fCreMontant&&fCreMontantOp){const v=parseFloat(fCreMontant);if(!isNaN(v)) r=r.filter(c=>fCreMontantOp===">"?c.solde>v:c.solde<v);}
    // Tri
    r=[...r].sort((a,b)=>{
      let va=a[sortCre.col]||"",vb=b[sortCre.col]||"";
      if(sortCre.col==="solde"){va=parseFloat(va)||0;vb=parseFloat(vb)||0;}
      const cmp=typeof va==="number"?va-vb:String(va).localeCompare(String(vb),"fr");
      return sortCre.dir==="asc"?cmp:-cmp;
    });
    return r;
  },[baseCre,fCreClient,fCreStatut,fCreAnciennete,fCreMontant,fCreMontantOp,factures,sortCre]);

  // ── FILTRE RELANCES ──────────────────────────────────────────────────────────
  const filteredFacRel=useMemo(()=>{
    let r=baseFac.filter(f=>f.statut!=="Réglé"&&f.statut!=="Hors relance"&&!f.is_avoir);
    if(fRelAnciennete) r=r.filter(f=>f.anciennete===fRelAnciennete);
    if(fRelClient) r=r.filter(f=>(f.nom_client||"").toLowerCase().includes(fRelClient.toLowerCase())||(f.numero_document||"").toLowerCase().includes(fRelClient.toLowerCase()));
    if(fRelSociete) r=r.filter(f=>f.societe_id===fRelSociete);
    if(fRelStatut) r=r.filter(f=>f.statut===fRelStatut);
    r=[...r].sort((a,b)=>{
      let va=a[sortRel.col]||"",vb=b[sortRel.col]||"";
      if(sortRel.col==="solde_restant"||sortRel.col==="montant"){va=parseFloat(va)||0;vb=parseFloat(vb)||0;}
      const cmp=typeof va==="number"?va-vb:String(va).localeCompare(String(vb),"fr");
      return sortRel.dir==="asc"?cmp:-cmp;
    });
    return r;
  },[baseFac,fRelAnciennete,fRelClient,fRelSociete,fRelStatut,sortRel]);

  // ── FILTRE TRANSACTIONS ──────────────────────────────────────────────────────
  const isAdmin = user?.role==="administrateur";

  const filteredTx=useMemo(()=>{
    // Profil utilisateur : encaissements uniquement
    let r=!isAdmin ? baseTx.filter(t=>t.type==="RECETTE") : baseTx;
    const norm=d=>{if(!d)return"";if(d.includes("/")){const p=d.split("/");return`${p[2]}-${p[1]}-${p[0]}`;}return d;};
    if(fTxDate1) r=r.filter(t=>norm(t.date)>=fTxDate1);
    if(fTxDate2) r=r.filter(t=>norm(t.date)<=fTxDate2);
    if(fTxType) r=r.filter(t=>t.type===fTxType);
    if(fTxCat) r=r.filter(t=>t.categorie===fTxCat);
    if(fTxSoc) r=r.filter(t=>t.societe_id===fTxSoc);
    if(fTxCompte) r=r.filter(t=>t.compte_bancaire_id===fTxCompte);
    if(fTxLibelle) r=r.filter(t=>t.libelle?.toLowerCase().includes(fTxLibelle.toLowerCase()));
    if(fTxMontant&&fTxMontantOp){const v=parseFloat(fTxMontant);if(!isNaN(v)) r=r.filter(t=>fTxMontantOp===">"?t.montant>v:t.montant<v);}
    // Trier par date décroissante (plus récentes en premier)
    return [...r].sort((a,b)=>{
      const da=a.date?.includes("/")?a.date.split("/").reverse().join("-"):a.date||"";
      const db=b.date?.includes("/")?b.date.split("/").reverse().join("-"):b.date||"";
      return db.localeCompare(da)||0;
    });
  },[baseTx,fTxDate1,fTxDate2,fTxType,fTxCat,fTxSoc,fTxCompte,fTxLibelle,fTxMontant,fTxMontantOp,isAdmin]);

  // ── KPIs ─────────────────────────────────────────────────────────────────────
  const totalRec=useMemo(()=>filteredTxDash.filter(t=>t.type==="RECETTE").reduce((s,t)=>s+t.montant,0),[filteredTxDash]);
  const totalDep=useMemo(()=>filteredTxDash.filter(t=>t.type==="DÉPENSE").reduce((s,t)=>s+t.montant,0),[filteredTxDash]);
  const solde=totalRec-totalDep;
  const totalCre=useMemo(()=>baseCre.filter(c=>c.statut!=="Réglé").reduce((s,c)=>s+c.solde,0),[baseCre]);
  const facEnRetard=useMemo(()=>baseFac.filter(f=>f.anciennete!=="a_echoir"&&f.statut!=="Réglé"&&f.statut!=="Hors relance"&&!f.is_avoir),[baseFac]);
  const prevRec=basePrev.filter(t=>t.type==="RECETTE").reduce((s,t)=>s+t.montant,0);
  const prevDep=basePrev.filter(t=>t.type==="DÉPENSE").reduce((s,t)=>s+t.montant,0);
  const alerte=entreprise?(solde<entreprise.seuil_critique?"critique":solde<entreprise.seuil_vigilance?"vigilance":"ok"):"ok";
  const getSoc=id=>societes.find(s=>s.id===id)?.nom||"-";
  const getCompte=id=>comptesBancaires.find(c=>c.id===id)?.libelle||"-";
  const getFacturesCre=creanceId=>factures.filter(f=>f.creance_id===creanceId);
  const getFicheClient=creance=>clients.find(c=>c.numero_compte===creance?.numero_compte&&c.societe_id===creance?.societe_id);

  // ── TRANSACTIONS ─────────────────────────────────────────────────────────────
  async function addTx(){
    if(!newTx.date||!newTx.libelle||!newTx.montant){showToast("Remplir tous les champs obligatoires","error");return;}
    const {data}=await supabase.from("transactions").insert([{...newTx,montant:parseFloat(newTx.montant),entreprise_id:entreprise.id,source:"manuel"}]).select();
    if(data){setTransactions(p=>[data[0],...p]);setNewTx({date:"",libelle:"",type:"DÉPENSE",categorie:"Divers dépenses",montant:"",societe_id:"",compte_bancaire_id:""});showToast("Ajouté ✓");}
  }
  async function deleteTx(id){await supabase.from("transactions").delete().eq("id",id);setTransactions(p=>p.filter(t=>t.id!==id));}
  async function desaffecterRapprochement(txId){
    const tx=transactions.find(t=>t.id===txId);
    if(!tx) return;
    // Remettre les factures rapprochées en "En attente"
    const facRapprochees=factures.filter(f=>f.creance_id===tx.rapproche_creance_id&&f.statut==="Réglé"&&f.montant_regle>0);
    for(const fac of facRapprochees){
      const newSolde=(fac.solde_restant??0)+fac.montant_regle;
      const newMontantRegle=Math.max(0,(fac.montant_regle||0)-tx.montant);
      await supabase.from("factures").update({statut:"En attente",solde_restant:newSolde,montant_regle:newMontantRegle}).eq("id",fac.id);
      setFactures(p=>p.map(f=>f.id===fac.id?{...f,statut:"En attente",solde_restant:newSolde,montant_regle:newMontantRegle}:f));
    }
    // Libérer la transaction
    await supabase.from("transactions").update({rapproche:false,rapproche_creance_id:null,rapproche_client_nom:null}).eq("id",txId);
    setTransactions(p=>p.map(t=>t.id===txId?{...t,rapproche:false,rapproche_creance_id:null,rapproche_client_nom:null}:t));
    // Tracer dans l'historique
    if(tx.rapproche_creance_id){
      await tracerAction(tx.rapproche_creance_id,"Note interne",`Désaffectation du rapprochement — Transaction : ${tx.libelle} (${fmt(tx.montant)}) du ${tx.date} — Factures remises en attente`);
    }
    showToast("Rapprochement annulé ✓");
  }
  async function updateTxCat(id,categorie){await supabase.from("transactions").update({categorie}).eq("id",id);setTransactions(p=>p.map(t=>t.id===id?{...t,categorie}:t));setEditTx(null);}

  // ── IMPORT CSV BANQUE ────────────────────────────────────────────────────────
  const onFileDrop=useCallback((e,societeId)=>{
    e.preventDefault();setDragOver(false);
    const file=e.dataTransfer?.files[0]||e.target.files?.[0];
    if(!file) return;
    const reader=new FileReader();
    reader.onload=async ev=>{
      const lines=ev.target.result.trim().split(/\r?\n/).filter(Boolean);
      const toInsert=[];
      const sep=lines[0].includes(";")?";":",";
      const startIdx=lines[0].toLowerCase().replace(/[\u00e9\u00e8\u00ea]/g,"e").match(/date|libell|debit|credit/)?1:0;
      const parseMt=v=>parseFloat((v||"").replace(/\s/g,"").replace(/[^0-9.,-]/g,"").replace(",","."))||0;
      for(let i=startIdx;i<lines.length;i++){
        const cols=lines[i].split(sep).map(c=>c.replace(/^"|"$/g,"").trim());
        if(cols.length<3) continue;
        const debit=parseMt(cols[2]),credit=parseMt(cols[3]);
        if(!cols[1]||(debit===0&&credit===0)) continue;
        // Détection intelligente : certaines banques mettent les recettes en Débit
        // Si les deux colonnes ont des valeurs dans le fichier → standard (Crédit=RECETTE)
        // Si les libellés contiennent des mots-clés de recette en colonne Débit → inverser
        const libLower=(cols[1]||"").toLowerCase();
        const isRecetteLibelle=libLower.includes("reçu")||libLower.includes("recu")||libLower.includes("remise")||libLower.includes("encaissement")||libLower.includes("avoir");
        let type,montant;
        if(credit>0){type="RECETTE";montant=credit;}
        else if(debit>0&&isRecetteLibelle){type="RECETTE";montant=debit;}
        else{type="DÉPENSE";montant=debit;}
        if(montant<=0) continue;
        // Appliquer règles de catégorisation automatique
        let categorie=type==="RECETTE"?"R\u00e8glement client":"Divers d\u00e9penses";
        for(const r of regles){
          if(cols[1]&&cols[1].toLowerCase().includes(r.mot_cle.toLowerCase())){
            if(r.type===type){categorie=r.categorie;break;}
          }
        }
        // Lire colonne Compte (col 4) si présente — chercher le compte bancaire correspondant
        const libelleCompte=cols[4]?.trim()||"";
        const cptMatch=libelleCompte?comptesBancaires.find(c=>c.societe_id===societeId&&c.libelle?.toLowerCase()===libelleCompte.toLowerCase()):null;
        toInsert.push({date:cols[0],libelle:cols[1],type,categorie,montant,source:"csv",entreprise_id:entreprise.id,societe_id:societeId||null,compte_bancaire_id:cptMatch?.id||null});
      }
      if(!toInsert.length){showToast("Aucune ligne lisible","error");return;}
      const {data}=await supabase.from("transactions").insert(toInsert).select();
      if(data){setTransactions(p=>[...data,...p]);showToast(`${data.length} transactions import\u00e9es \u2713`);}
    };
    reader.readAsText(file,"UTF-8");
  },[entreprise,regles,comptesBancaires]);

  // ── IMPORT BALANCE ÂGÉE EBP ──────────────────────────────────────────────────
  const onBalanceDrop=useCallback((e,societeId)=>{
    e.preventDefault();setDragOverBalance(false);
    const file=e.dataTransfer?.files[0]||e.target.files?.[0];
    if(!file){showToast("Aucun fichier","error");return;}
    if(!societeId){showToast("Sélectionner une société","error");return;}
    const reader=new FileReader();
    const processBalance=async text=>{
      const nomFichier=file?.name||"balance_agee.csv";
      const {creances:parsedCre,factures:parsedFac}=parseBalanceAgeeEBP(text,societeId,entreprise.id);
      if(!parsedCre.length){
        await logImport({type_import:"Balance âgée",nom_fichier:nomFichier,statut:"erreur",message_erreur:"Aucun compte 411 trouvé",societe_id:societeId});
        showToast("Aucun compte 411 trouvé","error");return;
      }
      const totalSteps=parsedCre.length+parsedFac.length+3;
      let stepDone=0;
      const progress=(label)=>{stepDone++;setImportProgress({label,current:stepDone,total:totalSteps,pct:Math.round((stepDone/totalSteps)*100)});};
      setImportProgress({label:"Lecture du fichier...",current:0,total:totalSteps,pct:0});

      // Charger créances et factures existantes pour cette société
      const {data:creExist}=await supabase.from("creances").select("*").eq("societe_id",societeId).eq("entreprise_id",entreprise.id);
      const {data:facExist}=await supabase.from("factures").select("id,numero_document,statut,creance_id").eq("societe_id",societeId).eq("entreprise_id",entreprise.id);
      const creMapExist={};(creExist||[]).forEach(c=>{creMapExist[c.numero_compte]=c;});
      const facMapExist={};(facExist||[]).forEach(f=>{facMapExist[f.numero_document]=f;});

      let creesCount=0,majCreCount=0,inserFac=0,majFac=0;
      const creMap={};

      // ── Créances : créer ou mettre à jour ───────────────────────────────────
      for(const cre of parsedCre){
        const exist=creMapExist[cre.numero_compte];
        progress(`Traitement client ${cre.nom_client}...`);
        if(exist){
          // Mettre à jour solde, retard, ancienneté — préserver statut/etape si modifié
          await supabase.from("creances").update({
            solde:cre.solde, jours_retard:cre.jours_retard, anciennete:cre.anciennete,
            nom_client:cre.nom_client
          }).eq("id",exist.id);
          creMap[cre.numero_compte]=exist.id;
          majCreCount++;
        } else {
          const {data:newCre}=await supabase.from("creances").insert([cre]).select();
          if(newCre&&newCre.length>0){
            creMap[cre.numero_compte]=newCre[0].id;
            setCreances(p=>[...p,newCre[0]]);
            creesCount++;
          }
        }
      }

      // ── Factures : créer, mettre à jour, ou ignorer si Réglé ────────────────
      for(const fac of parsedFac){
        progress(`Import facture ${fac.numero_document}...`);
        const creanceId=creMap[fac.numero_compte]||null;
        const exist=facMapExist[fac.numero_document];
        if(exist){
          // Ne pas écraser une facture déjà Réglée ou Rapprochée
          if(exist.statut==="Réglé") continue;
          await supabase.from("factures").update({
            montant:fac.montant, solde_restant:fac.solde_restant,
            date_facture:fac.date_facture, date_echeance:fac.date_echeance,
            anciennete:fac.anciennete, jours_retard:fac.jours_retard,
            libelle:fac.libelle
          }).eq("id",exist.id);
          majFac++;
        } else {
          const {data:newFac}=await supabase.from("factures").insert([{...fac,creance_id:creanceId}]).select();
          if(newFac) inserFac++;
        }
      }

      // ── Fiches clients : créer si inexistantes (préserver les existantes) ───
      const existingNums=clients.map(c=>c.numero_compte);
      const fichesACreer=parsedCre.filter(c=>!existingNums.includes(c.numero_compte)&&creMap[c.numero_compte]).map(c=>({
        numero_compte:c.numero_compte,nom_contact:c.nom_client,
        societe_id:c.societe_id,entreprise_id:c.entreprise_id,
        email:"",telephone:"",adresse:"",poste_contact:"",procedure_factures:"",piece_jointe_url:""
      }));
      if(fichesACreer.length>0){
        const {data:ficheData}=await supabase.from("clients").insert(fichesACreer).select();
        if(ficheData) setClients(p=>[...p,...ficheData]);
      }

      // ── Recharger tout depuis Supabase ───────────────────────────────────────
      const {data:allCre}=await supabase.from("creances").select("*").eq("societe_id",societeId).eq("entreprise_id",entreprise.id);
      const {data:allFac}=await supabase.from("factures").select("*").eq("societe_id",societeId).eq("entreprise_id",entreprise.id);
      if(allCre) setCreances(p=>[...p.filter(c=>c.societe_id!==societeId),...allCre]);
      if(allFac) setFactures(p=>[...p.filter(f=>f.societe_id!==societeId),...allFac]);

      // ── Synchronisation des factures réglées ────────────────────────────────
      // Uniquement pour les créances qui existaient DÉJÀ en base (pas les nouvelles)
      const dateImport=new Date().toLocaleDateString("fr-FR");
      let regleesCount=0;
      // 1. Clients présents dans le fichier — marquer réglées les factures absentes
      for(const cre of parsedCre){
        const creanceId=creMap[cre.numero_compte];
        if(!creanceId) continue;
        if(!creMapExist[cre.numero_compte]) continue;
        // Numéros de factures présents dans le fichier pour ce client
        const numsImport=new Set(parsedFac.filter(f=>f.numero_compte===cre.numero_compte).map(f=>f.numero_document));
        // Factures en base pour ce client qui ne sont plus dans le fichier
        const {data:facsEnBase}=await supabase.from("factures")
          .select("id,numero_document,statut")
          .eq("creance_id",creanceId)
          .neq("statut","Réglé");
        if(!facsEnBase?.length) continue;
        for(const fac of facsEnBase){
          if(!numsImport.has(fac.numero_document)){
            // Plus dans la balance → réglée dans EBP
            await supabase.from("factures").update({
              statut:"Réglé",
              solde_restant:0,
              date_realisation:new Date().toISOString().split("T")[0]
            }).eq("id",fac.id);
            setFactures(p=>p.map(f=>f.id===fac.id?{...f,statut:"Réglé",solde_restant:0}:f));
            // Tracer dans l'historique
            await supabase.from("historique_relances").insert([{
              creance_id:creanceId,
              type_action:"Paiement reçu",
              annotation:`Facture ${fac.numero_document} marquée Réglée automatiquement lors de l'import balance du ${dateImport}`,
              date_action:new Date().toISOString().split("T")[0],
              auteur:"Import automatique",
              montant_regle:0,
              created_at:new Date().toISOString(),
            }]);
            regleesCount++;
          }
        }
      }
      // 2. Clients totalement absents du fichier → toutes leurs factures réglées
      const comptesImport=new Set(parsedCre.map(c=>c.numero_compte));
      for(const [numCompte,creExistante] of Object.entries(creMapExist)){
        if(comptesImport.has(numCompte)) continue;
        if(!creExistante.id) continue;
        const {data:facsAbsentes}=await supabase.from("factures")
          .select("id,numero_document,statut")
          .eq("creance_id",creExistante.id)
          .neq("statut","Réglé");
        if(!facsAbsentes?.length) continue;
        for(const fac of facsAbsentes){
          await supabase.from("factures").update({
            statut:"Réglé",solde_restant:0,
            date_realisation:new Date().toISOString().split("T")[0]
          }).eq("id",fac.id);
          setFactures(p=>p.map(f=>f.id===fac.id?{...f,statut:"Réglé",solde_restant:0}:f));
          await supabase.from("historique_relances").insert([{
            creance_id:creExistante.id,type_action:"Paiement reçu",
            annotation:`Facture ${fac.numero_document} marquée Réglée automatiquement — client soldé (absent de l'import balance du ${dateImport})`,
            date_action:new Date().toISOString().split("T")[0],
            auteur:"Import automatique",montant_regle:0,
            created_at:new Date().toISOString(),
          }]);
          regleesCount++;
        }
      }


      setImportResult({clients:creesCount,factures:inserFac});
      const regleesLabel=regleesCount>0?` — ${regleesCount} factures réglées auto`:"";
      // Recalculer les statuts
      progress("Recalcul des statuts...");
      await recalculerStatutsCreances();
      progress("Terminé ✓");
      setTimeout(()=>setImportProgress(null),2000);
      // Log de l'import
      await logImport({type_import:"Balance âgée",nom_fichier:nomFichier,societe_id:societeId,
        nb_clients_crees:creesCount,nb_clients_maj:majCreCount,
        nb_factures_ajoutees:inserFac,nb_factures_maj:majFac,
        nb_factures_reglees:regleesCount,nb_erreurs:0});
      showToast(`${creesCount} clients créés, ${majCreCount} mis à jour — ${inserFac} factures ajoutées, ${majFac} mises à jour${regleesLabel} ✓`);
    };
    reader.onload=ev=>{
      const text=ev.target.result;
      if(text.includes("Ã©")||text.includes("Ã ")||text.includes("Ã¨")||text.includes("�")){
        const r2=new FileReader();
        r2.onload=ev2=>processBalance(ev2.target.result);
        r2.readAsText(file,"windows-1252");
        return;
      }
      processBalance(text);
    };
    reader.readAsText(file,"UTF-8");
  },[entreprise,clients]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── IMPORT CSV FACTURES (enrichi) ────────────────────────────────────────────
  const onFacDrop=useCallback((e,societeId)=>{
    e.preventDefault();setDragOverFac(false);
    const file=e.dataTransfer?.files[0]||e.target.files?.[0];
    if(!file){showToast("Aucun fichier","error");return;}
    if(!societeId){showToast("Sélectionner une société","error");return;}
    const reader=new FileReader();
    reader.onload=async ev=>{
      const parsed=parseFacturesCSV(ev.target.result,societeId,entreprise.id);
      if(!parsed.length){showToast("Aucune facture détectée","error");return;}
      let inserted=0,updated=0,creancesCreees=0,errors=0;
      // Charger créances existantes pour cette société
      const {data:creancesExist}=await supabase.from("creances").select("*").eq("societe_id",societeId).eq("entreprise_id",entreprise.id);
      const creMap={};
      (creancesExist||[]).forEach(c=>{if(c.numero_compte) creMap[c.numero_compte]=c;});
      // Grouper factures par client
      const parClient={};
      for(const fac of parsed){
        const key=fac.numero_compte||fac.nom_client;
        if(!parClient[key]) parClient[key]=[];
        parClient[key].push(fac);
      }
      for(const [,facs] of Object.entries(parClient)){
        const nomClient=facs[0].nom_client;
        const numCompte=facs[0].numero_compte;
        // Trouver ou créer la créance client
        let creanceId=creMap[numCompte]?.id||creMap[nomClient]?.id||null;
        if(!creanceId){
          const soldeTotal=facs.reduce((s,f)=>s+(f.solde_restant||f.montant),0);
          const maxRetard=Math.max(...facs.map(f=>f.jours_retard||0));
          const anc=maxRetard===0?"a_echoir":maxRetard<=30?"1_30j":maxRetard<=60?"31_60j":maxRetard<=90?"61_90j":"plus_90j";
          const {data:newCre}=await supabase.from("creances").insert([{
            numero_compte:numCompte||nomClient,nom_client:nomClient,
            societe_id:societeId,entreprise_id:entreprise.id,
            statut:"En attente",etape_relance:"Relevé de compte",
            solde:Math.abs(soldeTotal),anciennete:anc,
            jours_retard:maxRetard,date_import:new Date().toISOString().split("T")[0]
          }]).select();
          if(newCre&&newCre.length>0){
            creanceId=newCre[0].id;
            creMap[numCompte||nomClient]=newCre[0];
            setCreances(p=>[...p,newCre[0]]);
            // Créer fiche client si inexistante
            const {data:ficheExist}=await supabase.from("clients").select("id").eq("numero_compte",numCompte||nomClient).eq("societe_id",societeId).limit(1);
            if(!ficheExist||ficheExist.length===0){
              const {data:ficheNew}=await supabase.from("clients").insert([{numero_compte:numCompte||nomClient,nom_contact:nomClient,societe_id:societeId,entreprise_id:entreprise.id,email:"",telephone:"",adresse:"",poste_contact:"",procedure_factures:"",piece_jointe_url:""}]).select();
              if(ficheNew) setClients(p=>[...p,...ficheNew]);
            }
            creancesCreees++;
          }
        }
        // Insérer ou mettre à jour chaque facture
        for(const fac of facs){
          const {data:existing}=await supabase.from("factures").select("id,statut").eq("numero_document",fac.numero_document).eq("societe_id",societeId).limit(1);
          if(existing&&existing.length>0){
            if(existing[0].statut!=="Réglé"){
              await supabase.from("factures").update({date_facture:fac.date_facture,date_echeance:fac.date_echeance,montant:fac.montant,anciennete:fac.anciennete,jours_retard:fac.jours_retard,libelle:fac.libelle}).eq("id",existing[0].id);
              updated++;
            }
          } else {
            const {error}=await supabase.from("factures").insert([{...fac,creance_id:creanceId}]);
            if(error) errors++;
            else inserted++;
          }
        }
        // Recalculer solde créance
        if(creanceId){
          const {data:facsCre}=await supabase.from("factures").select("solde_restant,montant,statut,jours_retard").eq("creance_id",creanceId);
          if(facsCre){
            const newSolde=facsCre.filter(f=>f.statut!=="Réglé").reduce((s,f)=>s+(f.is_avoir?-(Math.abs(f.solde_restant??f.montant)):(f.solde_restant??f.montant)),0);
            const newRetard=Math.max(...facsCre.map(f=>f.jours_retard||0),0);
            const newAnc=newRetard===0?"a_echoir":newRetard<=30?"1_30j":newRetard<=60?"31_60j":newRetard<=90?"61_90j":"plus_90j";
            await supabase.from("creances").update({solde:Math.abs(newSolde),jours_retard:newRetard,anciennete:newAnc}).eq("id",creanceId);
            setCreances(p=>p.map(c=>c.id===creanceId?{...c,solde:Math.abs(newSolde),jours_retard:newRetard,anciennete:newAnc}:c));
          }
        }
      }
      const {data:newFacs}=await supabase.from("factures").select("*").eq("societe_id",societeId).eq("entreprise_id",entreprise.id);
      if(newFacs) setFactures(p=>[...p.filter(f=>f.societe_id!==societeId),...newFacs]);
      await logImport({type_import:"Factures CSV",nom_fichier:file?.name||"factures.csv",societe_id:societeId,
        nb_clients_crees:creancesCreees,nb_factures_ajoutees:inserted,nb_factures_maj:updated,nb_erreurs:errors});
      showToast(`${inserted} factures ajoutées, ${updated} mises à jour, ${creancesCreees} clients créés${errors>0?`, ${errors} erreurs`:""} ✓`);
      setImportResult({clients:creancesCreees,factures:inserted});
    };
    reader.readAsText(file,"UTF-8");
  },[entreprise]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── CRÉANCES ─────────────────────────────────────────────────────────────────
  async function updateCreance(id,updates){
    updates.date_derniere_relance=new Date().toISOString().split("T")[0];
    await supabase.from("creances").update(updates).eq("id",id);
    setCreances(p=>p.map(c=>c.id===id?{...c,...updates}:c));
    if(selectedCreance?.id===id) setSelectedCreance(p=>({...p,...updates}));
    showToast("Mis \u00e0 jour \u2713");
  }
  async function updateFactureStatut(id,statut){
    await supabase.from("factures").update({statut}).eq("id",id);
    setFactures(p=>p.map(f=>f.id===id?{...f,statut}:f));
  }
  async function reglerFacture(facture,montantRegle){
    const newSolde=Math.max(0,facture.montant-montantRegle);
    const newStatut=newSolde<=0?"R\u00e9gl\u00e9":"Relancé";
    await supabase.from("factures").update({montant_regle:montantRegle,solde_restant:newSolde,statut:newStatut}).eq("id",facture.id);
    setFactures(p=>p.map(f=>f.id===facture.id?{...f,montant_regle:montantRegle,solde_restant:newSolde,statut:newStatut}:f));
    const facsCre=factures.filter(f=>f.creance_id===facture.creance_id&&f.id!==facture.id&&!f.is_avoir);
    const newSoldeCre=facsCre.reduce((s,f)=>s+(f.solde_restant||f.montant),0)+newSolde;
    const newStatutCre=newSoldeCre<=0?"R\u00e9gl\u00e9":"En attente";
    await supabase.from("creances").update({solde:Math.max(0,newSoldeCre),statut:newStatutCre}).eq("id",facture.creance_id);
    setCreances(p=>p.map(c=>c.id===facture.creance_id?{...c,solde:Math.max(0,newSoldeCre),statut:newStatutCre}:c));
    if(selectedCreance?.id===facture.creance_id) setSelectedCreance(p=>({...p,solde:Math.max(0,newSoldeCre),statut:newStatutCre}));
    showToast("R\u00e8glement enregistr\u00e9 \u2713");
  }
  async function loadHistorique(creanceId){
    const {data}=await supabase.from("historique_relances").select("*").eq("creance_id",creanceId).order("created_at",{ascending:false});
    setHistorique(data||[]);
  }
  async function syncHistoriqueVersCarnet(){
    // Importer les actions de l'historique qui ne sont pas encore dans le carnet
    const creanceIds=creances.map(c=>c.id);
    if(!creanceIds.length) return;
    const {data:histo}=await supabase.from("historique_relances")
      .select("*").in("creance_id",creanceIds)
      .order("created_at",{ascending:false});
    if(!histo?.length) return;
    // Tâches déjà dans le carnet (pour éviter doublons)
    const {data:existingTaches}=await supabase.from("taches")
      .select("id,creance_id,date_prevue,type_action,origine")
      .eq("entreprise_id",entreprise.id)
      .eq("origine","historique");
    const existingKeys=new Set((existingTaches||[]).map(t=>`${t.creance_id}_${t.date_prevue}_${t.type_action}`));
    const toInsert=[];
    for(const h of histo){
      const cre=creances.find(c=>c.id===h.creance_id);
      const dateAction=h.date_action||h.created_at?.split("T")[0]||new Date().toISOString().split("T")[0];
      const key=`${h.creance_id}_${dateAction}_${h.type_action}`;
      if(existingKeys.has(key)) continue;
      if(!h.type_action||h.type_action==="Note interne") continue;
      toInsert.push({
        titre:`${h.type_action} — ${cre?.nom_client||""}`,
        type_action:h.type_action,
        date_prevue:dateAction,
        assignee_nom:h.auteur||"",
        societe_id:cre?.societe_id||null,
        client_nom:cre?.nom_client||"",
        description:h.annotation||"",
        origine:"historique",
        statut:"Terminé",
        date_realisation:dateAction,
        creance_id:h.creance_id,
        creee_par:h.auteur||"",
        entreprise_id:entreprise.id,
        created_at:h.created_at||new Date().toISOString(),
      });
    }
    if(toInsert.length>0){
      const {data:newTaches}=await supabase.from("taches").insert(toInsert).select();
      if(newTaches) setTaches(p=>[...p,...newTaches]);
      showToast(`${toInsert.length} action(s) importée(s) dans le carnet ✓`);
    } else {
      showToast("Carnet déjà à jour ✓");
    }
  }

  async function loadStats(){
    setStatsLoading(true);
    // historique_relances n'a pas de colonne entreprise_id
    // On filtre via les creances qui appartiennent à l'entreprise
    const creanceIds=creances.map(c=>c.id);
    if(!creanceIds.length){setStatsLoading(false);return;}
    const {data}=await supabase.from("historique_relances")
      .select("*")
      .in("creance_id",creanceIds)
      .order("created_at",{ascending:false});
    setStatsHistoGlobal(data||[]);
    setStatsLoading(false);
  }

  // ── TRACER UNE ACTION AUTOMATIQUE DANS L'HISTORIQUE ─────────────────────────
  async function tracerAction(creanceId, typeAction, annotation){
    if(!creanceId) return;
    const toInsert={
      creance_id: creanceId,
      type_action: typeAction,
      annotation,
      date_action: new Date().toISOString().split("T")[0],
      etape: creances.find(c=>c.id===creanceId)?.etape_relance||"",
      auteur: user?.nom||"Système",
      montant_regle: 0,
      created_at: new Date().toISOString(),
    };
    const {data}=await supabase.from("historique_relances").insert([toInsert]).select();
    // Mettre à jour l'historique si le client est actuellement ouvert
    if(data && selectedCreance?.id===creanceId){
      setHistorique(p=>[data[0],...p]);
    }
  }
  // eslint-disable-next-line no-unused-vars
  // ── CARNET DE BORD — TÂCHES ──────────────────────────────────────────────────
  async function addTache(){
    if(!newTache.titre||!newTache.date_prevue){showToast("Titre et date requis","error");return;}
    // Vérifier que la société est renseignée si client multi-entités
    if(newTache.creance_id&&!newTache.societe_id){
      const cre=creances.find(c=>c.id===newTache.creance_id);
      const autresSocietes=creances.filter(c=>c.nom_client===cre?.nom_client&&c.id!==cre?.id);
      if(autresSocietes.length>0){showToast("Ce client existe dans plusieurs sociétés — veuillez sélectionner la société","error");return;}
    }
    const assigneeNom=newTache.assignee_nom||user?.nom||"";
    const toInsert={
      ...newTache,
      assignee_nom:assigneeNom,
      entreprise_id:entreprise.id,
      societe_id:newTache.societe_id||null,
      statut:"A faire",
      creee_par:user?.nom||"",
      created_at:new Date().toISOString(),
    };
    const {data}=await supabase.from("taches").insert([toInsert]).select();
    if(data){
      setTaches(p=>[...p,...data]);
      // Si liée à un client → créer entrée dans historique (italique = planifiée)
      if(newTache.creance_id){
        const dateStr=new Date(newTache.date_prevue).toLocaleDateString("fr-FR");
        await supabase.from("historique_relances").insert([{
          creance_id:newTache.creance_id,
          type_action:newTache.type_action,
          annotation:`[PLANIFIÉ] ${newTache.titre}${newTache.description?` — ${newTache.description}`:""} (prévu le ${dateStr}, assigné à ${assigneeNom})`,
          date_action:new Date().toISOString().split("T")[0],
          etape:creances.find(c=>c.id===newTache.creance_id)?.etape_relance||"",
          auteur:user?.nom||"",
          montant_regle:0,
          tache_id:data[0].id,
          tache_statut:"A faire",
          created_at:new Date().toISOString(),
        }]);
        if(selectedCreance?.id===newTache.creance_id){
          const {data:h}=await supabase.from("historique_relances").select("*").eq("creance_id",newTache.creance_id).order("created_at",{ascending:false});
          if(h) setHistorique(h);
        }
      }
      setNewTache({titre:"",type_action:"Appel téléphonique",date_prevue:"",assignee_id:"",assignee_nom:"",societe_id:"",client_nom:"",description:"",origine:"manuelle",creance_id:null});
      setClientSearch("");
      setShowNewTache(false);
      showToast("Tâche créée ✓");
    }
  }
  async function updateTacheStatut(id,statut){
    const dateReal=statut==="Terminé"?new Date().toISOString().split("T")[0]:null;
    await supabase.from("taches").update({statut,date_realisation:dateReal}).eq("id",id);
    setTaches(p=>p.map(t=>t.id===id?{...t,statut,date_realisation:dateReal}:t));
    // Mettre à jour l'entrée historique liée (tache_id) si elle existe
    if(statut==="Terminé"){
      const tache=taches.find(t=>t.id===id);
      if(tache?.creance_id){
        // Mettre à jour l'annotation : enlever [PLANIFIÉ] → [RÉALISÉ]
        const {data:hExist}=await supabase.from("historique_relances").select("id,annotation").eq("tache_id",id).limit(1);
        if(hExist&&hExist.length>0){
          const newAnnot=hExist[0].annotation.replace("[PLANIFIÉ]","[RÉALISÉ]");
          await supabase.from("historique_relances").update({annotation:newAnnot,tache_statut:"Terminé"}).eq("id",hExist[0].id);
          if(selectedCreance?.id===tache.creance_id){
            const {data:h}=await supabase.from("historique_relances").select("*").eq("creance_id",tache.creance_id).order("created_at",{ascending:false});
            if(h) setHistorique(h);
          }
        }
      }
    }
  }
  async function deleteTache(id){
    await supabase.from("taches").delete().eq("id",id);
    setTaches(p=>p.filter(t=>t.id!==id));
  }
  // Créer une tâche depuis l'historique automatiquement
  // eslint-disable-next-line no-unused-vars
  async function tracerActionAvecTache(creanceId,typeAction,annotation,planifierRappel=null){
    await tracerAction(creanceId,typeAction,annotation);
    // Remonter dans le carnet de bord
    const cre=creances.find(c=>c.id===creanceId);
    const tacheAuto={
      titre:`${typeAction} — ${cre?.nom_client||""}`,
      type_action:typeAction,
      date_prevue:new Date().toISOString().split("T")[0],
      assignee_nom:user?.nom||"",
      assignee_id:user?.id||null,
      societe_id:cre?.societe_id||null,
      client_nom:cre?.nom_client||"",
      description:annotation,
      origine:"historique",
      statut:"Terminé",
      date_realisation:new Date().toISOString().split("T")[0],
      creee_par:user?.nom||"",
      entreprise_id:entreprise.id,
      creance_id:creanceId,
      created_at:new Date().toISOString(),
    };
    const {data:t1}=await supabase.from("taches").insert([tacheAuto]).select();
    if(t1) setTaches(p=>[...p,...t1]);
    // Si rappel planifié (J+N)
    if(planifierRappel){
      let dateRappel;
      if(planifierRappel.dateExacte){
        dateRappel=new Date(planifierRappel.dateExacte);
      } else {
        dateRappel=new Date();dateRappel.setDate(dateRappel.getDate()+planifierRappel.jours);
      }
      const tacheRappel={
        titre:planifierRappel.titre||`Rappel — ${cre?.nom_client||""}`,
        type_action:"Rappel planifié",
        date_prevue:dateRappel.toISOString().split("T")[0],
        assignee_nom:user?.nom||"",
        assignee_id:user?.id||null,
        societe_id:cre?.societe_id||null,
        client_nom:cre?.nom_client||"",
        description:planifierRappel.description||"",
        origine:"planifiée",
        statut:"A faire",
        creee_par:user?.nom||"",
        entreprise_id:entreprise.id,
        created_at:new Date().toISOString(),
      };
      const {data:t2}=await supabase.from("taches").insert([tacheRappel]).select();
      if(t2) setTaches(p=>[...p,...t2]);
    }
  }

  // ── LOG IMPORT ───────────────────────────────────────────────────────────────
  async function logImport({type_import,nom_fichier,statut="succès",message_erreur=null,
    nb_clients_crees=0,nb_clients_maj=0,nb_factures_ajoutees=0,nb_factures_maj=0,nb_factures_reglees=0,nb_erreurs=0,societe_id=null}){
    const entry={entreprise_id:entreprise.id,societe_id,type_import,nom_fichier,
      utilisateur:user?.nom||"",statut,message_erreur,
      nb_clients_crees,nb_clients_maj,nb_factures_ajoutees,nb_factures_maj,nb_factures_reglees,nb_erreurs,
      created_at:new Date().toISOString()};
    const {data}=await supabase.from("imports_log").insert([entry]).select();
    if(data) setImportsLog(p=>[data[0],...p]);
  }

  // ── RECALCULER STATUTS CRÉANCES ──────────────────────────────────────────────
  async function recalculerStatutsCreances(){
    setImportProgress({label:"Recalcul des statuts...",current:0,total:creances.length,pct:0});
    let corriges=0;let done=0;
    for(const cre of creances){
      done++;setImportProgress({label:`Vérification ${cre.nom_client}...`,current:done,total:creances.length,pct:Math.round((done/creances.length)*100)});
      const facsCre=factures.filter(f=>f.creance_id===cre.id);
      const toutesReglees=facsCre.filter(f=>!f.is_avoir).length>0&&facsCre.filter(f=>!f.is_avoir).every(f=>f.statut==="Réglé");
      const soldeCre=facsCre.filter(f=>f.statut!=="Réglé").reduce((s,f)=>s+(f.is_avoir?-(Math.abs(f.solde_restant??f.montant)):(f.solde_restant??f.montant)),0);
      // Corriger dans les deux sens : Réglé → En attente si solde > 0, En attente → Réglé si solde = 0
      let newStatut;
      if(toutesReglees||soldeCre<=0.01) newStatut="Réglé";
      else if(cre.statut==="Réglé"&&soldeCre>0.01) newStatut="En attente"; // remettre en attente si solde > 0
      else newStatut=cre.statut;
      if(newStatut!==cre.statut||(Math.abs(soldeCre-cre.solde)>0.01)){
        await supabase.from("creances").update({statut:newStatut,solde:Math.max(0,soldeCre)}).eq("id",cre.id);
        corriges++;
      }
    }
    setCreances(prev=>prev.map(c=>{
      const facsCre=factures.filter(f=>f.creance_id===c.id);
      const toutesReglees=facsCre.filter(f=>!f.is_avoir).length>0&&facsCre.filter(f=>!f.is_avoir).every(f=>f.statut==="Réglé");
      const soldeCre=facsCre.filter(f=>f.statut!=="Réglé").reduce((s,f)=>s+(f.is_avoir?-(Math.abs(f.solde_restant??f.montant)):(f.solde_restant??f.montant)),0);
      let newStatut;
      if(toutesReglees||soldeCre<=0.01) newStatut="Réglé";
      else if(c.statut==="Réglé"&&soldeCre>0.01) newStatut="En attente";
      else newStatut=c.statut;
      return {...c,statut:newStatut,solde:Math.max(0,soldeCre)};
    }));
    setImportProgress({label:"Terminé ✓",current:creances.length,total:creances.length,pct:100});
    setTimeout(()=>setImportProgress(null),1500);
    showToast(`${corriges} statut(s) corrigé(s) ✓`);
  }

  // ── FICHE CLIENT ─────────────────────────────────────────────────────────────
  async function saveFicheClient(data){
    const fiche=getFicheClient(selectedCreance||showFicheClient);
    if(fiche){
      await supabase.from("clients").update(data).eq("id",fiche.id);
      setClients(p=>p.map(c=>c.id===fiche.id?{...c,...data}:c));
    } else {
      const toInsert={...data,numero_compte:selectedCreance?.numero_compte||showFicheClient?.numero_compte,societe_id:selectedCreance?.societe_id||showFicheClient?.societe_id,entreprise_id:entreprise.id};
      const {data:newC}=await supabase.from("clients").insert([toInsert]).select();
      if(newC) setClients(p=>[...p,newC[0]]);
    }
    setShowFicheClient(null);setEditFicheClient(null);
    showToast("Fiche client sauvegard\u00e9e \u2713");
  }

  // ── PRÉVISION RÈGLEMENT ──────────────────────────────────────────────────────
  async function addPrevReglement(){
    if(!newPrevReglement.date||!newPrevReglement.montant){showToast("Date et montant requis","error");return;}
    const cre=showPrevReglement;
    const societeId=cre.societe_id||user?.societe_id||null;
    const toInsert={date:newPrevReglement.date,libelle:`Règlement prévu — ${cre.nom_client}`,type:"RECETTE",categorie:"Règlement client",montant:parseFloat(newPrevReglement.montant),statut:"Incertain",societe_id:societeId,entreprise_id:entreprise.id,source_creance_id:cre.id};
    const {data}=await supabase.from("previsionnel").insert([toInsert]).select();
    if(data){
      // Recharger tout le prévisionnel pour que l'admin le voie aussi
      const {data:prevAll}=await supabase.from("previsionnel").select("*").eq("entreprise_id",entreprise.id).order("date",{ascending:true});
      if(prevAll) setPrevisionnel(prevAll);
      else setPrevisionnel(p=>[...p,...data]);
      showToast("Prévision de règlement ajoutée au prévisionnel ✓");
      await tracerAction(cre.id,"Note interne",`Prévision de règlement saisie : ${fmt(parseFloat(newPrevReglement.montant))} attendu le ${newPrevReglement.date}`);
    }
    setShowPrevReglement(null);setNewPrevReglement({date:"",montant:""});
  }

  // ── PRÉVISIONNEL ─────────────────────────────────────────────────────────────
  async function addPrev(){
    if(!newPrev.date||!newPrev.libelle||!newPrev.montant){showToast("Remplir tous les champs","error");return;}
    const {data}=await supabase.from("previsionnel").insert([{...newPrev,montant:parseFloat(newPrev.montant),entreprise_id:entreprise.id}]).select();
    if(data){setPrevisionnel(p=>[...p,data[0]]);setNewPrev({date:"",libelle:"",type:"D\u00c9PENSE",categorie:"Divers d\u00e9penses",montant:"",statut:"Confirm\u00e9",societe_id:""});showToast("Ajout\u00e9 \u2713");}
  }
  async function deletePrev(id){await supabase.from("previsionnel").delete().eq("id",id);setPrevisionnel(p=>p.filter(t=>t.id!==id));}

  async function addRecurrent(){
    if(!newRecurrent.libelle||!newRecurrent.montant||!newRecurrent.date_debut){showToast("Remplir lib\u00e9ll\u00e9, montant et date de d\u00e9but","error");return;}
    const toInsert={...newRecurrent,montant:parseFloat(newRecurrent.montant),entreprise_id:entreprise.id,actif:true};
    const {data,error}=await supabase.from("recurrents").insert([toInsert]).select();
    if(error){showToast("Erreur — v\u00e9rifier que la table recurrents existe","error");return;}
    if(data){
      setRecurrents(p=>[data[0],...p]);
      setNewRecurrent({libelle:"",type:"D\u00c9PENSE",categorie:"Divers d\u00e9penses",montant:"",frequence:"Mensuel",date_debut:"",date_fin:"",societe_id:""});
      await genererPrevisionnel(data[0]);
      showToast("R\u00e9current cr\u00e9\u00e9 et pr\u00e9visionnel g\u00e9n\u00e9r\u00e9 \u2713");
    }
  }
  async function deleteRecurrent(id){await supabase.from("recurrents").delete().eq("id",id);setRecurrents(p=>p.filter(r=>r.id!==id));}
  async function updateMontantRecurrent(id,montant){
    await supabase.from("recurrents").update({montant:parseFloat(montant)}).eq("id",id);
    setRecurrents(p=>p.map(r=>r.id===id?{...r,montant:parseFloat(montant)}:r));
    setEditRecurrent(null);showToast("Montant mis \u00e0 jour \u2713");
  }
  async function genererPrevisionnel(rec){
    const dateDebut=new Date(rec.date_debut);
    const dateFin=rec.date_fin?new Date(rec.date_fin):new Date(dateDebut.getFullYear()+2,dateDebut.getMonth(),dateDebut.getDate());
    const occurrences=[];let current=new Date(dateDebut);
    while(current<=dateFin&&occurrences.length<60){
      occurrences.push({date:current.toISOString().split("T")[0],libelle:`[Auto] ${rec.libelle}`,type:rec.type,categorie:rec.categorie,montant:parseFloat(rec.montant),statut:"Confirm\u00e9",societe_id:rec.societe_id||null,entreprise_id:rec.entreprise_id,recurrent_id:rec.id});
      if(rec.frequence==="Mensuel"){current=new Date(current);current.setMonth(current.getMonth()+1);}
      else if(rec.frequence==="Trimestriel"){current=new Date(current);current.setMonth(current.getMonth()+3);}
      else{current=new Date(current);current.setFullYear(current.getFullYear()+1);}
    }
    if(!occurrences.length) return;
    const {data}=await supabase.from("previsionnel").insert(occurrences).select();
    if(data) setPrevisionnel(p=>[...p,...data]);
  }

  // ── CONFIGURATION ────────────────────────────────────────────────────────────
  // ── RAPPROCHEMENT BANCAIRE ───────────────────────────────────────────────────
  async function confirmerRapprochement(){
    const tx=showRapprochement;
    const entries=Object.entries(rapprochFacs).filter(([,v])=>parseFloat(v)>0);
    if(!entries.length){showToast("Aucune facture sélectionnée","error");return;}
    let totalApplique=0;
    for(const [facId,montantStr] of entries){
      const montant=parseFloat(montantStr);
      const fac=factures.find(f=>f.id===facId);
      if(!fac) continue;
      totalApplique+=montant;
      await reglerFacture(fac,montant);
    }
    // Calculer l'écart entre le montant de la transaction et le total appliqué
    const ecart=Math.abs((tx?.montant||0)-totalApplique);
    const nomClientRapproch=rapprochSelected?.nom_client||"";
    await supabase.from("transactions").update({rapproche:true,rapproche_creance_id:rapprochSelected?.id,rapproche_client_nom:nomClientRapproch}).eq("id",tx.id);
    setTransactions(p=>p.map(t=>t.id===tx.id?{...t,rapproche:true,rapproche_creance_id:rapprochSelected?.id,rapproche_client_nom:nomClientRapproch}:t));
    // Annotation d'écart si > 10€
    let ecartLabel="";
    if(ecart>10){
      ecartLabel=` — ⚠️ Écart de ${fmt(ecart)} avec la transaction (à régulariser)`;
      await tracerAction(rapprochSelected?.id,"Note interne",`Rapprochement avec écart de ${fmt(ecart)} — Montant transaction : ${fmt(tx?.montant||0)} / Montant appliqué : ${fmt(totalApplique)} — À régulariser`);
    }
    showToast(`Rapprochement effectué — ${fmt(totalApplique)} appliqués sur ${entries.length} facture(s)${ecart>10?" (écart "+fmt(ecart)+")":" ✓"}`);
    await tracerAction(rapprochSelected?.id,"Paiement reçu",`Rapprochement bancaire : ${fmt(totalApplique)} réglé sur ${entries.length} facture(s) — Réf. transaction : ${tx.libelle}${ecartLabel}`);
    setShowRapprochement(null);setRapprochSelected(null);setRapprochFacs({});setRapprochSearch("");
  }

  // ── ENVOI EMAIL RELANCE VIA RESEND ──────────────────────────────────────────
  async function sendRelanceEmail({to, subject, html, from_name, from_email, cc, attachments}){
    setSendingEmail(true);
    try {
      const body={to, subject, html, from_name, from_email};
      if(cc&&cc.length) body.cc=cc;
      if(attachments&&attachments.length) body.attachments=attachments;
      const {data,error} = await supabase.functions.invoke("send-relance", {
        body
      });
      if(error) throw new Error(error.message);
      if(data?.error) throw new Error(data.error);
      showToast("Email envoyé ✓");
      return true;
    } catch(err) {
      showToast(`Erreur envoi : ${err.message}`,"error");
      return false;
    } finally {
      setSendingEmail(false);
    }
  }

  // ── NOTES PAR FACTURE ───────────────────────────────────────────────────────
  async function loadNotesFacture(factureId){
    const {data}=await supabase.from("notes_factures").select("*").eq("facture_id",factureId).order("created_at",{ascending:false});
    setNotesFacture(p=>({...p,[factureId]:data||[]}));
  }
  async function addNoteFacture(factureId){
    if(!newNoteFacture.trim()){showToast("Note vide","error");return;}
    const toInsert={facture_id:factureId,texte:newNoteFacture.trim(),auteur:user.nom,entreprise_id:entreprise.id,created_at:new Date().toISOString()};
    const {data}=await supabase.from("notes_factures").insert([toInsert]).select();
    if(data){
      setNotesFacture(p=>({...p,[factureId]:[data[0],...(p[factureId]||[])]}));
      setNewNoteFacture("");
      showToast("Note ajoutée ✓");
    }
  }

  // ── CONVERTIR TX EN RÉCURRENT ──────────────────────────────────────────────
  async function confirmerRecurrentFromTx(){
    const tx=showRecurrentModal;
    if(!tx) return;
    const toInsert={
      libelle:tx.libelle,
      type:tx.type,
      categorie:tx.categorie,
      montant:tx.montant,
      frequence:newRecurrentFromTx.frequence,
      date_debut:tx.date&&tx.date.includes("/")?`${tx.date.slice(6,10)}-${tx.date.slice(3,5)}-${tx.date.slice(0,2)}`:tx.date||new Date().toISOString().split("T")[0],
      date_fin:newRecurrentFromTx.date_fin||null,
      societe_id:tx.societe_id||null,
      entreprise_id:entreprise.id,
      actif:true
    };
    const {data,error}=await supabase.from("recurrents").insert([toInsert]).select();
    if(error){showToast("Erreur — v\u00e9rifier que la table recurrents existe","error");return;}
    if(data){
      setRecurrents(p=>[data[0],...p]);
      // Marquer la transaction comme récurrente
      await supabase.from("transactions").update({is_recurrent:true,recurrent_id:data[0].id}).eq("id",tx.id);
      setTransactions(p=>p.map(t=>t.id===tx.id?{...t,is_recurrent:true,recurrent_id:data[0].id}:t));
      await genererPrevisionnel(data[0]);
      showToast("Transaction convertie en r\u00e9current \u2713");
    }
    setShowRecurrentModal(null);
    setNewRecurrentFromTx({frequence:"Mensuel",date_fin:""});
  }

  // ── RÈGLES DE CATÉGORISATION ─────────────────────────────────────────────────
  async function addRegle(){
    if(!newRegle.mot_cle.trim()||!newRegle.categorie){showToast("Mot-cl\u00e9 et cat\u00e9gorie requis","error");return;}
    const {data}=await supabase.from("regles_categorisation").insert([{...newRegle,mot_cle:newRegle.mot_cle.trim(),entreprise_id:entreprise.id}]).select();
    if(data){setRegles(p=>[...p,data[0]]);setNewRegle({mot_cle:"",categorie:"Divers d\u00e9penses",type:"D\u00c9PENSE"});showToast("R\u00e8gle ajout\u00e9e \u2713");}
  }
  async function deleteRegle(id){
    await supabase.from("regles_categorisation").delete().eq("id",id);
    setRegles(p=>p.filter(r=>r.id!==id));
    showToast("R\u00e8gle supprim\u00e9e");
  }

  async function addUser(sendEmail=false){
    if(!newUser.email||!newUser.mot_de_passe||!newUser.nom){showToast("Remplir tous les champs","error");return;}
    // Créer dans Supabase Auth (mot de passe hashé)
    const {data:authUser}=await supabase.auth.admin?.createUser
      ? await supabase.auth.admin.createUser({email:newUser.email,password:newUser.mot_de_passe,email_confirm:true})
      : {data:null,error:{message:"admin API non disponible"}};
    // Si admin API pas dispo (plan free), on crée via signUp
    const authId = authUser?.user?.id || null;
    const {data}=await supabase.from("utilisateurs").insert([{...newUser,entreprise_id:entreprise.id,auth_id:authId}]).select("id,nom,email,role,societe_id,auth_id");
    if(data){
      setUsers(p=>[...p,data[0]]);
      if(sendEmail){
        const socNom=newUser.societe_id?societes.find(s=>s.id===newUser.societe_id)?.nom:"Toutes les soci\u00e9t\u00e9s";
        const htmlAcces=`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="font-family:Arial,sans-serif;font-size:13px;color:#111;max-width:500px;margin:0 auto;padding:20px"><div style="background:#1F2937;padding:20px;border-radius:10px 10px 0 0;text-align:center"><div style="font-size:28px;font-weight:800;color:#F97316">G</div><div style="color:#fff;font-size:16px;font-weight:700">GestPilote</div><div style="color:#9CA3AF;font-size:12px">${entreprise?.nom}</div></div><div style="background:#fff;border:1px solid #E5E7EB;border-top:none;border-radius:0 0 10px 10px;padding:24px"><p>Bonjour <strong>${newUser.nom}</strong>,</p><p>Votre acc\u00e8s \u00e0 GestPilote a \u00e9t\u00e9 cr\u00e9\u00e9.</p><div style="background:#F9FAFB;border:1px solid #E5E7EB;border-radius:8px;padding:16px;margin:16px 0"><div style="margin-bottom:8px"><span style="color:#6B7280;font-size:12px">EMAIL</span><br><strong>${newUser.email}</strong></div><div><span style="color:#6B7280;font-size:12px">MOT DE PASSE</span><br><strong>${newUser.mot_de_passe}</strong></div></div><div style="background:#FFF7ED;border:1px solid #FED7AA;border-radius:6px;padding:10px;font-size:12px;color:#92400E">Soci\u00e9t\u00e9 : <strong>${socNom}</strong> \u2014 R\u00f4le : <strong>${newUser.role}</strong></div></div></body></html>`;
        await sendRelanceEmail({to:newUser.email,subject:`Vos acc\u00e8s GestPilote \u2014 ${entreprise?.nom}`,html:htmlAcces,from_name:entreprise?.nom||"Groupe Derussy",from_email:null});
      }
      setNewUser({nom:"",email:"",mot_de_passe:"",role:"utilisateur",societe_id:null});
      showToast(sendEmail?"Utilisateur cr\u00e9\u00e9 et email envoy\u00e9 \u2713":"Utilisateur cr\u00e9\u00e9 \u2713");
    }
  }
  async function deleteUser(id){await supabase.from("utilisateurs").delete().eq("id",id);setUsers(p=>p.filter(u=>u.id!==id));}

  async function addSociete(){
    if(!newSociete.nom){showToast("Nom de la soci\u00e9t\u00e9 requis","error");return;}
    const {data}=await supabase.from("societes").insert([{...newSociete,entreprise_id:entreprise.id,actif:true}]).select();
    if(data){setSocietes(p=>[...p,data[0]]);setNewSociete({nom:"",code:"",email_recouvrement:"",signature_email:""});showToast("Soci\u00e9t\u00e9 ajout\u00e9e \u2713");}
  }

  async function updateSociete(){
    if(!editSociete?.id) return;
    const {id,...updates}=editSociete;
    const {error}=await supabase.from("societes").update(updates).eq("id",id);
    if(error){showToast("Erreur mise à jour","error");return;}
    setSocietes(p=>p.map(s=>s.id===id?{...s,...updates}:s));
    setEditSociete(null);
    showToast("Société mise à jour ✓");
  }

  async function addCompte(){
    if(!newCompte.libelle||!newCompte.societe_id){showToast("Lib\u00e9ll\u00e9 et soci\u00e9t\u00e9 requis","error");return;}
    const {data}=await supabase.from("comptes_bancaires").insert([{...newCompte,entreprise_id:entreprise.id}]).select();
    if(data){setComptesBancaires(p=>[...p,data[0]]);setNewCompte({libelle:"",banque:"",iban:"",numero_compte_comptable:"",societe_id:"",principal:false});showToast("Compte ajout\u00e9 \u2713");}
  }
  async function deleteCompte(id){await supabase.from("comptes_bancaires").delete().eq("id",id);setComptesBancaires(p=>p.filter(c=>c.id!==id));}

  async function saveCategoriesCustom(recettes,depenses){
    const existing=await supabase.from("categories_custom").select("id").eq("entreprise_id",entreprise.id).limit(1);
    if(existing.data&&existing.data.length>0){
      await supabase.from("categories_custom").update({recettes,depenses}).eq("entreprise_id",entreprise.id);
    } else {
      await supabase.from("categories_custom").insert([{entreprise_id:entreprise.id,recettes,depenses}]);
    }
    setCategoriesCustom({recettes,depenses});
    showToast("Cat\u00e9gories sauvegard\u00e9es \u2713");
  }
  async function addCatRecette(){
    if(!newCatRecette.trim()) return;
    const updated=[...(categoriesCustom.recettes||[]),newCatRecette.trim()];
    await saveCategoriesCustom(updated,categoriesCustom.depenses||[]);
    setNewCatRecette("");
  }
  async function addCatDepense(){
    if(!newCatDepense.trim()) return;
    const updated=[...(categoriesCustom.depenses||[]),newCatDepense.trim()];
    await saveCategoriesCustom(categoriesCustom.recettes||[],updated);
    setNewCatDepense("");
  }
  async function removeCatRecette(cat){
    const updated=(categoriesCustom.recettes||[]).filter(c=>c!==cat);
    await saveCategoriesCustom(updated,categoriesCustom.depenses||[]);
  }
  async function removeCatDepense(cat){
    const updated=(categoriesCustom.depenses||[]).filter(c=>c!==cat);
    await saveCategoriesCustom(categoriesCustom.recettes||[],updated);
  }

  const CSS=`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    body,input,select,textarea{font-family:'Inter','Segoe UI',sans-serif;}
    input,select,textarea{outline:none;}
    ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:#1F2937}::-webkit-scrollbar-thumb{background:#374151;border-radius:3px}
    .bh:hover{opacity:0.88;transform:translateY(-1px);}
    .rh:hover{background:#FAFAFA;}
    .nav:hover{background:#374151 !important;color:#F9FAFB !important;}
    @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
    @keyframes fi{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
    .fi{animation:fi 0.22s ease;}
    @keyframes si{from{transform:translateY(-8px);opacity:0}to{transform:translateY(0);opacity:1}}
    .thc:hover{background:#F3F4F6;cursor:pointer;}
    .decoBtn{position:fixed;bottom:16px;left:16px;z-index:100;background:#1F2937;border:1px solid #374151;color:#9CA3AF;border-radius:8px;padding:8px 14px;font-size:12px;cursor:pointer;font-family:inherit;transition:all 0.15s;}
    .decoBtn:hover{background:#374151;color:#F9FAFB;}
  `;

  // ── SETUP ────────────────────────────────────────────────────────────────────
  if(setupMode) return(
    <div style={{minHeight:"100vh",background:"#F9FAFB",display:"flex",alignItems:"center",justifyContent:"center"}}><style>{CSS}</style>
      <div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:16,padding:40,maxWidth:440,width:"100%",boxShadow:"0 4px 24px rgba(0,0,0,0.08)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}>
          <div style={{width:44,height:44,background:"#F97316",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:20}}>G</div>
          <div><div style={{fontSize:20,fontWeight:700,color:"#111827"}}>GestPilote</div><div style={{fontSize:12,color:"#6B7280"}}>Configuration initiale</div></div>
        </div>
        {[{k:"nom",l:"Nom du groupe",p:"Ex: Groupe Transport Antilles"},{k:"seuil_critique",l:"Seuil critique (€)",p:"2000",t:"number"},{k:"seuil_vigilance",l:"Seuil vigilance (€)",p:"5000",t:"number"}].map(({k,l,p,t="text"})=>(
          <div key={k} style={{marginBottom:14}}><label style={S.label}>{l}</label><input style={S.input} type={t} placeholder={p} value={setupForm[k]} onChange={e=>setSetupForm(p=>({...p,[k]:e.target.value}))} /></div>
        ))}
        <button className="bh" onClick={createEntreprise} style={{...S.btnPrimary,width:"100%",padding:13,fontSize:14,marginTop:6}}>Créer mon espace →</button>
        <div style={{marginTop:10,fontSize:11,color:"#9CA3AF",textAlign:"center"}}>Compte admin : admin@admin.com / admin123</div>
      </div>
    </div>
  );

  if(!user) return(
    <div style={{minHeight:"100vh",background:"#F9FAFB",display:"flex",alignItems:"center",justifyContent:"center"}}><style>{CSS}</style>
      <div style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:16,padding:40,maxWidth:400,width:"100%",boxShadow:"0 4px 24px rgba(0,0,0,0.08)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
          <div style={{width:44,height:44,background:"#F97316",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:20}}>G</div>
          <div><div style={{fontSize:20,fontWeight:700,color:"#111827"}}>GestPilote</div><div style={{fontSize:11,color:"#6B7280"}}>Gestion financière & recouvrement</div></div>
        </div>
        <div style={{height:1,background:"#F3F4F6",margin:"18px 0"}}/>
        <div style={{marginBottom:14}}><label style={S.label}>Email</label><input style={S.input} type="email" placeholder="votre@email.com" value={loginForm.email} onChange={e=>setLoginForm(p=>({...p,email:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&handleLogin()} /></div>
        <div style={{marginBottom:18}}><label style={S.label}>Mot de passe</label><input style={S.input} type="password" placeholder="••••••••" value={loginForm.mot_de_passe} onChange={e=>setLoginForm(p=>({...p,mot_de_passe:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&handleLogin()} /></div>
        {loginError&&<div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#DC2626",marginBottom:14}}>{loginError}</div>}
        <button className="bh" onClick={handleLogin} style={{...S.btnPrimary,width:"100%",padding:13,fontSize:14}}>Se connecter</button>
      </div>
    </div>
  );

  if(loading) return(
    <div style={{minHeight:"100vh",background:"#F9FAFB",display:"flex",alignItems:"center",justifyContent:"center"}}><style>{CSS}</style>
      <div style={{textAlign:"center"}}><div style={{width:36,height:36,border:"3px solid #E5E7EB",borderTop:"3px solid #F97316",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 10px"}}/><div style={{fontSize:13,color:"#6B7280"}}>Chargement...</div></div>
    </div>
  );

  const today2=new Date().toISOString().split("T")[0];
  const tachesUrgentes=taches.filter(t=>t.statut!=="Terminé"&&(t.date_prevue<=today2)&&(!t.assignee_id||t.assignee_id===user?.id||isAdmin)).length;
  const navItems = isAdmin ? [
    {id:"dashboard",icon:"📊",label:"Dashboard"},
    {id:"creances",icon:"📋",label:"Créances clients"},
    {id:"relances",icon:"🎯",label:"Suivi recouvrement"},
    {id:"previsionnel",icon:"📅",label:"Prévisionnel"},
    {id:"import",icon:"⬆️",label:"Import"},
    {id:"carnet",icon:"📓",label:"Carnet de bord",badge:tachesUrgentes},
    {id:"stats",icon:"📈",label:"Statistiques"},
    {id:"config",icon:"⚙️",label:"Configuration"},
  ] : [
    {id:"creances",icon:"📋",label:"Créances clients"},
    {id:"relances",icon:"🎯",label:"Suivi recouvrement"},
    {id:"import",icon:"⬆️",label:"Import"},
    {id:"carnet",icon:"📓",label:"Carnet de bord",badge:tachesUrgentes},
    {id:"stats",icon:"📈",label:"Statistiques"},
  ];

  // ── HELPER TRI ────────────────────────────────────────────────────────────────
  const ThSort=({col,label})=>{
    const active=sortCre.col===col;
    return <th className="thc" style={{...S.th,background:active?"#FFF7ED":undefined,color:active?"#F97316":undefined}} onClick={()=>setSortCre(p=>({col,dir:p.col===col&&p.dir==="asc"?"desc":"asc"}))}>
      {label}{active?(sortCre.dir==="asc"?" ↑":" ↓"):" ⇅"}
    </th>;
  };

  return(
    <div style={{fontFamily:"'Inter','Segoe UI',sans-serif",display:"flex",minHeight:"100vh"}}><style>{CSS}</style>

      {toast&&<div style={{position:"fixed",top:20,right:20,zIndex:9999,background:toast.type==="error"?"#7F1D1D":"#064E3B",border:`1px solid ${toast.type==="error"?"#F87171":"#34D399"}`,color:"#fff",padding:"12px 20px",borderRadius:10,fontSize:13,fontWeight:500,animation:"si 0.2s ease",boxShadow:"0 4px 12px rgba(0,0,0,0.2)"}}>{toast.msg}</div>}
      {importProgress&&<div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:9998,background:"#1F2937",padding:"12px 24px",boxShadow:"0 -2px 12px rgba(0,0,0,0.3)"}}>
        <div style={{display:"flex",alignItems:"center",gap:16,maxWidth:900,margin:"0 auto"}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
              <span style={{fontSize:12,color:"#E5E7EB",fontWeight:500}}>{importProgress.label}</span>
              <span style={{fontSize:12,color:"#9CA3AF"}}>{importProgress.pct}%</span>
            </div>
            <div style={{height:6,background:"#374151",borderRadius:3,overflow:"hidden"}}>
              <div style={{height:"100%",width:importProgress.pct+"%",background:importProgress.pct===100?"#16A34A":"#F97316",borderRadius:3,transition:"width 0.3s ease"}}></div>
            </div>
          </div>
        </div>
      </div>}

      {/* MODAL FICHE CLIENT */}
      {showFicheClient&&<Modal title={`Fiche client — ${showFicheClient.nom_client}`} onClose={()=>{setShowFicheClient(null);setEditFicheClient(null);}}>
        {(()=>{
          const fiche=getFicheClient(showFicheClient);
          const form=editFicheClient||(fiche?{...fiche}:{nom_contact:"",poste_contact:"",email:"",telephone:"",adresse:"",procedure_factures:""});
          if(!editFicheClient&&fiche) return(
            <div>
              {[{l:"Contact",v:fiche.nom_contact},{l:"Poste",v:fiche.poste_contact},{l:"Email recouvrement",v:fiche.email},{l:"Téléphone",v:fiche.telephone},{l:"Adresse",v:fiche.adresse},{l:"Procédure factures",v:fiche.procedure_factures},{l:"Pièce jointe",v:fiche.piece_jointe_url,isLink:true}].map(({l,v,isLink})=>v?
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #F3F4F6",fontSize:13}}><span style={{color:"#6B7280",minWidth:140}}>{l}</span>{isLink?<a href={v} target="_blank" rel="noopener noreferrer" style={{color:"#F97316",textDecoration:"underline",fontWeight:500}}>📎 Voir le document</a>:<span style={{fontWeight:500,textAlign:"right"}}>{v}</span>}</div>:null
              )}
              <button className="bh" onClick={()=>setEditFicheClient({...fiche})} style={{...S.btnPrimary,marginTop:16}}>Modifier</button>
            </div>
          );
          return(
            <div>
              {[{k:"nom_contact",l:"Nom du contact"},{k:"poste_contact",l:"Poste / Fonction"},{k:"email",l:"Email recouvrement"},{k:"telephone",l:"Téléphone"},{k:"adresse",l:"Adresse"}].map(({k,l})=>(
                <div key={k} style={{marginBottom:12}}><label style={S.label}>{l}</label><input style={S.input} value={form[k]||""} onChange={e=>setEditFicheClient(p=>({...p,[k]:e.target.value}))} /></div>
              ))}
              <div style={{marginBottom:16}}><label style={S.label}>Procédure de transmission des factures</label><textarea style={{...S.input,height:80,resize:"vertical"}} value={form.procedure_factures||""} onChange={e=>setEditFicheClient(p=>({...p,procedure_factures:e.target.value}))} placeholder="Comment ce client reçoit-il les factures ? Email ? Portail fournisseur ?" /></div>
            <div style={{marginBottom:16}}>
              <label style={S.label}>Pièce jointe — Procédure factures (URL)</label>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input style={{...S.input,flex:1}} type="url" placeholder="https://drive.google.com/... ou lien vers le document" value={form.piece_jointe_url||""} onChange={e=>setEditFicheClient(p=>({...p,piece_jointe_url:e.target.value}))} />
                {form.piece_jointe_url&&<a href={form.piece_jointe_url} target="_blank" rel="noopener noreferrer" style={{...S.btnFilter,fontSize:11,color:"#F97316",textDecoration:"none",padding:"7px 10px"}}>📎 Voir</a>}
              </div>
              <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>Collez un lien Google Drive, Dropbox ou tout autre URL vers le document</div>
            </div>
              <div style={{display:"flex",gap:8}}>
                <button className="bh" onClick={()=>saveFicheClient(editFicheClient||form)} style={S.btnPrimary}>Sauvegarder</button>
                <button className="bh" onClick={()=>setEditFicheClient(null)} style={S.btnSecondary}>Annuler</button>
              </div>
            </div>
          );
        })()}
      </Modal>}

      {/* MODAL PRÉVISION RÈGLEMENT */}
      {showPrevReglement&&<Modal title={`Prévision de règlement — ${showPrevReglement.nom_client}`} onClose={()=>{setShowPrevReglement(null);setNewPrevReglement({date:"",montant:""});}}>
        <div style={{marginBottom:14,fontSize:13,color:"#6B7280"}}>Solde dû : <strong style={{color:"#D97706"}}>{fmt(showPrevReglement.solde)}</strong></div>
        <div style={{marginBottom:12}}><label style={S.label}>Date de règlement prévue</label><input type="date" style={S.input} value={newPrevReglement.date} onChange={e=>setNewPrevReglement(p=>({...p,date:e.target.value}))} /></div>
        <div style={{marginBottom:16}}><label style={S.label}>Montant attendu (€)</label><input type="number" style={S.input} placeholder={showPrevReglement.solde} value={newPrevReglement.montant} onChange={e=>setNewPrevReglement(p=>({...p,montant:e.target.value}))} /></div>
        <div style={{fontSize:12,color:"#6B7280",marginBottom:16}}>Cette prévision sera ajoutée au prévisionnel de trésorerie comme recette "Incertaine".</div>
        <button className="bh" onClick={addPrevReglement} style={{...S.btnPrimary,width:"100%"}}>Ajouter au prévisionnel →</button>
      </Modal>}

      {/* MODAL RÈGLEMENT PARTIEL */}
      {showReglementModal&&<Modal title={`Règlement — ${showReglementModal.numero_document||showReglementModal.libelle}`} onClose={()=>{setShowReglementModal(null);setMontantPartiel("");}}>
        <div style={{marginBottom:16,padding:14,background:"#F9FAFB",borderRadius:8}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,fontSize:13}}><span style={{color:"#6B7280"}}>Montant initial</span><strong>{fmt(showReglementModal.montant)}</strong></div>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,fontSize:13}}><span style={{color:"#6B7280"}}>Déjà réglé</span><span style={{color:"#16A34A",fontWeight:600}}>{fmt(showReglementModal.montant_regle||0)}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:700,borderTop:"1px solid #E5E7EB",paddingTop:8,marginTop:4}}><span>Solde restant</span><span style={{color:"#D97706"}}>{fmt(showReglementModal.solde_restant??showReglementModal.montant)}</span></div>
        </div>
        <div style={{marginBottom:12}}><label style={S.label}>Montant encaissé</label>
          <input type="number" style={S.input} placeholder={`Max : ${showReglementModal.solde_restant??showReglementModal.montant}`} value={montantPartiel} onChange={e=>setMontantPartiel(e.target.value)} autoFocus />
        </div>
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          <button className="bh" onClick={()=>setMontantPartiel(String(showReglementModal.solde_restant??showReglementModal.montant))} style={{...S.btnFilter,fontSize:12}}>Solde total</button>
          <button className="bh" onClick={()=>setMontantPartiel(String(((showReglementModal.solde_restant??showReglementModal.montant)/2).toFixed(2)))} style={{...S.btnFilter,fontSize:12}}>50%</button>
        </div>
        {montantPartiel&&parseFloat(montantPartiel)>0&&parseFloat(montantPartiel)<(showReglementModal.solde_restant??showReglementModal.montant)&&(
          <div style={{marginBottom:12,padding:10,background:"#FFF7ED",borderRadius:8,fontSize:12,color:"#92400E"}}>
            ⚠️ Règlement partiel — solde restant après : <strong>{fmt((showReglementModal.solde_restant??showReglementModal.montant)-parseFloat(montantPartiel))}</strong>
          </div>
        )}
        <button className="bh" onClick={()=>{const v=parseFloat(montantPartiel);if(!isNaN(v)&&v>0){reglerFacture(showReglementModal,v);setShowReglementModal(null);setMontantPartiel("");}else showToast("Montant invalide","error");}} style={{...S.btnPrimary,width:"100%"}}>💚 Enregistrer le règlement</button>
      </Modal>}

      {/* MODAL NOTES FACTURE */}
      {showNotesFacture&&<Modal title={`Notes — ${factures.find(f=>f.id===showNotesFacture)?.numero_document||""}`} onClose={()=>{setShowNotesFacture(null);setNewNoteFacture("");}}>
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",gap:8}}>
            <input style={{...S.input,flex:1}} placeholder="Ajouter une note..." value={newNoteFacture} onChange={e=>setNewNoteFacture(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addNoteFacture(showNotesFacture)} autoFocus />
            <button className="bh" onClick={()=>addNoteFacture(showNotesFacture)} style={S.btnPrimary}>+</button>
          </div>
        </div>
        <div style={{maxHeight:360,overflow:"auto"}}>
          {!(notesFacture[showNotesFacture]?.length)&&<div style={{textAlign:"center",color:"#9CA3AF",padding:24,fontSize:13}}>Aucune note — soyez le premier à commenter</div>}
          {(notesFacture[showNotesFacture]||[]).map((n,i)=>(
            <div key={n.id||i} style={{padding:"10px 0",borderBottom:"1px solid #F3F4F6",display:"flex",gap:10}}>
              <div style={{width:30,height:30,borderRadius:"50%",background:"#F97316",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:12,flexShrink:0}}>{(n.auteur||"?")[0].toUpperCase()}</div>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:12,fontWeight:600,color:"#111827"}}>{n.auteur}</span>
                  <span style={{fontSize:11,color:"#9CA3AF"}}>{n.created_at?new Date(n.created_at).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"}):"-"}</span>
                </div>
                <div style={{fontSize:13,color:"#374151",background:"#F9FAFB",borderRadius:8,padding:"8px 12px",lineHeight:1.5}}>{n.texte}</div>
              </div>
            </div>
          ))}
        </div>
      </Modal>}

      {/* MODAL CONVERSION RÉCURRENT */}
      {showRecurrentModal&&<Modal title={`Convertir en récurrent — ${showRecurrentModal.libelle}`} onClose={()=>{setShowRecurrentModal(null);setNewRecurrentFromTx({frequence:"Mensuel",date_fin:""});}}>
        <div style={{marginBottom:14,padding:12,background:"#F9FAFB",borderRadius:8,fontSize:13}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"#6B7280"}}>Libellé</span><strong>{showRecurrentModal.libelle}</strong></div>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"#6B7280"}}>Montant</span><strong style={{color:showRecurrentModal.type==="RECETTE"?"#16A34A":"#DC2626"}}>{fmt(showRecurrentModal.montant)}</strong></div>
          <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:"#6B7280"}}>Catégorie</span><span>{showRecurrentModal.categorie}</span></div>
        </div>
        <div style={{marginBottom:12}}><label style={S.label}>Fréquence</label>
          <select style={S.input} value={newRecurrentFromTx.frequence} onChange={e=>setNewRecurrentFromTx(p=>({...p,frequence:e.target.value}))}>
            {["Mensuel","Trimestriel","Annuel"].map(f=><option key={f}>{f}</option>)}
          </select>
        </div>
        <div style={{marginBottom:16}}><label style={S.label}>Date de fin (optionnel)</label>
          <input type="date" style={S.input} value={newRecurrentFromTx.date_fin} onChange={e=>setNewRecurrentFromTx(p=>({...p,date_fin:e.target.value}))} />
        </div>
        <div style={{fontSize:12,color:"#6B7280",marginBottom:16}}>Les occurrences futures seront générées automatiquement dans le prévisionnel.</div>
        <button className="bh" onClick={confirmerRecurrentFromTx} style={{...S.btnPrimary,width:"100%"}}>🔁 Convertir en récurrent →</button>
      </Modal>}

      {/* MODAL RELEVÉ DE COMPTE */}
      {showReleveModal&&(()=>{
        const cre=showReleveModal;
        const fiche=getFicheClient(cre);
        const soc=societes.find(s=>s.id===cre.societe_id);
        const toutesFactures=getFacturesCre(cre.id).filter(f=>!f.is_avoir&&f.statut!=="Réglé");
        const facsNormales=toutesFactures.filter(f=>f.statut!=="Hors relance");
        const facsHorsRelance=toutesFactures.filter(f=>f.statut==="Hors relance");
        const facsClient=facsNormales; // pour le reste du calcul (corps relance, KPIs)
        const totalDu=facsNormales.reduce((s,f)=>s+(f.solde_restant??f.montant),0);
        const today=new Date().toLocaleDateString("fr-FR");
        const etape=cre.etape_relance||"Relevé de compte";
        const prenomContact=fiche?.nom_contact?.split(" ")[0]||"Madame, Monsieur";
        const iban=comptesBancaires.find(c=>c.societe_id===cre.societe_id)?.iban||"[IBAN]";
        const maxRetard=Math.max(...facsClient.map(f=>calcRetard(f.date_echeance)||0),0);
        const printId="releve-print-zone";

        // ── Tableau ASCII des factures pour l'email ─────────────────────────────
        const tableauEmail=()=>{
          const col=(v,w)=>String(v).padEnd(w).slice(0,w);
          const colR=(v,w)=>String(v).padStart(w).slice(0,w);
          const sep="─";
          const w=[16,12,12,8,14];
          const line="+"+w.map(n=>sep.repeat(n+2)).join("+")+"+";
          const row=(a,b,c,d,e)=>`| ${col(a,w[0])} | ${col(b,w[1])} | ${col(c,w[2])} | ${col(d,w[3])} | ${colR(e,w[4])} |`;
          const rows=[
            line,
            row("N° Facture","Date fac.","Échéance","Retard","Solde dû"),
            line,
            ...facsClient.map(f=>row(f.numero_document||"-",fmtDate(f.date_ecriture||f.date_echeance),fmtDate(f.date_echeance),calcRetard(f.date_echeance)>0?calcRetard(f.date_echeance)+"j":"À échoir",fmt(f.solde_restant??f.montant))),
            line,
            row("TOTAL DÛ","","","",fmt(totalDu)),
            line,
          ];
          return rows.join("\n");
        };

        // ── Texte seul pour le PDF (sans objet, sans liste factures) ─────────────
        const getCorpsTexte=()=>{
          if(etape==="Relevé de compte") return `Madame, Monsieur,\n\nVeuillez trouver ci-dessous votre relevé de compte au ${today}.\n\nNous vous remercions de bien vouloir régulariser votre situation dans les meilleurs délais.`;
          if(etape==="1ère relance") return `Bonjour ${prenomContact},\n\nSauf erreur de notre part, nous n'avons pas encore reçu le règlement des factures ci-dessous, dont l'échéance est dépassée.\n\nPourriez-vous nous confirmer la date de mise en paiement ? Nous restons disponibles pour toute question.`;
          if(etape==="2ème relance") return `Bonjour ${prenomContact},\n\nSauf règlement de votre part intervenu entre-temps, nous constatons que les factures mentionnées ci-dessous demeurent à ce jour impayées, malgré notre première relance.\n\nRetard constaté : ${maxRetard} jours.\n\nConformément à nos Conditions Générales de Vente et à la législation en vigueur (Article L441-10 du Code de Commerce), une indemnité forfaitaire de recouvrement de 40 € est désormais applicable.\n\nNous vous demandons de bien vouloir procéder au règlement de la somme totale de ${fmt(totalDu)} par virement bancaire, dans un délai de 8 jours ouvrés à compter de la réception du présent message.\n\nIBAN : ${iban}\nLibellé : ${soc?.nom||entreprise?.nom} — Règlement factures\n\nSi vous rencontrez des difficultés de paiement passagères, nous vous invitons à nous contacter dans les meilleurs délais.\n\nÀ défaut de règlement dans le délai indiqué, nous nous verrons dans l'obligation de vous adresser une mise en demeure.`;
          return `Madame, Monsieur,\n\nMalgré nos relances successives, nous n'avons toujours pas reçu le règlement des sommes figurant ci-dessous.\n\nNous vous mettons en demeure de régler la somme totale de ${fmt(totalDu+40)} (dont 40 € d'indemnité forfaitaire de recouvrement) sous 15 jours à compter de la réception du présent courrier.\n\nÀ défaut, nous nous verrons contraints d'engager une procédure judiciaire sans autre avertissement.`;
        };

        // ── Email complet avec tableau ASCII ────────────────────────────────────
        const getCorpsMessage=()=>{
          const sig=soc?.signature_email||("Cordialement,\n"+(soc?.nom||entreprise?.nom));
          const objet={
            "Relevé de compte":`Objet : Relevé de compte au ${today}`,
            "1ère relance":`Objet : Facture(s) — 1ère relance — ${soc?.nom||entreprise?.nom}`,
            "2ème relance":`Objet : RELANCE N°2 — Facture(s) impayée(s) — ${soc?.nom||entreprise?.nom}`,
            "Mise en demeure":`Objet : MISE EN DEMEURE — Facture(s) impayée(s) — ${soc?.nom||entreprise?.nom}`,
          }[etape]||`Objet : Relevé de compte au ${today}`;
          const totalLigne=etape==="Mise en demeure"?`\nTotal dû (hors pénalités) : ${fmt(totalDu)}\nIndemnité forfaitaire : 40,00 €\nTOTAL À RÉGLER : ${fmt(totalDu+40)}`:``;
          return [objet,"",getCorpsTexte(),"",tableauEmail(),totalLigne,"",sig].join("\n");
        };

        const printReleve=()=>{
          const el=document.getElementById(printId);
          if(!el) return;
          const w=window.open("","_blank");
          // eslint-disable-next-line no-useless-escape
          w.document.write("<!DOCTYPE html><html><head><title>"+etape+" — "+cre.nom_client+"<\/title><style>body{font-family:Arial,sans-serif;font-size:13px;color:#111;padding:40px;max-width:800px;margin:0 auto;}table{width:100%;border-collapse:collapse;margin-bottom:20px;}th{background:#F97316;color:#fff;padding:8px 12px;text-align:left;font-size:12px;}td{padding:8px 12px;border-bottom:1px solid #E5E7EB;font-size:12px;}tr:nth-child(even) td{background:#FAFAFA;}@media print{body{padding:20px;}}<\/style><\/head><body>"+el.innerHTML+"<\/body><\/html>");
          w.document.close();w.focus();
          setTimeout(()=>{w.print();},400);
        };

        const copyEmail=()=>{
          navigator.clipboard.writeText(getCorpsMessage());
          showToast("Email copié dans le presse-papiers ✓");
        };

        const etapeColors={"Relevé de compte":"#2563EB","1ère relance":"#D97706","2ème relance":"#F97316","Mise en demeure":"#DC2626"};
        const etapeColor=etapeColors[etape]||"#F97316";

        return <div style={S.modal} onClick={e=>{if(e.target===e.currentTarget)setShowReleveModal(null);}}>
          <div style={{...S.modalBox,maxWidth:780,padding:0,overflow:"hidden"}}>
            {/* Barre d'actions */}
            <div style={{padding:"14px 20px",borderBottom:"1px solid #E5E7EB",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fff"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{fontSize:15,fontWeight:700,color:"#111827"}}>📄 Relevé de compte</div>
                <span style={{...tag(etape==="Relevé de compte"?"a_echoir":etape==="1ère relance"?"1_30j":etape==="2ème relance"?"31_60j":"plus_90j"),fontSize:11}}>{etape}</span>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button className="bh" title="Copier le texte email dans le presse-papiers" onClick={copyEmail} style={{...S.btnSecondary,fontSize:12}}>📋 Copier</button>
                {fiche?.email?(
                  <button className="bh" title={`Envoyer par email à ${fiche.email}`}
                    disabled={sendingEmail}
                    onClick={async()=>{
                      // Construire le HTML de l'email
                      const lignesFacs=facsClient.map(f=>`
                        <tr style="background:${facsClient.indexOf(f)%2===0?"#fff":"#f9f9f9"}">
                          <td style="padding:7px 10px;font-family:monospace;font-size:12px">${f.numero_document||"-"}</td>
                          <td style="padding:7px 10px;font-size:11px;color:#666">${fmtDate(f.date_facture||f.date_ecriture||f.date_echeance)}</td>
                          <td style="padding:7px 10px;font-size:11px">${fmtDate(f.date_echeance)}</td>
                          <td style="padding:7px 10px;font-size:11px;font-weight:600;color:${calcRetard(f.date_echeance)>60?"#DC2626":calcRetard(f.date_echeance)>30?"#F97316":calcRetard(f.date_echeance)>0?"#D97706":"#16A34A"}">${calcRetard(f.date_echeance)>0?calcRetard(f.date_echeance)+"j retard":"À échoir"}</td>
                          <td style="padding:7px 10px;font-size:12px;text-align:right">${fmt(f.montant)}</td>
                          <td style="padding:7px 10px;font-size:12px;font-weight:700;text-align:right;color:${calcRetard(f.date_echeance)>0?"#DC2626":"#D97706"}">${fmt(f.solde_restant??f.montant)}</td>
                        </tr>`).join("");
                      const htmlEmail=`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="font-family:Arial,sans-serif;font-size:13px;color:#111;max-width:700px;margin:0 auto;padding:20px">
                        <div style="border-bottom:2px solid ${etapeColor};padding-bottom:12px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:flex-start">
                          <div><strong style="font-size:18px">${soc?.nom||entreprise?.nom}</strong><br><span style="color:${etapeColor};font-size:12px;font-weight:600">${etape}</span></div>
                          <div style="text-align:right;font-size:12px;color:#666">Date d'édition<br><strong>${today}</strong></div>
                        </div>
                        <div style="background:#f9f9f9;border:1px solid #e5e7eb;border-radius:6px;padding:12px;margin-bottom:16px;display:flex;justify-content:space-between">
                          <div><strong style="font-size:14px">${cre.nom_client}</strong><br><span style="font-size:11px;color:#666">N° compte : ${cre.numero_compte}</span></div>
                          <div style="text-align:right"><span style="font-size:11px;color:#666">Total dû</span><br><strong style="font-size:22px;color:${etapeColor}">${fmt(totalDu)}</strong></div>
                        </div>
                        <p style="line-height:1.8;margin-bottom:16px">${getCorpsTexte().split("\n").join("<br>")}</p>
                        <table style="width:100%;border-collapse:collapse;margin-bottom:16px">
                          <thead><tr style="background:${etapeColor}">
                            <th style="padding:7px 10px;text-align:left;font-size:11px;color:#fff">N° Facture</th>
                            <th style="padding:7px 10px;text-align:left;font-size:11px;color:#fff">Date facture</th>
                            <th style="padding:7px 10px;text-align:left;font-size:11px;color:#fff">Échéance</th>
                            <th style="padding:7px 10px;text-align:left;font-size:11px;color:#fff">Retard</th>
                            <th style="padding:7px 10px;text-align:right;font-size:11px;color:#fff">Montant</th>
                            <th style="padding:7px 10px;text-align:right;font-size:11px;color:#fff">Solde dû</th>
                          </tr></thead>
                          <tbody>${lignesFacs}</tbody>
                          <tfoot><tr style="background:#fff7ed">
                            <td colspan="5" style="padding:8px 10px;font-weight:700;font-size:13px">TOTAL DÛ</td>
                            <td style="padding:8px 10px;font-weight:700;font-size:14px;text-align:right;color:${etapeColor}">${fmt(totalDu)}</td>
                          </tr></tfoot>
                        </table>
                        <div style="border-top:1px solid #e5e7eb;padding-top:12px;font-size:12px;white-space:pre-wrap">${soc?.signature_email||("Cordialement,<br>"+(soc?.nom||entreprise?.nom))}</div>
                      </body></html>`;
                      const objet={"Relevé de compte":`Relevé de compte au ${today}`,"1ère relance":`1ère relance — Factures impayées`,"2ème relance":`RELANCE N°2 — Factures impayées — ${soc?.nom||entreprise?.nom}`,"Mise en demeure":`MISE EN DEMEURE — ${soc?.nom||entreprise?.nom}`}[etape]||`Relevé de compte au ${today}`;
                      // Préparer CC
                      const ccList=emailCC.split(/[,;\s]+/).map(e=>e.trim()).filter(e=>e.includes("@"));
                      // Préparer PJ
                      const attachList=emailPJ?[{content:emailPJ.base64,filename:emailPJ.name,type:emailPJ.type,disposition:"attachment"}]:[];
                      const ok = await sendRelanceEmail({
                        to: fiche.email,
                        subject: objet,
                        html: htmlEmail,
                        from_name: soc?.nom||entreprise?.nom||"Groupe Derussy",
                        from_email: soc?.email_recouvrement||null,
                        cc: ccList,
                        attachments: attachList,
                      });
                      if(ok){
                        const pjLabel=emailPJ?` — PJ : ${emailPJ.name}`:"";
                        const ccLabel=ccList.length?` — CC : ${ccList.join(", ")}`:"";
                        await tracerAction(cre.id, "Relevé envoyé", `${etape} envoyé par email à ${fiche.email}${ccLabel}${pjLabel} — ${objet}`);
                        setEmailCC("");setEmailPJ(null);
                      }
                    }}
                    style={{...S.btnPrimary,fontSize:12,opacity:sendingEmail?0.6:1}}>
                    {sendingEmail?"⏳ Envoi...":"✉️ Envoyer à "+fiche.email}
                  </button>
                ):(
                  <button className="bh" title="Aucun email renseigné dans la fiche client" disabled style={{...S.btnPrimary,fontSize:12,opacity:0.4,cursor:"not-allowed"}}>✉️ Pas d'email client</button>
                )}
                <button className="bh" title="Imprimer / Enregistrer en PDF" onClick={printReleve} style={{...S.btnSecondary,fontSize:12}}>🖨️ PDF</button>
                <button className="bh" onClick={()=>setShowReleveModal(null)} style={{...S.btnFilter,padding:"6px 12px"}}>✕</button>
              </div>
            </div>
            <div style={{padding:24,overflow:"auto",maxHeight:"78vh",background:"#F9FAFB"}}>
              <div id={printId} style={{background:"#fff",padding:32,borderRadius:8,boxShadow:"0 1px 8px rgba(0,0,0,0.08)"}}>

                {/* En-tête société */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,paddingBottom:14,borderBottom:`2px solid ${etapeColor}`}}>
                  <div>
                    <div style={{fontSize:22,fontWeight:800,color:"#111827"}}>{soc?.nom||entreprise?.nom}</div>
                    <div style={{fontSize:12,fontWeight:600,color:etapeColor,marginTop:3}}>{etape}</div>
                    {soc?.email_recouvrement&&<div style={{fontSize:11,color:"#6B7280",marginTop:2}}>{soc.email_recouvrement}</div>}
                  </div>
                  <div style={{textAlign:"right",fontSize:12,color:"#6B7280"}}>
                    <div>Date d'édition</div>
                    <div style={{fontWeight:700,color:"#111827",fontSize:14,marginTop:2}}>{today}</div>
                  </div>
                </div>

                {/* Bloc client */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
                  <div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:8,padding:14}}>
                    <div style={{fontSize:10,fontWeight:600,color:"#9CA3AF",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Client</div>
                    <div style={{fontSize:14,fontWeight:700,color:"#111827",marginBottom:4}}>{cre.nom_client}</div>
                    <div style={{fontSize:12,color:"#6B7280"}}>N° compte : {cre.numero_compte}</div>
                    {fiche?.nom_contact&&<div style={{fontSize:12,color:"#374151",marginTop:3,fontWeight:500}}>{fiche.nom_contact}{fiche.poste_contact?` — ${fiche.poste_contact}`:""}</div>}
                    {fiche?.email&&<div style={{fontSize:12,color:"#6B7280",marginTop:2}}>{fiche.email}</div>}
                  </div>
                  <div style={{background:"#FFF7ED",border:`1px solid ${etapeColor}33`,borderRadius:8,padding:14,display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"flex-end"}}>
                    <div style={{fontSize:10,fontWeight:600,color:"#9CA3AF",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Total dû</div>
                    <div style={{fontSize:28,fontWeight:800,color:etapeColor}}>{fmt(totalDu)}</div>
                    <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{facsClient.length} facture(s) en cours</div>
                  </div>
                </div>

                {/* Texte du message AVANT le tableau */}
                <div style={{fontSize:13,color:"#374151",lineHeight:1.8,marginBottom:20,whiteSpace:"pre-wrap"}}>
                  {getCorpsTexte()}
                </div>

                {/* Tableau factures APRÈS le texte */}
                <div style={{fontSize:12,fontWeight:600,color:"#111827",marginBottom:8}}>Détail des factures</div>
                <table style={{width:"100%",borderCollapse:"collapse",marginBottom:20}}>
                  <thead>
                    <tr style={{background:etapeColor}}>
                      {["N° Facture","Date facture","Libellé","Date échéance","Retard","Montant","Solde dû"].map(h=>(
                        <th key={h} style={{padding:"7px 10px",textAlign:"left",fontSize:11,fontWeight:600,color:"#fff"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {facsClient.map((f,i)=>(
                      <tr key={f.id} style={{background:i%2===0?"#fff":"#FAFAFA"}}>
                        <td style={{padding:"7px 10px",fontSize:12,fontFamily:"monospace"}}>{f.numero_document||f.numero_piece||"-"}</td>
                        <td style={{padding:"7px 10px",fontSize:11,color:"#6B7280",whiteSpace:"nowrap"}}>{fmtDate(f.date_facture||f.date_ecriture||f.date_echeance)}</td>
                        <td style={{padding:"7px 10px",fontSize:12,maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.libelle||"-"}</td>
                        <td style={{padding:"7px 10px",fontSize:11,whiteSpace:"nowrap"}}>{fmtDate(f.date_echeance)}</td>
                        <td style={{padding:"7px 10px",fontSize:11,fontWeight:600,color:calcRetard(f.date_echeance)>90?"#7F1D1D":calcRetard(f.date_echeance)>60?"#DC2626":calcRetard(f.date_echeance)>30?"#F97316":calcRetard(f.date_echeance)>0?"#D97706":"#16A34A"}}>
                          {calcRetard(f.date_echeance)>0?calcRetard(f.date_echeance)+"j":"À échoir"}
                        </td>
                        <td style={{padding:"7px 10px",fontSize:12,textAlign:"right"}}>{fmt(f.montant)}</td>
                        <td style={{padding:"7px 10px",fontSize:12,fontWeight:700,textAlign:"right",color:calcRetard(f.date_echeance)>0?"#DC2626":"#D97706"}}>{fmt(f.solde_restant??f.montant)}</td>
                      </tr>
                    ))}
                    {facsClient.length===0&&<tr><td colSpan={7} style={{padding:"16px 10px",textAlign:"center",color:"#9CA3AF",fontSize:12}}>Aucune facture en cours</td></tr>}
                  </tbody>
                </table>

                {/* Section Hors relance */}
                {facsHorsRelance.length>0&&<div style={{marginBottom:20}}>
                  <label style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"#6B7280",cursor:"pointer",marginBottom:10}}>
                    <input type="checkbox" checked={inclureHorsRelance} onChange={e=>setInclureHorsRelance(e.target.checked)} />
                    Inclure les {facsHorsRelance.length} facture(s) hors relance dans ce relevé
                  </label>
                  {inclureHorsRelance&&<div style={{background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:8,padding:14}}>
                    <div style={{fontSize:12,color:"#6B7280",marginBottom:10,fontStyle:"italic"}}>Nous vous rappelons par ailleurs que vous restez redevable de la/des facture(s) ci-dessous :</div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,marginBottom:10}}>
                      <thead><tr>{["N° Facture","Date fac.","Échéance","Montant"].map(h=><th key={h} style={{padding:"5px 8px",textAlign:"left",fontWeight:600,color:"#6B7280",fontSize:11,borderBottom:"1px solid #E5E7EB"}}>{h}</th>)}</tr></thead>
                      <tbody>{facsHorsRelance.map((f,i)=><tr key={f.id} style={{background:i%2===0?"#fff":"#F9FAFB"}}>
                        <td style={{padding:"5px 8px",fontFamily:"monospace",fontSize:11}}>{f.numero_document}</td>
                        <td style={{padding:"5px 8px",fontSize:11,color:"#9CA3AF"}}>{fmtDate(f.date_facture||f.date_echeance)}</td>
                        <td style={{padding:"5px 8px",fontSize:11,color:"#9CA3AF"}}>{fmtDate(f.date_echeance)}</td>
                        <td style={{padding:"5px 8px",fontWeight:600,color:"#6B7280"}}>{fmt(f.solde_restant??f.montant)}</td>
                      </tr>)}</tbody>
                    </table>
                    <div style={{textAlign:"right",fontWeight:700,fontSize:12,color:"#6B7280",borderTop:"1px solid #E5E7EB",paddingTop:8}}>
                      Sous-total hors relance : {fmt(facsHorsRelance.reduce((s,f)=>s+(f.solde_restant??f.montant),0))}
                    </div>
                  </div>}
                </div>}

                {/* Signature */}
                <div style={{borderTop:"1px solid #E5E7EB",paddingTop:12,fontSize:12,color:"#374151",whiteSpace:"pre-wrap"}}>
                  {soc?.signature_email||(("Cordialement,\n")+(soc?.nom||entreprise?.nom))}
                </div>
              </div>
            </div>
          </div>
        </div>;
      })()}

      {/* MODAL RAPPROCHEMENT BANCAIRE */}
      {showRapprochement&&(()=>{
        const tx=showRapprochement;
        const montantTx=tx.montant;
        const socId=tx.societe_id;
        // Filtrer créances selon société et recherche
        const creancesFiltrees=creances.filter(c=>{
          if(c.statut==="Réglé") return false;
          if(socId&&c.societe_id!==socId) return false;
          if(rapprochSearch) return c.nom_client.toLowerCase().includes(rapprochSearch.toLowerCase())||c.numero_compte.includes(rapprochSearch);
          return true;
        });
        const facsSelectionnees=rapprochSelected?factures.filter(f=>f.creance_id===rapprochSelected.id&&!f.is_avoir&&f.statut!=="Réglé"):[];
        const totalSaisi=Object.values(rapprochFacs).reduce((s,v)=>s+(parseFloat(v)||0),0);
        const restant=montantTx-totalSaisi;

        return <div style={S.modal} onClick={e=>{if(e.target===e.currentTarget){setShowRapprochement(null);setRapprochSelected(null);setRapprochFacs({});}}}>
          <div style={{...S.modalBox,maxWidth:820,padding:0,overflow:"hidden",maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid #E5E7EB",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fff"}}>
              <div style={{fontSize:15,fontWeight:700,color:"#111827"}}>🔗 Rapprochement bancaire</div>
              <button className="bh" onClick={()=>{setShowRapprochement(null);setRapprochSelected(null);setRapprochFacs({});}} style={{...S.btnFilter,padding:"6px 12px"}}>✕</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"280px 1fr",flex:1,minHeight:0,overflow:"hidden"}}>

              {/* Colonne gauche — transaction + recherche client */}
              <div style={{borderRight:"1px solid #E5E7EB",display:"flex",flexDirection:"column",background:"#F9FAFB",overflow:"hidden",minHeight:0}}>
                {/* Résumé transaction */}
                <div style={{padding:16,borderBottom:"1px solid #E5E7EB"}}>
                  <div style={{fontSize:10,fontWeight:600,color:"#9CA3AF",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Transaction</div>
                  <div style={{fontSize:13,fontWeight:600,color:"#111827",marginBottom:4,lineHeight:1.4}}>{tx.libelle}</div>
                  <div style={{fontSize:11,color:"#6B7280",marginBottom:8}}>{tx.date} — {getSoc(tx.societe_id)}</div>
                  <div style={{display:"flex",justifyContent:"space-between",padding:"8px 10px",background:"#fff",borderRadius:6,border:"1px solid #E5E7EB"}}>
                    <span style={{fontSize:12,color:"#6B7280"}}>Montant</span>
                    <strong style={{fontSize:14,color:"#16A34A"}}>{fmt(montantTx)}</strong>
                  </div>
                  {totalSaisi>0&&<div style={{display:"flex",justifyContent:"space-between",padding:"6px 10px",marginTop:4,background:restant<0?"#FEF2F2":"#F0FDF4",borderRadius:6,border:`1px solid ${restant<0?"#FECACA":"#BBF7D0"}`}}>
                    <span style={{fontSize:11,color:"#6B7280"}}>Restant à affecter</span>
                    <strong style={{fontSize:13,color:restant<0?"#DC2626":"#16A34A"}}>{fmt(restant)}</strong>
                  </div>}
                </div>
                {/* Recherche client */}
                <div style={{padding:12,borderBottom:"1px solid #E5E7EB"}}>
                  <input style={{...S.input,fontSize:12}} placeholder="🔍 Rechercher client ou N° compte..." value={rapprochSearch} onChange={e=>{setRapprochSearch(e.target.value);setRapprochSelected(null);setRapprochFacs({});}} />
                </div>
                {/* Liste créances */}
                <div style={{flex:1,overflowY:"auto",minHeight:0,paddingBottom:8}}>
                  {creancesFiltrees.length===0&&<div style={{padding:16,fontSize:12,color:"#9CA3AF",textAlign:"center"}}>Aucun client trouvé</div>}
                  {creancesFiltrees.map(c=>(
                    <div key={c.id} className="rh" onClick={()=>{setRapprochSelected(c);setRapprochFacs({});}} style={{padding:"10px 14px",cursor:"pointer",borderBottom:"1px solid #F3F4F6",background:rapprochSelected?.id===c.id?"#FFF7ED":"transparent",borderLeft:rapprochSelected?.id===c.id?"3px solid #F97316":"3px solid transparent"}}>
                      <div style={{fontSize:13,fontWeight:600,color:"#111827",marginBottom:2}}>{c.nom_client}</div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:11,color:"#6B7280"}}>{c.numero_compte}</span>
                        <span style={{fontSize:12,fontWeight:700,color:"#D97706"}}>{fmt(c.solde)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Colonne droite — factures du client sélectionné */}
              <div style={{display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0}}>
                {!rapprochSelected&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#9CA3AF",fontSize:13,flexDirection:"column",gap:8}}>
                  <div style={{fontSize:32}}>←</div>
                  <div>Sélectionner un client pour voir ses factures</div>
                </div>}
                {rapprochSelected&&<>
                  <div style={{padding:"12px 16px",borderBottom:"1px solid #E5E7EB",background:"#FFF7ED",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:"#111827"}}>{rapprochSelected.nom_client}</div>
                      <div style={{fontSize:11,color:"#6B7280"}}>{rapprochSelected.numero_compte} — Solde : <strong style={{color:"#D97706"}}>{fmt(rapprochSelected.solde)}</strong></div>
                    </div>
                    <button className="bh" onClick={()=>{
                      // Pré-remplir automatiquement les factures dans l'ordre d'ancienneté
                      const facsOrdre=[...facsSelectionnees].sort((a,b)=>(ancienneteOrder[b.anciennete]||0)-(ancienneteOrder[a.anciennete]||0));
                      let restantAuto=montantTx;
                      const auto={};
                      for(const f of facsOrdre){
                        if(restantAuto<=0) break;
                        const solde=f.solde_restant??f.montant;
                        const applique=Math.min(solde,restantAuto);
                        auto[f.id]=applique.toFixed(2);
                        restantAuto-=applique;
                      }
                      setRapprochFacs(auto);
                    }} style={{...S.btnFilter,fontSize:11}}>⚡ Affecter auto</button>
                  </div>
                  <div style={{flex:1,overflow:"auto",padding:12}}>
                    {facsSelectionnees.length===0&&<div style={{textAlign:"center",color:"#9CA3AF",padding:24,fontSize:12}}>Aucune facture en cours</div>}
                    {facsSelectionnees.map(f=>{
                      const solde=f.solde_restant??f.montant;
                      const val=rapprochFacs[f.id]||"";
                      return <div key={f.id} style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:10,alignItems:"center",padding:"10px 0",borderBottom:"1px solid #F3F4F6"}}>
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                            <span style={{fontFamily:"monospace",fontSize:12,fontWeight:600}}>{f.numero_document||"-"}</span>
                            {calcRetard(f.date_echeance)>0&&<span style={{fontSize:10,fontWeight:600,color:calcRetard(f.date_echeance)>60?"#DC2626":"#D97706"}}>{calcRetard(f.date_echeance)}j retard</span>}
                          </div>
                          <div style={{fontSize:11,color:"#6B7280"}}>{fmtDate(f.date_echeance)} — {f.libelle}</div>
                        </div>
                        <div style={{textAlign:"right",minWidth:80}}>
                          <div style={{fontSize:11,color:"#9CA3AF"}}>Solde</div>
                          <div style={{fontSize:13,fontWeight:700,color:"#D97706"}}>{fmt(solde)}</div>
                        </div>
                        <div style={{minWidth:130}}>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <input type="number" style={{...S.inputSm,width:85,textAlign:"right",borderColor:val&&parseFloat(val)>solde?"#EF4444":val&&parseFloat(val)>0?"#16A34A":"#D1D5DB"}} placeholder="Montant" max={solde} value={val} onChange={e=>setRapprochFacs(p=>({...p,[f.id]:e.target.value}))} />
                            <label title="Cocher pour solder la facture entièrement" style={{display:"flex",alignItems:"center",gap:3,cursor:"pointer",fontSize:11,color:"#16A34A",whiteSpace:"nowrap"}}>
                              <input type="checkbox" checked={!!val&&parseFloat(val)>=solde} onChange={e=>setRapprochFacs(p=>({...p,[f.id]:e.target.checked?String(solde):""}))} style={{cursor:"pointer"}} />
                              Soldé
                            </label>
                          </div>
                          {val&&parseFloat(val)>0&&parseFloat(val)<solde&&<div style={{fontSize:9,color:"#D97706",marginTop:2}}>Règlement partiel</div>}
                        </div>
                      </div>;
                    })}
                  </div>
                  <div style={{padding:"12px 16px",borderTop:"1px solid #E5E7EB",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fff"}}>
                    <div style={{fontSize:12,color:"#6B7280"}}>
                      {totalSaisi>0&&<span>Affecté : <strong style={{color:"#16A34A"}}>{fmt(totalSaisi)}</strong> / {fmt(montantTx)}</span>}
                      {restant<0&&<span style={{color:"#DC2626",marginLeft:8}}>⚠ Dépassement de {fmt(Math.abs(restant))}</span>}
                    </div>
                    <button className="bh" onClick={confirmerRapprochement} disabled={totalSaisi<=0||restant<0} style={{...S.btnPrimary,opacity:totalSaisi<=0||restant<0?0.5:1,fontSize:13}}>✓ Confirmer le rapprochement</button>
                  </div>
                </>}
              </div>
            </div>
          </div>
        </div>;
      })()}

      {/* MODAL RAPPROCHEMENT DEPUIS CRÉANCE */}
      {showRapprochementCreance&&(()=>{
        const factureCible=showRapprochementCreance;
        const soldeCible=factureCible.solde_restant??factureCible.montant;
        // Transactions RECETTE non encore rapprochées, filtrées par société
        const txDispos=transactions.filter(t=>
          t.type==="RECETTE"&&!t.rapproche&&
          (factureCible.societe_id?t.societe_id===factureCible.societe_id:true)
        ).sort((a,b)=>b.date?.localeCompare(a.date||"")||0);

        const txFiltered=rapprochSearch
          ?txDispos.filter(t=>t.libelle?.toLowerCase().includes(rapprochSearch.toLowerCase())||t.date?.includes(rapprochSearch))
          :txDispos;

        return <div style={S.modal} onClick={e=>{if(e.target===e.currentTarget){setShowRapprochementCreance(null);setRapprochSearch("");}}}>
          <div style={{...S.modalBox,maxWidth:800,padding:0,overflow:"hidden"}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid #E5E7EB",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fff"}}>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:"#111827"}}>🔗 Rapprocher une facture avec un encaissement</div>
                <div style={{fontSize:11,color:"#6B7280",marginTop:2}}>Facture {factureCible.numero_document} — Solde : <strong style={{color:"#D97706"}}>{fmt(soldeCible)}</strong></div>
              </div>
              <button className="bh" onClick={()=>{setShowRapprochementCreance(null);setRapprochSearch("");}} style={{...S.btnFilter,padding:"6px 12px"}}>✕</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",height:"65vh"}}>
              {/* Colonne gauche — liste des encaissements */}
              <div style={{borderRight:"1px solid #E5E7EB",display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0}}>
                <div style={{padding:12,borderBottom:"1px solid #E5E7EB",background:"#F9FAFB"}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#6B7280",marginBottom:6}}>ENCAISSEMENTS NON RAPPROCHÉS</div>
                  <input style={{...S.input,fontSize:12}} placeholder="🔍 Rechercher par libellé ou date..." value={rapprochSearch} onChange={e=>setRapprochSearch(e.target.value)} />
                </div>
                <div style={{flex:1,overflowY:"auto",minHeight:0}}>
                  {txFiltered.length===0&&<div style={{padding:16,fontSize:12,color:"#9CA3AF",textAlign:"center"}}>Aucun encaissement disponible</div>}
                  {txFiltered.map(tx=>{
                    const isSelected=rapprochFacs[factureCible.id]!==undefined&&rapprochSelected?.id===tx.id;
                    return <div key={tx.id} className="rh" onClick={()=>{
                      setRapprochSelected(tx);
                      // Pré-remplir avec le min(montant tx, solde facture)
                      const montantSuggere=Math.min(tx.montant,soldeCible).toFixed(2);
                      setRapprochFacs(prev=>({[factureCible.id]:montantSuggere}));
                    }} style={{padding:"10px 14px",cursor:"pointer",borderBottom:"1px solid #F3F4F6",background:isSelected?"#FFF7ED":"transparent",borderLeft:isSelected?"3px solid #F97316":"3px solid transparent"}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:12,fontWeight:600,color:"#111827"}} title={tx.libelle}>{tx.libelle?.slice(0,40)}{(tx.libelle?.length||0)>40?"...":""}</span>
                        <span style={{fontSize:12,fontWeight:700,color:"#16A34A"}}>{fmt(tx.montant)}</span>
                      </div>
                      <div style={{fontSize:11,color:"#6B7280"}}>{tx.date} — {getSoc(tx.societe_id)}</div>
                    </div>;
                  })}
                </div>
              </div>

              {/* Colonne droite — détail du rapprochement */}
              <div style={{display:"flex",flexDirection:"column"}}>
                {!rapprochSelected&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8,color:"#9CA3AF"}}>
                  <div style={{fontSize:28}}>←</div>
                  <div style={{fontSize:13}}>Sélectionner un encaissement</div>
                </div>}
                {rapprochSelected&&<>
                  <div style={{padding:"12px 16px",background:"#FFF7ED",borderBottom:"1px solid #E5E7EB"}}>
                    <div style={{fontSize:11,fontWeight:600,color:"#6B7280",marginBottom:4}}>ENCAISSEMENT SÉLECTIONNÉ</div>
                    <div style={{fontSize:13,fontWeight:600,color:"#111827",marginBottom:2}}>{rapprochSelected.libelle}</div>
                    <div style={{fontSize:12,color:"#6B7280"}}>{rapprochSelected.date} — <strong style={{color:"#16A34A"}}>{fmt(rapprochSelected.montant)}</strong></div>
                  </div>
                  <div style={{padding:16,flex:1}}>
                    <div style={{marginBottom:12,padding:12,background:"#F9FAFB",borderRadius:8,fontSize:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"#6B7280"}}>Facture</span><strong>{factureCible.numero_document}</strong></div>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"#6B7280"}}>Solde à régler</span><strong style={{color:"#D97706"}}>{fmt(soldeCible)}</strong></div>
                      <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:"#6B7280"}}>Encaissement dispo</span><strong style={{color:"#16A34A"}}>{fmt(rapprochSelected.montant)}</strong></div>
                    </div>
                    <div style={{marginBottom:12}}>
                      <label style={S.label}>Montant à affecter sur cette facture</label>
                      <div style={{display:"flex",gap:8,marginBottom:6}}>
                        <input type="number" style={{...S.input,flex:1}} value={rapprochFacs[factureCible.id]||""} onChange={e=>setRapprochFacs(prev=>({[factureCible.id]:e.target.value}))} placeholder="Montant" />
                        <button className="bh" onClick={()=>setRapprochFacs(prev=>({[factureCible.id]:String(soldeCible)}))} style={{...S.btnFilter,fontSize:11,whiteSpace:"nowrap"}}>Solde total</button>
                      </div>
                      {rapprochFacs[factureCible.id]&&parseFloat(rapprochFacs[factureCible.id])>0&&parseFloat(rapprochFacs[factureCible.id])<soldeCible&&
                        <div style={{fontSize:11,color:"#D97706"}}>⚠ Règlement partiel — reste {fmt(soldeCible-parseFloat(rapprochFacs[factureCible.id]))} à régler</div>}
                    </div>
                  </div>
                  <div style={{padding:"12px 16px",borderTop:"1px solid #E5E7EB",background:"#fff"}}>
                    <button className="bh" onClick={async()=>{
                      const montant=parseFloat(rapprochFacs[factureCible.id]||0);
                      if(!montant||montant<=0){showToast("Saisir un montant","error");return;}
                      // Régler la facture
                      await reglerFacture(factureCible,montant);
                      // Marquer l'encaissement comme rapproché
                      const nomCli2=selectedCreance?.nom_client||"";
                      await supabase.from("transactions").update({rapproche:true,rapproche_creance_id:selectedCreance?.id,rapproche_client_nom:nomCli2}).eq("id",rapprochSelected.id);
                      setTransactions(p=>p.map(t=>t.id===rapprochSelected.id?{...t,rapproche:true,rapproche_creance_id:selectedCreance?.id,rapproche_client_nom:nomCli2}:t));
                      // Tracer dans l'historique
                      await tracerAction(selectedCreance?.id,"Paiement reçu",`Rapprochement : ${fmt(montant)} réglé sur facture ${factureCible.numero_document} — Encaissement : ${rapprochSelected.libelle} du ${rapprochSelected.date}`);
                      showToast(`Rapprochement effectué — ${fmt(montant)} sur ${factureCible.numero_document} ✓`);
                      setShowRapprochementCreance(null);setRapprochSearch("");setRapprochSelected(null);setRapprochFacs({});
                    }} disabled={!rapprochFacs[factureCible.id]||parseFloat(rapprochFacs[factureCible.id])<=0} style={{...S.btnPrimary,width:"100%",opacity:(!rapprochFacs[factureCible.id]||parseFloat(rapprochFacs[factureCible.id])<=0)?0.5:1}}>
                      ✓ Confirmer le rapprochement
                    </button>
                  </div>
                </>}
              </div>
            </div>
          </div>
        </div>;
      })()}

      {/* MODAL IMPUTATION AVOIR */}
      {showAvoirModal&&(()=>{
        const avoir=showAvoirModal;
        const montantAvoir=Math.abs(avoir.solde_restant??avoir.montant);
        const facsOuvertes=factures.filter(f=>f.creance_id===avoir.creance_id&&!f.is_avoir&&f.statut!=="Réglé");
        return <Modal title={`Imputer l'avoir ${avoir.numero_document} — ${fmt(montantAvoir)}`} onClose={()=>setShowAvoirModal(null)}>
          <div style={{fontSize:12,color:"#6B7280",marginBottom:16}}>Sélectionnez la facture sur laquelle imputer cet avoir de <strong style={{color:"#5B21B6"}}>{fmt(montantAvoir)}</strong></div>
          {facsOuvertes.length===0&&<div style={{textAlign:"center",color:"#9CA3AF",padding:24}}>Aucune facture ouverte pour ce client</div>}
          <div style={{maxHeight:360,overflowY:"auto"}}>
            {facsOuvertes.map(f=>{
              const soldeFac=f.solde_restant??f.montant;
              const imputé=Math.min(montantAvoir,soldeFac);
              const resteAvoir=montantAvoir-imputé;
              const resteFac=soldeFac-imputé;
              return <div key={f.id} style={{border:"1px solid #E5E7EB",borderRadius:8,padding:14,marginBottom:10,cursor:"pointer"}} className="rh" onClick={async()=>{
                // Imputer l'avoir sur la facture
                const newSoldeFac=Math.max(0,resteFac);
                const newStatutFac=newSoldeFac<=0?"Réglé":"Relancé";
                await supabase.from("factures").update({solde_restant:newSoldeFac,montant_regle:(f.montant_regle||0)+imputé,statut:newStatutFac}).eq("id",f.id);
                setFactures(p=>p.map(fac=>fac.id===f.id?{...fac,solde_restant:newSoldeFac,statut:newStatutFac}:fac));
                // Marquer l'avoir comme réglé si entièrement imputé
                const newSoldeAvoir=Math.max(0,resteAvoir);
                const newStatutAvoir=newSoldeAvoir<=0?"Réglé":avoir.statut;
                await supabase.from("factures").update({solde_restant:newSoldeAvoir,statut:newStatutAvoir}).eq("id",avoir.id);
                setFactures(p=>p.map(fac=>fac.id===avoir.id?{...fac,solde_restant:newSoldeAvoir,statut:newStatutAvoir}:fac));
                // Tracer dans l'historique
                await tracerAction(avoir.creance_id,"Note interne",`Avoir ${avoir.numero_document} (${fmt(montantAvoir)}) imputé sur facture ${f.numero_document} — ${fmt(imputé)} déduits, solde facture : ${fmt(newSoldeFac)}`);
                showToast(`Avoir imputé sur ${f.numero_document} — ${fmt(imputé)} déduits ✓`);
                setShowAvoirModal(null);
              }}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <span style={{fontWeight:600,fontSize:13}}>{f.numero_document}</span>
                  <span style={{fontWeight:700,color:"#D97706"}}>{fmt(soldeFac)}</span>
                </div>
                <div style={{fontSize:11,color:"#6B7280",marginBottom:8}}>{fmtDate(f.date_echeance)} — {f.libelle?.slice(0,40)}</div>
                <div style={{background:"#F5F3FF",borderRadius:6,padding:"8px 12px",fontSize:11}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span>Avoir imputé</span><span style={{color:"#5B21B6",fontWeight:600}}>- {fmt(imputé)}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}><span>Nouveau solde facture</span><span style={{fontWeight:700,color:resteFac<=0?"#16A34A":"#D97706"}}>{fmt(resteFac)}</span></div>
                  {resteAvoir>0&&<div style={{display:"flex",justifyContent:"space-between",marginTop:4,color:"#9CA3AF"}}><span>Reste de l'avoir non imputé</span><span>{fmt(resteAvoir)}</span></div>}
                </div>
                <div style={{textAlign:"right",marginTop:8}}><button className="bh" style={{...S.btnPrimary,fontSize:11,padding:"5px 14px"}}>✓ Imputer</button></div>
              </div>;
            })}
          </div>
        </Modal>;
      })()}

      {/* SIDEBAR */}
      <div style={S.sidebar}>
        <div style={{padding:"18px 14px 14px",borderBottom:"1px solid #374151"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <div style={{width:34,height:34,background:"#F97316",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:15,flexShrink:0}}>G</div>
            <div><div style={{fontSize:13,fontWeight:700,color:"#F9FAFB"}}>GestPilote</div><div style={{fontSize:10,color:"#9CA3AF"}}>{entreprise?.nom}</div></div>
          </div>
          {isAdmin ? (
            <select style={{...S.input,background:"#374151",border:"1px solid #4B5563",color:"#F9FAFB",fontSize:12,padding:"7px 10px"}} value={selectedSociete} onChange={e=>setSelectedSociete(e.target.value)}>
              <option value="all">📊 Vue groupe consolidée</option>
              {societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          ) : (
            <div style={{background:"#374151",border:"1px solid #4B5563",borderRadius:8,padding:"7px 10px",fontSize:12,color:"#F9FAFB"}}>
              🏢 {societes.find(s=>s.id===user.societe_id)?.nom||"Ma société"}
            </div>
          )}
        </div>
        <div style={{padding:"10px 8px",flex:1}}>
          {navItems.map(({id,icon,label})=>(
            <div key={id} className={page!==id?"nav":""} onClick={()=>{setPage(id);setSelectedCreance(null);}} style={{padding:"9px 12px",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:10,marginBottom:2,transition:"all 0.12s",background:page===id?"#374151":"transparent",color:page===id?"#F9FAFB":"#9CA3AF"}}>
              <span style={{fontSize:14}}>{icon}</span><span>{label}</span>
              {id==="relances"&&facEnRetard.length>0&&<span style={{marginLeft:"auto",background:"#EF4444",color:"#fff",borderRadius:10,padding:"1px 7px",fontSize:10,fontWeight:700}}>{facEnRetard.length}</span>}
            </div>
          ))}
        </div>
        <div style={{padding:"12px 14px",borderTop:"1px solid #374151"}}>
          <div style={{fontSize:12,fontWeight:500,color:"#D1D5DB",marginBottom:2}}>{user.nom}</div>
          <span style={tag(user.role)}>{user.role}</span>
          <div onClick={()=>{supabase.auth.signOut();setUser(null);setPage("dashboard");setSelectedCreance(null);}} style={{fontSize:11,color:"#6B7280",cursor:"pointer",marginTop:8}}>← Déconnexion</div>
        </div>
      </div>

      {/* MAIN */}
      <div style={S.main}>
        <div style={{background:"#fff",borderBottom:"1px solid #E5E7EB",padding:"13px 28px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:10}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"#111827"}}>{navItems.find(n=>n.id===page)?.label}</div>
            <div style={{fontSize:11,color:"#9CA3AF"}}>{selectedSociete==="all"?"Vue consolidée groupe":getSoc(selectedSociete)}</div>
          </div>
          {alerte!=="ok"&&<div style={{display:"flex",alignItems:"center",gap:8,background:alerte==="critique"?"#FEF2F2":"#FFFBEB",border:`1px solid ${alerte==="critique"?"#FECACA":"#FDE68A"}`,borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:500,color:alerte==="critique"?"#DC2626":"#92400E"}}>
            {alerte==="critique"?"🔴 Alerte critique":"🟠 Vigilance"} — {fmt(solde)}
          </div>}
        </div>

        <div style={{padding:28}}>

          {/* ══════════ DASHBOARD ══════════ */}
          {page==="dashboard"&&<div className="fi">
            {/* MODAL DÉTAIL DASHBOARD */}
            {dashModal&&<Modal title={dashModal.title} onClose={()=>setDashModal(null)}>
              <div style={{maxHeight:420,overflow:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{dashModal.cols.map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{dashModal.items.map((item,i)=>(
                    <tr key={i} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      {dashModal.render(item)}
                    </tr>
                  ))}</tbody>
                </table>
                {dashModal.items.length===0&&<div style={{textAlign:"center",color:"#9CA3AF",padding:24}}>Aucun élément</div>}
              </div>
            </Modal>}

            {/* Filtre période */}
            <FilterBar>
              <FF label="Date début"><input type="date" style={{...S.inputSm,width:150}} value={dashDateDeb} onChange={e=>setDashDateDeb(e.target.value)} /></FF>
              <FF label="Date fin"><input type="date" style={{...S.inputSm,width:150}} value={dashDateFin} onChange={e=>setDashDateFin(e.target.value)} /></FF>
              {(dashDateDeb||dashDateFin)&&<button className="bh" onClick={()=>{setDashDateDeb("");setDashDateFin("");}} style={{...S.btnFilter,alignSelf:"flex-end"}}>✕ Réinitialiser</button>}
              {(dashDateDeb||dashDateFin)&&<div style={{fontSize:11,color:"#6B7280",alignSelf:"flex-end",padding:"6px 0"}}>
                Période : {dashDateDeb?new Date(dashDateDeb).toLocaleDateString("fr-FR"):"début"} → {dashDateFin?new Date(dashDateFin).toLocaleDateString("fr-FR"):"aujourd'hui"}
              </div>}
            </FilterBar>

            {/* Calcul des filtres de période */}
            {(()=>{
              // Filtre période sur date_facture
              const hasPeriode=dashDateDeb||dashDateFin;
              const facsPeriode=hasPeriode?baseFac.filter(f=>{
                const d=f.date_facture||f.date_echeance||"";
                if(dashDateDeb&&d<dashDateDeb) return false;
                if(dashDateFin&&d>dashDateFin) return false;
                return true;
              }):baseFac;
              const facsActivePeriode=facsPeriode.filter(f=>!f.is_avoir&&f.statut!=="Réglé");
              const caPeriode=facsPeriode.filter(f=>!f.is_avoir).reduce((s,f)=>s+f.montant,0);
              const creancesAttente=facsActivePeriode.reduce((s,f)=>s+(f.solde_restant??f.montant),0);
              const litigePeriode=facsPeriode.filter(f=>f.statut==="Litigieux"&&!f.is_avoir);
              const medPeriode=hasPeriode
                ?baseCre.filter(c=>c.etape_relance==="Mise en demeure"&&facsPeriode.some(f=>f.creance_id===c.id))
                :baseCre.filter(c=>c.etape_relance==="Mise en demeure");
              const tranchesPeriode={
                "1_30j":facsPeriode.filter(f=>f.anciennete==="1_30j"&&f.statut!=="Réglé"&&!f.is_avoir),
                "31_60j":facsPeriode.filter(f=>f.anciennete==="31_60j"&&f.statut!=="Réglé"&&!f.is_avoir),
                "61_90j":facsPeriode.filter(f=>f.anciennete==="61_90j"&&f.statut!=="Réglé"&&!f.is_avoir),
                "plus_90j":facsPeriode.filter(f=>f.anciennete==="plus_90j"&&f.statut!=="Réglé"&&!f.is_avoir),
              };
              return <>

            {/* LIGNE 1 — CA + Créances période */}
            {hasPeriode&&<>
              <div style={{fontSize:12,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Activité de la période</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18}}>
                <div style={{...S.card,borderColor:"#DBEAFE"}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>CA facturé sur la période</div>
                  <div style={{fontSize:26,fontWeight:700,color:"#1E40AF"}}>{fmt(caPeriode)}</div>
                  <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{facsPeriode.filter(f=>!f.is_avoir).length} facture(s)</div>
                </div>
                <div style={{...S.card,borderColor:"#FED7AA"}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>Créances en attente sur la période</div>
                  <div style={{fontSize:26,fontWeight:700,color:"#D97706"}}>{fmt(creancesAttente)}</div>
                  <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{facsActivePeriode.length} facture(s) non réglée(s)</div>
                </div>
              </div>
            </>}

            {/* LIGNE 2 — Créances */}
            <div style={{fontSize:12,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Créances & Recouvrement{hasPeriode?" (période)":""}</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:18}}>
              {/* Créances en cours */}
              <div style={{...S.card,cursor:"pointer"}} onClick={()=>setDashModal({title:"Créances en cours",cols:["Client","Société","Solde","Étape"],items:baseCre.filter(c=>c.statut!=="Réglé"),render:c=>[
                <td key="n" style={{...S.td,fontWeight:600}}>{c.nom_client}</td>,
                <td key="s" style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(c.societe_id)}</td>,
                <td key="m" style={{...S.td,fontWeight:700,color:"#D97706"}}>{fmt(c.solde)}</td>,
                <td key="e" style={S.td}><span style={tag(c.etape_relance==="Relevé de compte"?"a_echoir":c.etape_relance==="1ère relance"?"1_30j":c.etape_relance==="2ème relance"?"31_60j":"plus_90j")}>{c.etape_relance}</span></td>
              ]})}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5}}>Créances en cours</div>
                  <span style={{fontSize:18}}>📋</span>
                </div>
                <div style={{fontSize:26,fontWeight:700,color:"#D97706"}}>{fmt(totalCre)}</div>
                <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{baseCre.filter(c=>c.statut!=="Réglé").length} client(s) — cliquer pour détail</div>
              </div>

              {/* Factures en litige */}
              {(()=>{const items=hasPeriode?litigePeriode:baseFac.filter(f=>f.statut==="Litigieux"&&!f.is_avoir);const mt=items.reduce((s,f)=>s+(f.solde_restant??f.montant),0);return(
                <div style={{...S.card,cursor:"pointer",borderColor:items.length>0?"#EDE9FE":"#E5E7EB"}} onClick={()=>setDashModal({title:"Factures en litige",cols:["Client","N° Facture","Société","Montant"],items,render:f=>[
                  <td key="c" style={{...S.td,fontWeight:600}}>{f.nom_client}</td>,
                  <td key="n" style={{...S.td,fontFamily:"monospace",fontSize:12}}>{f.numero_document||"-"}</td>,
                  <td key="s" style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(f.societe_id)}</td>,
                  <td key="m" style={{...S.td,fontWeight:700,color:"#5B21B6"}}>{fmt(f.solde_restant??f.montant)}</td>
                ]})}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                    <div style={{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5}}>Factures en litige</div>
                    <span style={{fontSize:18}}>⚖️</span>
                  </div>
                  <div style={{fontSize:26,fontWeight:700,color:"#5B21B6"}}>{items.length}</div>
                  <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{fmt(mt)} — cliquer pour détail</div>
                </div>
              );})()}

              {/* Clients en mise en demeure */}
              {(()=>{const items=medPeriode;const mt=items.reduce((s,c)=>s+c.solde,0);return(
                <div style={{...S.card,cursor:"pointer",borderColor:items.length>0?"#FECACA":"#E5E7EB"}} onClick={()=>setDashModal({title:"Clients en mise en demeure",cols:["Client","Société","Solde","Dernière action"],items,render:c=>[
                  <td key="n" style={{...S.td,fontWeight:600}}>{c.nom_client}</td>,
                  <td key="s" style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(c.societe_id)}</td>,
                  <td key="m" style={{...S.td,fontWeight:700,color:"#DC2626"}}>{fmt(c.solde)}</td>,
                  <td key="d" style={{...S.td,fontSize:12,color:"#9CA3AF"}}>{c.date_derniere_relance||"-"}</td>
                ]})}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                    <div style={{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5}}>Mises en demeure</div>
                    <span style={{fontSize:18}}>📮</span>
                  </div>
                  <div style={{fontSize:26,fontWeight:700,color:"#DC2626"}}>{items.length}</div>
                  <div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>{fmt(mt)} — cliquer pour détail</div>
                </div>
              );})()}
            </div>

            {/* LIGNE 2 — Retards par tranche */}
            <div style={{fontSize:12,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Retards de règlement</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:18}}>
              {[{k:"1_30j",l:"1-30 jours",color:"#D97706"},{k:"31_60j",l:"31-60 jours",color:"#F97316"},{k:"61_90j",l:"61-90 jours",color:"#DC2626"},{k:"plus_90j",l:"+90 jours",color:"#7F1D1D"}].map(({k,l,color})=>{
                const items=hasPeriode?(tranchesPeriode[k]||[]):baseFac.filter(f=>f.anciennete===k&&f.statut!=="Réglé"&&!f.is_avoir);
                const mt=items.reduce((s,f)=>s+(f.solde_restant??f.montant),0);
                return <div key={k} style={{...S.cardSm,cursor:"pointer",borderColor:items.length>0?color+"66":"#E5E7EB"}} onClick={()=>setDashModal({title:`Factures échues ${l}`,cols:["Client","N° Facture","Société","Échéance","Montant dû"],items,render:f=>[
                  <td key="c" style={{...S.td,fontWeight:600}}>{f.nom_client}</td>,
                  <td key="n" style={{...S.td,fontFamily:"monospace",fontSize:12}}>{f.numero_document||"-"}</td>,
                  <td key="s" style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(f.societe_id)}</td>,
                  <td key="e" style={{...S.td,fontSize:12,color:"#9CA3AF"}}>{fmtDate(f.date_echeance)}</td>,
                  <td key="m" style={{...S.td,fontWeight:700,color}}>{fmt(f.solde_restant??f.montant)}</td>
                ]})}>
                  <div style={{...tag(k),fontSize:10,marginBottom:8}}>{l}</div>
                  <div style={{fontSize:22,fontWeight:700,color,marginBottom:2}}>{items.length}</div>
                  <div style={{fontSize:12,color:"#6B7280",fontWeight:500}}>{fmt(mt)}</div>
                  <div style={{fontSize:10,color:"#9CA3AF",marginTop:4}}>cliquer pour détail</div>
                </div>;
              })}
            </div>

            {/* LIGNE 3 — Prévisions d'encaissement */}
            <div style={{fontSize:12,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Prévisions d'encaissement</div>
            {(()=>{
              const now=new Date();
              const prevEnc=basePrev.filter(p=>p.type==="RECETTE"&&p.statut!=="Annulé");
              const mois=[0,1,2].map(offset=>{
                const d=new Date(now.getFullYear(),now.getMonth()+offset,1);
                const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                const label=d.toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                const items=prevEnc.filter(p=>p.date&&p.date.startsWith(key));
                const mt=items.reduce((s,p)=>s+p.montant,0);
                const isCurrent=offset===0;
                return {key,label,items,mt,isCurrent};
              });
              return <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:18}}>
                {mois.map(({key,label,items,mt,isCurrent})=>(
                  <div key={key} style={{...S.cardSm,cursor:"pointer",border:isCurrent?"2px solid #F97316":"1px solid #E5E7EB"}} onClick={()=>setDashModal({title:`Encaissements prévus — ${label}`,cols:["Date","Libellé","Société","Montant","Statut"],items,render:p=>[
                    <td key="d" style={{...S.td,fontSize:12,color:"#9CA3AF"}}>{p.date}</td>,
                    <td key="l" style={{...S.td,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.libelle}</td>,
                    <td key="s" style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(p.societe_id)}</td>,
                    <td key="m" style={{...S.td,fontWeight:700,color:"#16A34A"}}>{fmt(p.montant)}</td>,
                    <td key="st" style={S.td}><span style={tag(p.statut)}>{p.statut}</span></td>
                  ]})}>
                    <div style={{fontSize:11,fontWeight:600,color:isCurrent?"#F97316":"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>{label}{isCurrent&&" (en cours)"}</div>
                    <div style={{fontSize:22,fontWeight:700,color:"#16A34A",marginBottom:2}}>{fmt(mt)}</div>
                    <div style={{fontSize:11,color:"#9CA3AF"}}>{items.length} prévision(s) — cliquer pour détail</div>
                  </div>
                ))}
              </div>;
            })()}

            {/* LIGNE 4 — Vue par société */}
            {selectedSociete==="all"&&<div style={S.card}>
              <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>Vue par société</div>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>{["Société","Créances","Retard +90j","En litige","Mise en demeure","Prévisions enc."].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>{societes.map(soc=>{
                  const creS=creances.filter(c=>c.societe_id===soc.id&&c.statut!=="Réglé").reduce((s,c)=>s+c.solde,0);
                  const ret90=factures.filter(f=>f.societe_id===soc.id&&f.anciennete==="plus_90j"&&f.statut!=="Réglé"&&!f.is_avoir).reduce((s,f)=>s+(f.solde_restant??f.montant),0);
                  const litS=factures.filter(f=>f.societe_id===soc.id&&f.statut==="Litigieux").length;
                  const mdeS=creances.filter(c=>c.societe_id===soc.id&&c.etape_relance==="Mise en demeure").length;
                  const prevS=previsionnel.filter(p=>p.societe_id===soc.id&&p.type==="RECETTE").reduce((s,p)=>s+p.montant,0);
                  return <tr key={soc.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                    <td style={{...S.td,fontWeight:600}}>{soc.nom}</td>
                    <td style={{...S.td,color:"#D97706",fontWeight:500}}>{fmt(creS)}</td>
                    <td style={{...S.td,color:"#7F1D1D",fontWeight:500}}>{fmt(ret90)}</td>
                    <td style={S.td}>{litS>0?<span style={tag("Litigieux")}>{litS}</span>:<span style={{color:"#9CA3AF"}}>—</span>}</td>
                    <td style={S.td}>{mdeS>0?<span style={tag("plus_90j")}>{mdeS}</span>:<span style={{color:"#9CA3AF"}}>—</span>}</td>
                    <td style={{...S.td,color:"#16A34A",fontWeight:500}}>{fmt(prevS)}</td>
                  </tr>;
                })}</tbody>
              </table>
            </div>}
          </>;
            })()}
          </div>}

          {/* ══════════ CRÉANCES ══════════ */}
          {(page==="creances"||page==="relances")&&<button className="decoBtn" onClick={()=>{supabase.auth.signOut();setUser(null);setPage("dashboard");setSelectedCreance(null);}}>← Déconnexion</button>}
          {page==="creances"&&!selectedCreance&&<div className="fi">
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:12}}>
              {[{l:"Total créances",v:fmt(totalCre),c:"#D97706"},{l:"Factures en retard",v:facEnRetard.length,c:"#DC2626"},{l:"Montant en retard",v:fmt(facEnRetard.reduce((s,f)=>s+(f.solde_restant||f.montant),0)),c:"#DC2626"},{l:"Clients réglés",v:baseCre.filter(c=>c.statut==="Réglé").length,c:"#16A34A"}].map(({l,v,c})=>(
                <div key={l} style={S.cardSm}><div style={{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>{l}</div><div style={{fontSize:20,fontWeight:700,color:c}}>{v}</div></div>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10}}>
              <button className="bh" title="Recalcule les statuts de toutes les créances en fonction des factures réglées" onClick={recalculerStatutsCreances} style={{...S.btnFilter,fontSize:11,color:"#16A34A",borderColor:"#BBF7D0"}}>🔄 Recalculer les statuts</button>
            </div>
            <FilterBar>
              <FF label="Client"><input style={{...S.inputSm,width:160}} placeholder="Rechercher..." value={fCreClient} onChange={e=>setFCreClient(e.target.value)} /></FF>
              <FF label="Tranche de retard">
                <select style={{...S.inputSm,width:140}} value={fCreAnciennete} onChange={e=>setFCreAnciennete(e.target.value)}>
                  <option value="">Tous</option>
                  {Object.entries(ancienneteLabel).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                </select>
              </FF>
              <FF label="Statut">
                <select style={{...S.inputSm,width:120}} value={fCreStatut} onChange={e=>setFCreStatut(e.target.value)}>
                  <option value="">Tous</option>
                  {STATUTS_FACTURE.map(s=><option key={s}>{s}</option>)}
                </select>
              </FF>
              <FF label="Montant">
                <div style={{display:"flex",gap:4}}>
                  <select style={{...S.inputSm,width:60}} value={fCreMontantOp} onChange={e=>setFCreMontantOp(e.target.value)}><option value="">—</option><option value=">">{">"}</option><option value="<">{"<"}</option></select>
                  <input style={{...S.inputSm,width:90}} type="number" placeholder="0" value={fCreMontant} onChange={e=>setFCreMontant(e.target.value)} />
                </div>
              </FF>
              {(fCreClient||fCreAnciennete||fCreMontant||fCreStatut)&&<button className="bh" onClick={()=>{setFCreClient("");setFCreAnciennete("");setFCreMontant("");setFCreMontantOp("");setFCreStatut("");}} style={{...S.btnFilter,alignSelf:"flex-end"}}>✕ Reset</button>}
              <div style={{alignSelf:"flex-end",fontSize:12,color:"#6B7280",padding:"6px 0"}}>{filteredCre.length} client(s)</div>
            </FilterBar>
            <div style={S.card}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  <ThSort col="nom_client" label="Client"/>
                  <th style={S.th}>N° compte</th>
                  <th style={S.th}>Société</th>
                  <ThSort col="solde" label="Solde"/>
                  <th style={S.th}>Nb fac.</th>
                  <th style={S.th}>Retard max</th>
                  <ThSort col="statut" label="Statut"/>
                  <th style={S.th}></th>
                </tr></thead>
                <tbody>
                  {filteredCre.map(c=>{
                    const facsCli=getFacturesCre(c.id).filter(f=>!f.is_avoir&&f.statut!=="Réglé");
                    const maxAnc=facsCli.reduce((m,f)=>(ancienneteOrder[f.anciennete]||0)>(ancienneteOrder[m]||0)?f.anciennete:m,"a_echoir");
                    const hasFiche=!!getFicheClient(c);
                    return <tr key={c.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,fontWeight:600}}>
                        {c.nom_client}
                        {hasFiche&&<span style={{...tag("Réglé"),fontSize:9,marginLeft:6}}>👤</span>}
                      </td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{c.numero_compte}</td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(c.societe_id)}</td>
                      <td style={{...S.td,fontWeight:700,color:c.statut==="Réglé"?"#16A34A":"#D97706"}}>{fmt(c.solde)}</td>
                      <td style={{...S.td,textAlign:"center"}}>{facsCli.length}</td>
                      <td style={S.td}>{c.statut==="Réglé" ? <span style={{color:"#9CA3AF",fontSize:12}}>—</span> : <span style={{...tag(maxAnc)}}>{ancienneteLabel[maxAnc]||"-"}</span>}</td>
                      <td style={S.td}><span style={tag(c.statut)}>{c.statut}</span></td>
                      <td style={{...S.td,whiteSpace:"nowrap"}}>
                        <div style={{display:"flex",gap:4}}>
                          <button className="bh" title="Voir le détail des factures et l'historique" onClick={async()=>{setSelectedCreance(c);await loadHistorique(c.id);}} style={{...S.btnSecondary,fontSize:12,padding:"5px 12px"}}>Voir →</button>
                          <button className="bh" title="Fiche client (contact, email, procédure)" onClick={()=>{setShowFicheClient(c);setEditFicheClient(hasFiche?null:{nom_contact:"",poste_contact:"",email:"",telephone:"",adresse:"",procedure_factures:"",piece_jointe_url:""});}} style={{...S.btnFilter,fontSize:11,padding:"5px 10px"}}>👤</button>
                          <button className="bh" title="Saisir une prévision de règlement vers le prévisionnel" onClick={()=>setShowPrevReglement(c)} style={{...S.btnGreen,fontSize:11,padding:"5px 10px"}}>📅</button>
                          <button className="bh" title="Générer un relevé de compte" onClick={()=>setShowReleveModal(c)} style={{...S.btnFilter,fontSize:11,padding:"5px 10px",color:"#6366F1",borderColor:"#6366F1"}}>📄</button>
                        </div>
                      </td>
                    </tr>;
                  })}
                  {filteredCre.length===0&&<tr><td colSpan={8} style={{...S.td,textAlign:"center",color:"#9CA3AF",padding:32}}>Aucune créance trouvée</td></tr>}
                </tbody>
              </table>
            </div>
          </div>}

          {/* ══════════ DÉTAIL CRÉANCE ══════════ */}
          {page==="creances"&&selectedCreance&&<div className="fi">
            <div style={{display:"flex",gap:8,marginBottom:20,alignItems:"center"}}>
              <button className="bh" onClick={()=>setSelectedCreance(null)} style={S.btnSecondary}>← Retour</button>
              <button className="bh" onClick={()=>{setShowFicheClient(selectedCreance);setEditFicheClient(getFicheClient(selectedCreance)?null:{nom_contact:"",poste_contact:"",email:"",telephone:"",adresse:"",procedure_factures:"",piece_jointe_url:""});}} style={S.btnFilter}>👤 Fiche client</button>
              <button className="bh" onClick={()=>setShowPrevReglement(selectedCreance)} style={S.btnGreen}>📅 Prévision règlement</button>
              <button className="bh" title="Générer un relevé de compte PDF" onClick={()=>setShowReleveModal(selectedCreance)} style={{...S.btnSecondary,borderColor:"#6366F1",color:"#6366F1"}}>📄 Relevé de compte</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20}}>
              <div style={S.card}>
                <div style={{fontSize:16,fontWeight:700,color:"#111827",marginBottom:16}}>{selectedCreance.nom_client}</div>
                {[{l:"Compte",v:selectedCreance.numero_compte},{l:"Société",v:getSoc(selectedCreance.societe_id)},{l:"Solde dû",v:fmt(selectedCreance.solde)},{l:"Import",v:selectedCreance.date_import}].map(({l,v})=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #F3F4F6",fontSize:13}}><span style={{color:"#6B7280"}}>{l}</span><span style={{fontWeight:500}}>{v}</span></div>
                ))}
                {(()=>{const fiche=getFicheClient(selectedCreance);return fiche?(
                  <div style={{marginTop:12,padding:10,background:"#F9FAFB",borderRadius:8,fontSize:12}}>
                    {fiche.nom_contact&&<div><span style={{color:"#6B7280"}}>Contact : </span><strong>{fiche.nom_contact}</strong>{fiche.poste_contact&&` — ${fiche.poste_contact}`}</div>}
                    {fiche.email&&<div><span style={{color:"#6B7280"}}>Email : </span>{fiche.email}</div>}
                    {fiche.telephone&&<div><span style={{color:"#6B7280"}}>Tél : </span>{fiche.telephone}</div>}
                  </div>
                ):null;})()}
                <div style={{marginTop:14}}>
                  <label style={S.label}>Étape de relance</label>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {ETAPES_RELANCE.map(e=><button key={e} className="bh" onClick={()=>updateCreance(selectedCreance.id,{etape_relance:e})} style={{...selectedCreance.etape_relance===e?S.btnPrimary:S.btnSecondary,padding:"5px 10px",fontSize:11}}>{e}</button>)}
                  </div>
                </div>
              </div>
              <div style={S.card}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>Ajouter une action</div>
                <div style={{marginBottom:12}}><label style={S.label}>Type d'action</label><select style={S.input} value={newAnnotation.type_action} onChange={e=>setNewAnnotation(p=>({...p,type_action:e.target.value}))}>{TYPES_ACTION.map(t=><option key={t}>{t}</option>)}</select></div>
                <div style={{marginBottom:12}}><label style={S.label}>Annotation</label><textarea style={{...S.input,height:80,resize:"vertical"}} placeholder="Détails, engagement de paiement..." value={newAnnotation.annotation} onChange={e=>setNewAnnotation(p=>({...p,annotation:e.target.value}))} /></div>
                <div style={{marginBottom:12}}><label style={S.label}>Montant reçu (si paiement)</label><input type="number" style={S.input} placeholder="0" value={newAnnotation.montant_regle} onChange={e=>setNewAnnotation(p=>({...p,montant_regle:e.target.value}))} /></div>
                {/* Option planifier un rappel */}
                <div style={{marginBottom:12,padding:"10px 12px",background:"#F9FAFB",borderRadius:8,border:"1px solid #E5E7EB"}}>
                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,fontWeight:500,color:"#374151"}}>
                    <input type="checkbox" checked={rappelActif} onChange={e=>setRappelActif(e.target.checked)} />
                    Planifier un rappel / tâche à venir
                  </label>
                  {rappelActif&&<div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{display:"flex",gap:8}}>
                      <button className="bh" onClick={()=>setRappelMode("jours")} style={{...rappelMode==="jours"?S.btnPrimary:S.btnFilter,padding:"5px 12px",fontSize:12}}>Dans X jours</button>
                      <button className="bh" onClick={()=>setRappelMode("date")} style={{...rappelMode==="date"?S.btnPrimary:S.btnFilter,padding:"5px 12px",fontSize:12}}>Date précise</button>
                    </div>
                    {rappelMode==="jours"
                      ?<div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:12,color:"#6B7280"}}>Dans</span>
                          <input type="number" min="1" max="90" value={rappelJours} onChange={e=>setRappelJours(parseInt(e.target.value)||1)} style={{...S.input,width:60}} />
                          <span style={{fontSize:12,color:"#6B7280"}}>jours ({(()=>{const d=new Date();d.setDate(d.getDate()+rappelJours);return d.toLocaleDateString("fr-FR");})()})</span>
                        </div>
                      :<input type="date" value={rappelDate} onChange={e=>setRappelDate(e.target.value)} style={S.input} min={new Date().toISOString().split("T")[0]} />
                    }
                    <input style={S.input} placeholder="Objet du rappel (ex: Rappel téléphonique BMJ)..." value={rappelTitre} onChange={e=>setRappelTitre(e.target.value)} />
                  </div>}
                </div>
                <button className="bh" onClick={async()=>{
                  if(!newAnnotation.annotation){showToast("Saisir une annotation","error");return;}
                  const rappel=rappelActif?{
                    jours:rappelMode==="jours"?rappelJours:Math.max(1,Math.floor((new Date(rappelDate)-new Date())/(1000*60*60*24))),
                    titre:rappelTitre||`Rappel — ${selectedCreance?.nom_client||""}`,
                    description:newAnnotation.annotation,
                    dateExacte:rappelMode==="date"?rappelDate:null,
                  }:null;
                  await tracerActionAvecTache(selectedCreance.id,newAnnotation.type_action,newAnnotation.annotation,rappel);
                  setNewAnnotation({type_action:"Note interne",annotation:"",montant_regle:0});
                  setRappelActif(false);setRappelJours(2);setRappelDate("");setRappelTitre("");
                  showToast("Action enregistrée ✓");
                }} style={{...S.btnPrimary,width:"100%"}}>Enregistrer</button>
              </div>
            </div>


            {/* Tableau des factures avec annotations */}
            {(()=>{
              const limit30fc=new Date();limit30fc.setDate(limit30fc.getDate()-30);
              const l30fc=limit30fc.toISOString().split("T")[0];
              const showRegl=showFacturesReglees[selectedCreance.id]||false;
              const facsClient=getFacturesCre(selectedCreance.id).filter(f=>{
                if(f.statut!=="Réglé") return true;
                if(showRegl) return true;
                const d=f.date_realisation||"";
                return !(d&&d<l30fc);
              }).sort((a,b)=>{
                let va=a[sortFac.col]||"",vb=b[sortFac.col]||"";
                if(sortFac.col==="montant"||sortFac.col==="solde_restant"||sortFac.col==="jours_retard"){va=parseFloat(va)||0;vb=parseFloat(vb)||0;}
                const cmp=typeof va==="number"?va-vb:String(va).localeCompare(String(vb));
                return sortFac.dir==="asc"?cmp:-cmp;
              });
              return <div style={{...S.card,marginBottom:20}}>
              <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>Détail des factures ({facsClient.length})</div>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>
                    {[{col:"numero_document",label:"N° document"},{col:"date_facture",label:"Date facture"},{col:null,label:"Libellé"},{col:"date_echeance",label:"Échéance"},{col:"jours_retard",label:"Retard"},{col:"montant",label:"Montant"},{col:"solde_restant",label:"Solde restant"},{col:"statut",label:"Statut"},{col:null,label:"Actions"}].map(({col,label})=>(
                      <th key={label} style={{...S.th,cursor:col?"pointer":"default",userSelect:"none",whiteSpace:"nowrap"}}
                        onClick={()=>col&&setSortFac(p=>({col,dir:p.col===col&&p.dir==="asc"?"desc":"asc"}))}>
                        {label}{col&&sortFac.col===col?(sortFac.dir==="asc"?" ↑":" ↓"):col?" ⇅":""}
                      </th>
                    ))}
                  </tr></thead>
                  <tbody>
                  {facsClient.map(f=>(
                    <tr key={f.id} className="rh" style={{borderTop:"1px solid #F3F4F6",background:f.is_avoir?"#F0FDF4":f.statut==="Réglé"?"#F0FDF4":undefined}}>
                      <td style={{...S.td,fontFamily:"monospace",fontSize:11}}>{f.numero_document||f.numero_piece||"-"}</td>
                      <td style={{...S.td,fontSize:11,color:"#6B7280",whiteSpace:"nowrap"}}>{fmtDate(f.date_facture||f.date_ecriture||f.date_echeance)}</td>
                      <td style={{...S.td,fontSize:12,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.libelle}</td>
                      <td style={{...S.td,fontSize:11,color:"#6B7280",whiteSpace:"nowrap"}}>{fmtDate(f.date_echeance)}</td>
                      <td style={S.td}>
                        {f.is_avoir?<span style={{...tag("Réglé"),fontSize:10}}>Avoir</span>:
                        calcRetard(f.date_echeance)>0?<span style={{fontSize:11,fontWeight:600,color:calcRetard(f.date_echeance)>90?"#7F1D1D":calcRetard(f.date_echeance)>60?"#DC2626":calcRetard(f.date_echeance)>30?"#F97316":"#D97706"}}>{calcRetard(f.date_echeance)}j</span>:
                        <span style={{fontSize:11,color:"#16A34A",fontWeight:600}}>À échoir</span>}
                      </td>
                      <td style={{...S.td,fontWeight:500,textAlign:"right"}}>{f.is_avoir?"-":""}{fmt(Math.abs(f.montant))}</td>
                      <td style={{...S.td,fontWeight:600,color:f.statut==="Réglé"?"#16A34A":"#D97706",textAlign:"right"}}>{fmt(f.solde_restant??f.montant)}</td>
                      <td style={S.td}><span style={tag(f.statut)}>{f.statut}</span></td>
                      <td style={S.td}>
                        <div style={{display:"flex",gap:4}}>
                          <button className="bh" title="Ajouter / voir les notes horodatées" onClick={async()=>{setShowNotesFacture(f.id);await loadNotesFacture(f.id);}} style={{...S.btnFilter,padding:"4px 8px",fontSize:12}}>📝{(notesFacture[f.id]?.length||0)>0?` ${notesFacture[f.id].length}`:""}</button>
                          {!f.is_avoir&&f.statut!=="Réglé"&&<>
                            <button className="bh" title="Marquer cette facture comme réglée" onClick={async()=>{
                              if(!window.confirm("Marquer la facture "+f.numero_document+" comme Réglée ?")) return;
                              await reglerFacture(f,f.solde_restant??f.montant);
                              await tracerAction(f.creance_id,"Paiement reçu","Facture marquée Réglée manuellement — "+fmt(f.solde_restant??f.montant));
                              showToast("Facture réglée ✓");
                            }} style={{...S.btnGreen,padding:"4px 10px",fontSize:12}}>✓ Réglé</button>
                            <button className="bh" title="Marquer en litige" onClick={()=>updateFactureStatut(f.id,"Litigieux")} style={{...S.btnDanger,padding:"4px 8px",fontSize:12}}>⚠</button>
                            {f.statut!=="Hors relance"
                              ?<button className="bh" title="Marquer Hors relance — facture due mais à ne pas relancer" onClick={()=>updateFactureStatut(f.id,"Hors relance")} style={{...S.btnFilter,padding:"4px 8px",fontSize:11,color:"#6B7280"}}>🔕</button>
                              :<button className="bh" title="Remettre En attente" onClick={()=>updateFactureStatut(f.id,"En attente")} style={{...S.btnFilter,padding:"4px 8px",fontSize:11,color:"#F97316"}}>🔔</button>
                            }
                          </>}
                          {f.is_avoir&&(f.solde_restant??f.montant)<0&&<button className="bh" title="Imputer cet avoir sur une facture" onClick={()=>setShowAvoirModal(f)} style={{...S.btnFilter,padding:"4px 10px",fontSize:12,color:"#5B21B6",borderColor:"#DDD6FE"}}>🔗 Imputer</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            {/* Bouton voir factures réglées archivées */}
            {(()=>{
              const toutesReglees=getFacturesCre(selectedCreance.id).filter(f=>f.statut==="Réglé");
              const limit30b=new Date();limit30b.setDate(limit30b.getDate()-30);const l30b=limit30b.toISOString().split("T")[0];
              const masquees=toutesReglees.filter(f=>{const d=f.date_realisation||"";return d&&d<l30b;});
              if(!masquees.length) return null;
              const show=showFacturesReglees[selectedCreance.id];
              return <div style={{textAlign:"center",padding:"8px 0",borderTop:"1px solid #F3F4F6"}}>
                <button className="bh" onClick={()=>setShowFacturesReglees(p=>({...p,[selectedCreance.id]:!show}))}
                  style={{...S.btnFilter,fontSize:12,color:"#6B7280"}}>
                  {show?`▲ Masquer les ${masquees.length} facture(s) réglée(s) archivée(s)`:`▼ Voir les ${masquees.length} facture(s) réglée(s) archivée(s)`}
                </button>
              </div>;
            })()}
            </div>
            })()}

            {/* Historique */}
            <div style={S.card}>
              <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>Historique ({historique.length})</div>
              {historique.length===0&&<div style={{color:"#9CA3AF",fontSize:13,textAlign:"center",padding:20}}>Aucune action enregistrée</div>}
              {historique.map(h=>(
                <div key={h.id} style={{padding:"12px 0",borderBottom:"1px solid #F3F4F6",display:"flex",gap:12}}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:"#FEF3C7",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>
                    {h.annotation?.startsWith("[PLANIFIÉ]")?"🕐":h.annotation?.startsWith("[RÉALISÉ]")?"✅":h.type_action==="Paiement reçu"?"💚":h.type_action==="Appel téléphonique"?"📞":h.type_action==="Note interne"?"📝":h.type_action==="Relevé envoyé"?"📤":h.type_action==="Relance envoyée"?"🔔":h.type_action==="Mise en demeure"?"⚖️":h.type_action==="Rapprochement"?"🔗":"📧"}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><span style={{fontSize:13,fontWeight:600}}>{h.type_action}</span><span style={{fontSize:11,color:"#9CA3AF"}}>{h.date_action} — {h.auteur}</span></div>
                    {h.montant_regle>0&&<div style={{fontSize:12,color:"#16A34A",fontWeight:600,marginBottom:3}}>Paiement : {fmt(h.montant_regle)}</div>}
                    {h.annotation&&<div style={{fontSize:13,color:"#374151",background:"#F9FAFB",borderRadius:8,padding:"8px 12px"}}>{h.annotation}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>}

          {/* ══════════ SUIVI RECOUVREMENT ══════════ */}
          {page==="relances"&&<div className="fi">

            {/* ── BLOC ACTIONS À FAIRE AUJOURD'HUI ── */}
            {(()=>{
              const today=new Date().toISOString().split("T")[0];
              // Factures +90j sans action récente
              const urgentes=baseFac.filter(f=>f.anciennete==="plus_90j"&&f.statut!=="Réglé"&&!f.is_avoir);
              // Clients en mise en demeure
              const mde=baseCre.filter(c=>c.etape_relance==="Mise en demeure");
              // Prévisions de règlement dues aujourd'hui ou dépassées
              const prevDues=basePrev.filter(p=>p.type==="RECETTE"&&p.source_creance_id&&p.date<=today&&p.statut==="Incertain");
              // Factures en litige
              const litiges=baseFac.filter(f=>f.statut==="Litigieux"&&!f.is_avoir);
              const hasActions=urgentes.length>0||mde.length>0||prevDues.length>0||litiges.length>0;
              return <>
                <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:10}}>
                  📅 Actions du jour — {new Date().toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                </div>
                {!hasActions&&<div style={{background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13,color:"#065F46",fontWeight:500}}>
                  ✓ Aucune action urgente à traiter aujourd'hui
                </div>}
                {hasActions&&<div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}}>
                  {urgentes.length>0&&<div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,padding:14,cursor:"pointer"}} onClick={()=>setFRelAnciennete(fRelAnciennete==="plus_90j"?"":"plus_90j")}>
                    <div style={{fontSize:11,fontWeight:600,color:"#DC2626",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>⚠️ Retard +90 jours</div>
                    <div style={{fontSize:22,fontWeight:800,color:"#DC2626"}}>{urgentes.length}</div>
                    <div style={{fontSize:11,color:"#9CA3AF",marginTop:3}}>{fmt(urgentes.reduce((s,f)=>s+(f.solde_restant??f.montant),0))} — Envoyer mise en demeure</div>
                  </div>}
                  {mde.length>0&&<div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,padding:14,cursor:"pointer"}} onClick={()=>{setFRelClient("");setFRelStatut("");setFRelAnciennete("");}}>
                    <div style={{fontSize:11,fontWeight:600,color:"#7F1D1D",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>📮 Mises en demeure</div>
                    <div style={{fontSize:22,fontWeight:800,color:"#7F1D1D"}}>{mde.length}</div>
                    <div style={{fontSize:11,color:"#9CA3AF",marginTop:3}}>{fmt(mde.reduce((s,c)=>s+c.solde,0))} — Suivi procédure</div>
                  </div>}
                  {prevDues.length>0&&<div style={{background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:10,padding:14}}>
                    <div style={{fontSize:11,fontWeight:600,color:"#D97706",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>💰 Règlements attendus</div>
                    <div style={{fontSize:22,fontWeight:800,color:"#D97706"}}>{prevDues.length}</div>
                    <div style={{fontSize:11,color:"#9CA3AF",marginTop:3}}>{fmt(prevDues.reduce((s,p)=>s+p.montant,0))} — Vérifier encaissement</div>
                  </div>}
                  {litiges.length>0&&<div style={{background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:10,padding:14}}>
                    <div style={{fontSize:11,fontWeight:600,color:"#5B21B6",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>⚖️ En litige</div>
                    <div style={{fontSize:22,fontWeight:800,color:"#5B21B6"}}>{litiges.length}</div>
                    <div style={{fontSize:11,color:"#9CA3AF",marginTop:3}}>{fmt(litiges.reduce((s,f)=>s+(f.solde_restant??f.montant),0))} — À suivre</div>
                  </div>}
                </div>}
              </>;
            })()}

            {/* ── KPI TRANCHES + FILTRES ── */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:18}}>
              {[{k:"a_echoir",l:"À échoir"},{k:"1_30j",l:"1-30j"},{k:"31_60j",l:"31-60j"},{k:"61_90j",l:"61-90j"},{k:"plus_90j",l:"+90j"}].map(({k,l})=>{
                const items=baseFac.filter(f=>f.anciennete===k&&f.statut!=="Réglé"&&!f.is_avoir);
                const isActive=fRelAnciennete===k;
                return <div key={k} style={{...S.cardSm,cursor:"pointer",border:isActive?"2px solid #F97316":"1px solid #E5E7EB"}} onClick={()=>setFRelAnciennete(isActive?"":k)}>
                  <div style={{...tag(k),fontSize:10,marginBottom:8}}>{l}</div>
                  <div style={{fontSize:22,fontWeight:700,color:"#111827",marginBottom:2}}>{items.length}</div>
                  <div style={{fontSize:12,color:"#D97706",fontWeight:500}}>{fmt(items.reduce((s,f)=>s+(f.solde_restant??f.montant),0))}</div>
                  {isActive&&<div style={{fontSize:10,color:"#F97316",marginTop:4,fontWeight:600}}>✓ Filtré</div>}
                </div>;
              })}
            </div>
            <FilterBar>
              <FF label="Client ou N° facture"><input style={{...S.inputSm,width:180}} placeholder="Rechercher..." value={fRelClient} onChange={e=>setFRelClient(e.target.value)} /></FF>
              {isAdmin&&<FF label="Société"><select style={{...S.inputSm,width:140}} value={fRelSociete} onChange={e=>setFRelSociete(e.target.value)}><option value="">Toutes</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></FF>}
              <FF label="Statut"><select style={{...S.inputSm,width:120}} value={fRelStatut} onChange={e=>setFRelStatut(e.target.value)}><option value="">Tous</option>{STATUTS_FACTURE.map(s=><option key={s}>{s}</option>)}</select></FF>
              <FF label="Trier par">
                <div style={{display:"flex",gap:4}}>
                  <select style={{...S.inputSm,width:140}} value={sortRel.col} onChange={e=>setSortRel(p=>({...p,col:e.target.value}))}>
                    <option value="nom_client">Client</option>
                    <option value="solde_restant">Montant</option>
                    <option value="date_echeance">Échéance</option>
                    <option value="anciennete">Ancienneté</option>
                  </select>
                  <button className="bh" onClick={()=>setSortRel(p=>({...p,dir:p.dir==="asc"?"desc":"asc"}))} style={{...S.btnFilter,padding:"6px 10px"}}>{sortRel.dir==="asc"?"↑":"↓"}</button>
                </div>
              </FF>
              {(fRelClient||fRelSociete||fRelStatut)&&<button className="bh" onClick={()=>{setFRelClient("");setFRelSociete("");setFRelStatut("");}} style={{...S.btnFilter,alignSelf:"flex-end"}}>✕ Reset</button>}
              <div style={{alignSelf:"flex-end",fontSize:12,color:"#6B7280",padding:"6px 0"}}>{filteredFacRel.length} facture(s)</div>
            </FilterBar>

            {/* ── LISTE FACTURES ── */}
            {(fRelClient||fRelSociete||fRelStatut)?(
              <div style={S.card}>
                <div style={{fontSize:13,fontWeight:600,color:"#111827",marginBottom:12}}>{filteredFacRel.length} résultat(s)</div>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Client","N° facture","Ancienneté","Société","Échéance","Montant dû","Statut",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{filteredFacRel.map(f=>(
                    <tr key={f.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,fontWeight:600}}>{f.nom_client}</td>
                      <td style={{...S.td,fontFamily:"monospace",fontSize:12}}>{f.numero_document||"-"}</td>
                      <td style={S.td}><span style={{...tag(f.anciennete),fontSize:10}}>{ancienneteLabel[f.anciennete]}</span></td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(f.societe_id)}</td>
                      <td style={{...S.td,fontSize:12,color:"#9CA3AF"}}>{fmtDate(f.date_echeance)}</td>
                      <td style={{...S.td,fontWeight:700,color:"#D97706"}}>{fmt(f.solde_restant??f.montant)}</td>
                      <td style={S.td}><span style={tag(f.statut)}>{f.statut}</span></td>
                      <td style={S.td}><button className="bh" title="Aller à la fiche client" onClick={async()=>{const cre=creances.find(c=>c.id===f.creance_id);if(cre){setSelectedCreance(cre);await loadHistorique(cre.id);setPage("creances");}}} style={S.btnSecondary}>Gérer →</button></td>
                    </tr>
                  ))}
                  {filteredFacRel.length===0&&<tr><td colSpan={8} style={{...S.td,textAlign:"center",color:"#9CA3AF",padding:32}}>Aucun résultat</td></tr>}
                  </tbody>
                </table>
              </div>
            ):(
              <>
              {(fRelAnciennete?[fRelAnciennete]:["plus_90j","61_90j","31_60j","1_30j"]).map(anc=>{
                const items=filteredFacRel.filter(f=>f.anciennete===anc);
                if(!items.length) return null;
                const colors={"1_30j":"#D97706","31_60j":"#F97316","61_90j":"#DC2626","plus_90j":"#7F1D1D","a_echoir":"#2563EB"};
                return <div key={anc} style={{...S.card,marginBottom:14}}>
                  <div style={{fontSize:14,fontWeight:600,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:colors[anc],display:"inline-block"}}/>
                    {ancienneteLabel[anc]} — {items.length} facture(s) — {fmt(items.reduce((s,f)=>s+(f.solde_restant??f.montant),0))}
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr>{["Client","N° facture","Société","Échéance","Montant dû","Statut",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>{items.map(f=>(
                      <tr key={f.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                        <td style={{...S.td,fontWeight:600}}>{f.nom_client}</td>
                        <td style={{...S.td,fontFamily:"monospace",fontSize:12}}>{f.numero_document||"-"}</td>
                        <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(f.societe_id)}</td>
                        <td style={{...S.td,fontSize:12,color:"#9CA3AF"}}>{fmtDate(f.date_echeance)}</td>
                        <td style={{...S.td,fontWeight:700,color:"#D97706"}}>{fmt(f.solde_restant??f.montant)}</td>
                        <td style={S.td}><span style={tag(f.statut)}>{f.statut}</span></td>
                        <td style={S.td}><button className="bh" title="Aller à la fiche client pour gérer cette facture" onClick={async()=>{const cre=creances.find(c=>c.id===f.creance_id);if(cre){setSelectedCreance(cre);await loadHistorique(cre.id);setPage("creances");}}} style={S.btnSecondary}>Gérer →</button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>;
              })}
              {filteredFacRel.filter(f=>f.anciennete!=="a_echoir").length===0&&<div style={{...S.card,textAlign:"center",color:"#9CA3AF",padding:48}}>Aucune facture en retard 🎉</div>}
              </>
            )}
          </div>}

                    {/* ══════════ TRANSACTIONS ══════════ */}
          {page==="transactions"&&<div className="fi">
            <div style={{...S.card,marginBottom:18}}>
              <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>+ {isAdmin?"Ajouter une transaction":"Saisir un encaissement"}</div>
              <div style={{display:"grid",gridTemplateColumns:"120px 1fr 100px"+(isAdmin?" 120px 160px 140px 140px":"")+" auto",gap:8,alignItems:"end"}}>
                <div><label style={S.label}>Date</label><input style={S.input} placeholder="JJ/MM/AAAA" value={newTx.date} onChange={e=>setNewTx(p=>({...p,date:e.target.value}))} /></div>
                <div><label style={S.label}>Libellé</label><input style={S.input} placeholder="Description..." value={newTx.libelle} onChange={e=>setNewTx(p=>({...p,libelle:e.target.value}))} /></div>
                <div><label style={S.label}>Montant</label><input style={S.input} type="number" placeholder="0" value={newTx.montant} onChange={e=>setNewTx(p=>({...p,montant:e.target.value}))} /></div>
                {isAdmin&&<><div><label style={S.label}>Type</label><select style={S.input} value={newTx.type} onChange={e=>setNewTx(p=>({...p,type:e.target.value,categorie:e.target.value==="RECETTE"?"Ventes / CA":"Divers dépenses"}))}><option>DÉPENSE</option><option>RECETTE</option></select></div>
                <div><label style={S.label}>Catégorie</label><select style={S.input} value={newTx.categorie} onChange={e=>setNewTx(p=>({...p,categorie:e.target.value}))}>{(newTx.type==="RECETTE"?catsRecettes:catsDepenses).map(c=><option key={c}>{c}</option>)}</select></div>
                <div><label style={S.label}>Société</label><select style={S.input} value={newTx.societe_id} onChange={e=>setNewTx(p=>({...p,societe_id:e.target.value}))}><option value="">—</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
                <div><label style={S.label}>Compte bancaire</label><select style={S.input} value={newTx.compte_bancaire_id} onChange={e=>setNewTx(p=>({...p,compte_bancaire_id:e.target.value}))}><option value="">—</option>{comptesBancaires.filter(c=>!newTx.societe_id||c.societe_id===newTx.societe_id).map(c=><option key={c.id} value={c.id}>{c.libelle}</option>)}</select></div></>}
                {!isAdmin&&<><div><label style={S.label}>Catégorie</label><select style={S.input} value={newTx.categorie} onChange={e=>setNewTx(p=>({...p,categorie:e.target.value}))}><option>Règlement client</option><option>Ventes / CA</option></select></div></>}
                <div><label style={{...S.label,color:"transparent"}}>.</label><button className="bh" onClick={addTx} style={S.btnPrimary}>+</button></div>
              </div>
            </div>
            <FilterBar>
              <FF label="Du"><input type="date" style={{...S.inputSm,width:130}} value={fTxDate1} onChange={e=>setFTxDate1(e.target.value)} /></FF>
              <FF label="Au"><input type="date" style={{...S.inputSm,width:130}} value={fTxDate2} onChange={e=>setFTxDate2(e.target.value)} /></FF>
              {isAdmin&&<FF label="Type"><select style={{...S.inputSm,width:110}} value={fTxType} onChange={e=>setFTxType(e.target.value)}><option value="">Tous</option><option>RECETTE</option><option>DÉPENSE</option></select></FF>}
              {isAdmin&&<FF label="Catégorie"><select style={{...S.inputSm,width:160}} value={fTxCat} onChange={e=>setFTxCat(e.target.value)}><option value="">Toutes</option>{[...catsRecettes,...catsDepenses].map(c=><option key={c}>{c}</option>)}</select></FF>}
              {isAdmin&&<FF label="Société"><select style={{...S.inputSm,width:130}} value={fTxSoc} onChange={e=>setFTxSoc(e.target.value)}><option value="">Toutes</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></FF>}
              {isAdmin&&<FF label="Compte"><select style={{...S.inputSm,width:130}} value={fTxCompte} onChange={e=>setFTxCompte(e.target.value)}><option value="">Tous</option>{comptesBancaires.map(c=><option key={c.id} value={c.id}>{c.libelle}</option>)}</select></FF>}
              <FF label="Libellé"><input style={{...S.inputSm,width:130}} placeholder="Rechercher..." value={fTxLibelle} onChange={e=>setFTxLibelle(e.target.value)} /></FF>
              {(fTxDate1||fTxDate2||fTxType||fTxCat||fTxSoc||fTxCompte||fTxLibelle||fTxMontant)&&<button className="bh" onClick={()=>{setFTxDate1("");setFTxDate2("");setFTxType("");setFTxCat("");setFTxSoc("");setFTxCompte("");setFTxLibelle("");setFTxMontant("");setFTxMontantOp("");}} style={{...S.btnFilter,alignSelf:"flex-end"}}>✕ Reset</button>}
              <div style={{alignSelf:"flex-end",fontSize:12,color:"#6B7280",padding:"6px 0"}}>{filteredTx.length} {isAdmin?"tx":"encaissement(s)"} — <span style={{color:"#16A34A"}}>{fmt(filteredTx.filter(t=>t.type==="RECETTE").reduce((s,t)=>s+t.montant,0))}</span>{isAdmin&&<> / <span style={{color:"#DC2626"}}>{fmt(filteredTx.filter(t=>t.type==="DÉPENSE").reduce((s,t)=>s+t.montant,0))}</span></>}</div>
            </FilterBar>
            <div style={S.card}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Libellé</th>
                  {isAdmin&&<th style={S.th}>Société</th>}
                  {isAdmin&&<th style={S.th}>Compte</th>}
                  {isAdmin&&<th style={S.th}>Type</th>}
                  {isAdmin&&<th style={S.th}>Catégorie</th>}
                  <th style={S.th}>Montant</th>
                  <th style={S.th}>Client</th>
                  {isAdmin&&<th style={S.th}>🔁</th>}
                  <th style={S.th}></th>
                </tr></thead>
                <tbody>{filteredTx.map(tx=>(
                  <tr key={tx.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                    <td style={{...S.td,color:"#9CA3AF",whiteSpace:"nowrap",fontSize:12}}>{tx.date}</td>
                    <td style={{...S.td,maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={tx.libelle}>{tx.libelle}</td>
                    {isAdmin&&<td style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(tx.societe_id)}</td>}
                    {isAdmin&&<td style={{...S.td,fontSize:11,color:"#9CA3AF"}}>{getCompte(tx.compte_bancaire_id)}</td>}
                    <td style={S.td}><span style={tag(tx.type)}>{tx.type}</span></td>
                    <td style={S.td}>{editTx===tx.id?<select style={{...S.input,padding:"4px 8px",width:"auto"}} defaultValue={tx.categorie} onChange={e=>updateTxCat(tx.id,e.target.value)}>{(tx.type==="RECETTE"?catsRecettes:catsDepenses).map(c=><option key={c}>{c}</option>)}</select>:<span style={{fontSize:12,color:"#6B7280",cursor:"pointer"}} onClick={()=>setEditTx(tx.id)}>{tx.categorie} ✎</span>}</td>
                    <td style={{...S.td,fontWeight:600,color:tx.type==="RECETTE"?"#16A34A":"#DC2626",textAlign:"right"}}>{tx.type==="RECETTE"?"+":"-"}{fmt(tx.montant)}</td>
                    <td style={{...S.td,textAlign:"center"}}>
                      {tx.is_recurrent
                        ? <span title="Récurrent actif" style={{fontSize:16,cursor:"default"}}>🔁</span>
                        : <button className="bh" title="Convertir en transaction récurrente — génère les occurrences dans le prévisionnel" onClick={()=>setShowRecurrentModal(tx)} style={{background:"none",border:"1px solid #E5E7EB",borderRadius:6,padding:"3px 8px",fontSize:14,cursor:"pointer"}}>🔁</button>
                      }
                    </td>
                    <td style={{...S.td,fontSize:11,maxWidth:130,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {(()=>{
                        const nomClient=tx.rapproche_client_nom||creances.find(c=>c.id===tx.rapproche_creance_id)?.nom_client||"";
                        if(tx.rapproche&&nomClient) return <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{color:"#16A34A",fontWeight:600,fontSize:11}}>✓ {nomClient}</span><button className="bh" title="Annuler le rapprochement" onClick={()=>{if(window.confirm("Annuler ce rapprochement ?\nLes factures seront remises en attente.")) desaffecterRapprochement(tx.id);}} style={{background:"none",border:"none",cursor:"pointer",color:"#DC2626",fontSize:11,padding:"1px 4px",lineHeight:1}}>↩</button></span>;
                        if(tx.rapproche&&!nomClient) return <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{color:"#16A34A",fontSize:11}}>✓ Rapproché</span><button className="bh" title="Annuler le rapprochement" onClick={()=>{if(window.confirm("Annuler ce rapprochement ?\nLes factures seront remises en attente.")) desaffecterRapprochement(tx.id);}} style={{background:"none",border:"none",cursor:"pointer",color:"#DC2626",fontSize:11,padding:"1px 4px",lineHeight:1}}>↩</button></span>;
                        if(tx.type==="RECETTE") return <button className="bh" title="Affecter ce règlement à un client" onClick={()=>{setShowRapprochement(tx);setRapprochSearch("");setRapprochSelected(null);setRapprochFacs({});}} style={{...S.btnFilter,fontSize:10,padding:"2px 7px",color:"#F97316",borderColor:"#FED7AA"}}>🔗 Affecter</button>;
                        return <span style={{color:"#D1D5DB",fontSize:11}}>—</span>;
                      })()}
                    </td>
                    {isAdmin&&<td style={S.td}>
                      {tx.is_recurrent
                        ? <span title="Récurrent actif" style={{fontSize:16,cursor:"default"}}>🔁</span>
                        : <button className="bh" title="Convertir en récurrent" onClick={()=>setShowRecurrentModal(tx)} style={{background:"none",border:"1px solid #E5E7EB",borderRadius:6,padding:"3px 8px",fontSize:14,cursor:"pointer"}}>🔁</button>
                      }
                    </td>}
                    <td style={S.td}>
                      <button className="bh" onClick={()=>deleteTx(tx.id)} style={S.btnDanger}>✕</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>}

          {/* ══════════ PRÉVISIONNEL ══════════ */}
          {page==="previsionnel"&&<div className="fi">
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:18}}>
              {[{l:"Recettes prévues",v:fmt(prevRec),c:"#16A34A"},{l:"Dépenses prévues",v:fmt(prevDep),c:"#DC2626"},{l:"Solde prévisionnel",v:fmt(prevRec-prevDep),c:(prevRec-prevDep)>=0?"#16A34A":"#DC2626"}].map(({l,v,c})=>(
                <div key={l} style={S.cardSm}><div style={{fontSize:11,fontWeight:600,color:"#6B7280",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>{l}</div><div style={{fontSize:22,fontWeight:700,color:c}}>{v}</div></div>
              ))}
            </div>
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              {["liste","mensuel","recurrents"].map(t=>(
                <button key={t} className="bh" onClick={()=>setPrevTab(t)} style={{...prevTab===t?S.btnPrimary:S.btnSecondary,padding:"8px 16px",fontSize:13}}>
                  {t==="liste"?"📋 Liste":t==="mensuel"?"📆 Par mois":"🔁 Récurrents"}
                </button>
              ))}
            </div>

            {prevTab==="liste"&&<>
              <div style={{...S.card,marginBottom:18}}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>+ Ajouter une prévision</div>
                <div style={{display:"grid",gridTemplateColumns:"120px 1fr 100px 120px 120px 140px auto",gap:8,alignItems:"end"}}>
                  <div><label style={S.label}>Date</label><input style={S.input} placeholder="AAAA-MM-JJ" type="date" value={newPrev.date} onChange={e=>setNewPrev(p=>({...p,date:e.target.value}))} /></div>
                  <div><label style={S.label}>Libellé</label><input style={S.input} placeholder="Ex: Loyer..." value={newPrev.libelle} onChange={e=>setNewPrev(p=>({...p,libelle:e.target.value}))} /></div>
                  <div><label style={S.label}>Montant</label><input style={S.input} type="number" value={newPrev.montant} onChange={e=>setNewPrev(p=>({...p,montant:e.target.value}))} /></div>
                  <div><label style={S.label}>Type</label><select style={S.input} value={newPrev.type} onChange={e=>setNewPrev(p=>({...p,type:e.target.value}))}><option>DÉPENSE</option><option>RECETTE</option></select></div>
                  <div><label style={S.label}>Statut</label><select style={S.input} value={newPrev.statut} onChange={e=>setNewPrev(p=>({...p,statut:e.target.value}))}><option>Confirmé</option><option>Incertain</option></select></div>
                  <div><label style={S.label}>Société</label><select style={S.input} value={newPrev.societe_id} onChange={e=>setNewPrev(p=>({...p,societe_id:e.target.value}))}><option value="">—</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
                  <div><label style={{...S.label,color:"transparent"}}>.</label><button className="bh" onClick={addPrev} style={S.btnPrimary}>+</button></div>
                </div>
              </div>
              <div style={S.card}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Date","Libellé","Société","Type","Montant","Statut",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{basePrev.map(p=>(
                    <tr key={p.id} className="rh" style={{borderTop:"1px solid #F3F4F6",background:p.recurrent_id?"#FAFFF7":p.source_creance_id?"#FFF8F0":undefined}}>
                      <td style={{...S.td,color:"#9CA3AF",fontSize:12}}>{p.date}</td>
                      <td style={{...S.td,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {p.libelle}
                        {p.recurrent_id&&<span style={{...tag("Réglé"),fontSize:9,marginLeft:4}}>Auto</span>}
                        {p.source_creance_id&&<span style={{...tag("1_30j"),fontSize:9,marginLeft:4}}>Règlement prévu</span>}
                      </td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(p.societe_id)}</td>
                      <td style={S.td}><span style={tag(p.type)}>{p.type}</span></td>
                      <td style={{...S.td,fontWeight:600,color:p.type==="RECETTE"?"#16A34A":"#DC2626",textAlign:"right"}}>{p.type==="RECETTE"?"+":"-"}{fmt(p.montant)}</td>
                      <td style={S.td}><span style={tag(p.statut)}>{p.statut}</span></td>
                      <td style={S.td}><button className="bh" onClick={()=>deletePrev(p.id)} style={S.btnDanger}>✕</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>}

            {prevTab==="mensuel"&&(()=>{
              // Grouper par mois
              const byMonth={};
              basePrev.forEach(p=>{
                const m=p.date?p.date.slice(0,7):"?";
                if(!byMonth[m]) byMonth[m]={rec:0,dep:0,items:[]};
                if(p.type==="RECETTE") byMonth[m].rec+=p.montant;
                else byMonth[m].dep+=p.montant;
                byMonth[m].items.push(p);
              });
              const months=Object.keys(byMonth).sort();
              let cumul=0;
              return <div style={S.card}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Mois","Recettes","Dépenses","Solde du mois","Solde cumulé"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{months.map(m=>{
                    const {rec,dep}=byMonth[m];
                    const net=rec-dep;cumul+=net;
                    const label=new Date(m+"-01").toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                    return <tr key={m} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,fontWeight:600}}>{label}</td>
                      <td style={{...S.td,color:"#16A34A",fontWeight:500}}>{fmt(rec)}</td>
                      <td style={{...S.td,color:"#DC2626",fontWeight:500}}>{fmt(dep)}</td>
                      <td style={{...S.td,fontWeight:700,color:net>=0?"#16A34A":"#DC2626"}}>{fmt(net)}</td>
                      <td style={{...S.td,fontWeight:700,color:cumul>=0?"#16A34A":"#DC2626"}}>{fmt(cumul)}</td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>;
            })()}

            {prevTab==="recurrents"&&<>
              <div style={{...S.card,marginBottom:18}}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>🔁 Nouvelle récurrence</div>
                <div style={{fontSize:12,color:"#6B7280",marginBottom:14}}>Génère automatiquement les occurrences dans le prévisionnel</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 100px 120px 120px 130px 130px 130px auto",gap:8,alignItems:"end"}}>
                  <div><label style={S.label}>Libellé</label><input style={S.input} placeholder="Ex: Loyer bureau..." value={newRecurrent.libelle} onChange={e=>setNewRecurrent(p=>({...p,libelle:e.target.value}))} /></div>
                  <div><label style={S.label}>Montant</label><input style={S.input} type="number" value={newRecurrent.montant} onChange={e=>setNewRecurrent(p=>({...p,montant:e.target.value}))} /></div>
                  <div><label style={S.label}>Type</label><select style={S.input} value={newRecurrent.type} onChange={e=>setNewRecurrent(p=>({...p,type:e.target.value}))}><option>DÉPENSE</option><option>RECETTE</option></select></div>
                  <div><label style={S.label}>Fréquence</label><select style={S.input} value={newRecurrent.frequence} onChange={e=>setNewRecurrent(p=>({...p,frequence:e.target.value}))}>{FREQUENCES.map(f=><option key={f}>{f}</option>)}</select></div>
                  <div><label style={S.label}>Début</label><input style={S.input} type="date" value={newRecurrent.date_debut} onChange={e=>setNewRecurrent(p=>({...p,date_debut:e.target.value}))} /></div>
                  <div><label style={S.label}>Fin (optionnel)</label><input style={S.input} type="date" value={newRecurrent.date_fin} onChange={e=>setNewRecurrent(p=>({...p,date_fin:e.target.value}))} /></div>
                  <div><label style={S.label}>Société</label><select style={S.input} value={newRecurrent.societe_id} onChange={e=>setNewRecurrent(p=>({...p,societe_id:e.target.value}))}><option value="">—</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
                  <div><label style={{...S.label,color:"transparent"}}>.</label><button className="bh" onClick={addRecurrent} style={S.btnPrimary}>+</button></div>
                </div>
              </div>
              <div style={S.card}>
                {recurrents.length===0&&<div style={{textAlign:"center",color:"#9CA3AF",padding:32}}>Aucune récurrence configurée</div>}
                {recurrents.length>0&&<table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Libellé","Type","Montant","Fréquence","Début","Fin","Société",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{recurrents.map(r=>(
                    <tr key={r.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,fontWeight:500}}>{r.libelle}</td>
                      <td style={S.td}><span style={tag(r.type)}>{r.type}</span></td>
                      <td style={{...S.td,fontWeight:600,color:r.type==="RECETTE"?"#16A34A":"#DC2626"}}>
                        {editRecurrent?.id===r.id?(
                          <div style={{display:"flex",gap:4}}>
                            <input style={{...S.inputSm,width:80}} type="number" value={editRecurrent.montant} onChange={e=>setEditRecurrent(p=>({...p,montant:e.target.value}))} />
                            <button className="bh" onClick={()=>updateMontantRecurrent(r.id,editRecurrent.montant)} style={{...S.btnGreen,padding:"3px 7px",fontSize:11}}>✓</button>
                            <button className="bh" onClick={()=>setEditRecurrent(null)} style={{...S.btnDanger,padding:"3px 7px",fontSize:11}}>✕</button>
                          </div>
                        ):<span style={{cursor:"pointer"}} onClick={()=>setEditRecurrent({id:r.id,montant:r.montant})}>{fmt(r.montant)} ✎</span>}
                      </td>
                      <td style={S.td}><span style={{...tag("a_echoir"),fontSize:11}}>{r.frequence}</span></td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{fmtDate(r.date_debut)}</td>
                      <td style={{...S.td,fontSize:12,color:"#9CA3AF"}}>{r.date_fin?fmtDate(r.date_fin):"—"}</td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{getSoc(r.societe_id)}</td>
                      <td style={S.td}><button className="bh" onClick={()=>deleteRecurrent(r.id)} style={S.btnDanger}>✕</button></td>
                    </tr>
                  ))}</tbody>
                </table>}
              </div>
            </>}
          </div>}

          {/* ══════════ IMPORT ══════════ */}
          {page==="import"&&<div className="fi">
            {importResult&&<div style={{background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:10,padding:"14px 20px",marginBottom:18,fontSize:13,color:"#065F46",fontWeight:500}}>
              ✓ Dernier import : {importResult.clients} client(s), {importResult.factures} facture(s) importés
            </div>}
            <div style={{display:"grid",gridTemplateColumns:isAdmin?"1fr 1fr 1fr":"1fr",gap:20}}>
              {isAdmin&&<div style={S.card}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>📥 Relevé bancaire</div>
                <div style={{fontSize:12,color:"#6B7280",marginBottom:14}}>Date ; Libellé ; Débit ; Crédit</div>
                <div style={{marginBottom:12}}><label style={S.label}>Société</label><select style={S.input} value={societeImportTx} onChange={e=>setSocieteImportTx(e.target.value)}><option value="">— Sélectionner —</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
                <div onDragOver={e=>{e.preventDefault();setDragOver(true)}} onDragLeave={()=>setDragOver(false)} onDrop={e=>onFileDrop(e,societeImportTx)} style={{border:`2px dashed ${dragOver?"#F97316":"#E5E7EB"}`,borderRadius:10,padding:28,textAlign:"center",background:dragOver?"#FFF7ED":"#F9FAFB",transition:"all 0.2s"}}>
                  <input type="file" accept=".csv,.txt" onChange={e=>onFileDrop(e,societeImportTx)} style={{display:"none"}} id="csvTx"/>
                  <label htmlFor="csvTx" style={{cursor:"pointer"}}><div style={{fontSize:24,marginBottom:6}}>⬆️</div><div style={{fontSize:13,fontWeight:500,color:"#374151"}}>Déposer ou cliquer</div><div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>CSV ; ou ,</div></label>
                </div>
              </div>}
              {isAdmin&&<div style={S.card}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>📊 Balance âgée EBP</div>
                <div style={{fontSize:12,color:"#6B7280",marginBottom:14}}>Export EBP "Balance âgée par lettrage"</div>
                <div style={{marginBottom:12}}><label style={S.label}>Société</label><select style={S.input} value={societeImportBal} onChange={e=>setSocieteImportBal(e.target.value)}><option value="">— Sélectionner —</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
                <div onDragOver={e=>{e.preventDefault();setDragOverBalance(true)}} onDragLeave={()=>setDragOverBalance(false)} onDrop={e=>onBalanceDrop(e,societeImportBal)} style={{border:`2px dashed ${dragOverBalance?"#F97316":"#E5E7EB"}`,borderRadius:10,padding:28,textAlign:"center",background:dragOverBalance?"#FFF7ED":"#F9FAFB",transition:"all 0.2s"}}>
                  <input type="file" accept=".csv,.txt" onChange={e=>onBalanceDrop(e,societeImportBal)} style={{display:"none"}} id="csvBal"/>
                  <label htmlFor="csvBal" style={{cursor:"pointer"}}><div style={{fontSize:24,marginBottom:6}}>📊</div><div style={{fontSize:13,fontWeight:500,color:"#374151"}}>Déposer ou cliquer</div><div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>Comptes 411 par facture</div></label>
                </div>
              </div>}
              <div style={S.card}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>🧾 Import factures CSV</div>
                <div style={{fontSize:12,color:"#6B7280",marginBottom:14}}>Déduplication automatique sur N° facture</div>
                <div style={{marginBottom:12}}><label style={S.label}>Société</label>
                  {isAdmin?(
                    <select style={S.input} value={societeImportFac} onChange={e=>setSocieteImportFac(e.target.value)}><option value="">— Sélectionner —</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select>
                  ):(
                    <div style={{...S.input,background:"#F9FAFB",color:"#374151"}}>{societes.find(s=>s.id===user.societe_id)?.nom||"Ma société"}</div>
                  )}
                </div>
                <div onDragOver={e=>{e.preventDefault();setDragOverFac(true)}} onDragLeave={()=>setDragOverFac(false)} onDrop={e=>onFacDrop(e,societeImportFac)} style={{border:`2px dashed ${dragOverFac?"#F97316":"#E5E7EB"}`,borderRadius:10,padding:28,textAlign:"center",background:dragOverFac?"#FFF7ED":"#F9FAFB",transition:"all 0.2s"}}>
                  <input type="file" accept=".csv,.txt" onChange={e=>onFacDrop(e,societeImportFac)} style={{display:"none"}} id="csvFac"/>
                  <label htmlFor="csvFac" style={{cursor:"pointer"}} onClick={()=>{if(!isAdmin&&user.societe_id) setSocieteImportFac(user.societe_id);}}><div style={{fontSize:24,marginBottom:6}}>🧾</div><div style={{fontSize:13,fontWeight:500,color:"#374151"}}>Déposer ou cliquer</div><div style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>N° facture, client, montant, échéance</div></label>
                </div>
              </div>
            </div>
          </div>}

          {/* Historique des imports */}
          {page==="import"&&importsLog.length>0&&<div style={{...S.card,margin:"0 24px 24px"}}>
            <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>Historique des imports</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:"#F9FAFB"}}>
                  {["Date","Utilisateur","Fichier","Type","Résultat","Détail"].map(h=><th key={h} style={{...S.th,fontSize:11}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {importsLog.map(log=>(
                    <tr key={log.id} style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,fontSize:11,whiteSpace:"nowrap"}}>{new Date(log.created_at).toLocaleDateString("fr-FR")} {new Date(log.created_at).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}</td>
                      <td style={{...S.td,fontSize:11}}>{log.utilisateur||"—"}</td>
                      <td style={{...S.td,fontSize:11,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={log.nom_fichier}>{log.nom_fichier||"—"}</td>
                      <td style={{...S.td,fontSize:11}}><span style={{padding:"2px 7px",borderRadius:20,background:"#EEF2FF",color:"#6366F1",fontSize:10,fontWeight:600}}>{log.type_import}</span></td>
                      <td style={{...S.td,fontSize:11}}>{log.statut==="succès"?<span style={{color:"#16A34A",fontWeight:600}}>✅ Succès</span>:<span style={{color:"#DC2626",fontWeight:600}}>❌ {log.statut}</span>}</td>
                      <td style={{...S.td,fontSize:11}}>
                        <span title={[
                          log.nb_clients_crees>0?`${log.nb_clients_crees} client(s) créé(s)`:"",
                          log.nb_clients_maj>0?`${log.nb_clients_maj} client(s) mis à jour`:"",
                          log.nb_factures_ajoutees>0?`${log.nb_factures_ajoutees} facture(s) ajoutée(s)`:"",
                          log.nb_factures_maj>0?`${log.nb_factures_maj} facture(s) mises à jour`:"",
                          log.nb_factures_reglees>0?`${log.nb_factures_reglees} facture(s) réglée(s) auto`:"",
                          log.nb_erreurs>0?"⚠️ "+log.nb_erreurs+" erreur(s)":"",
                          log.message_erreur||""
                        ].filter(Boolean).join(" · ")||"Aucun détail"}
                          style={{cursor:"help",background:"#F3F4F6",padding:"3px 8px",borderRadius:6,fontSize:11,display:"inline-block"}}>
                          ℹ️ Détail
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>}

          {/* ══════════ CONFIGURATION ══════════ */}
          {page==="stats"&&<StatistiquesPage
            isAdmin={isAdmin} user={user} societes={societes}
            histoGlobal={statsHistoGlobal} taches={taches}
            loading={statsLoading}
            periode={statsPeriode} setPeriode={setStatsPeriode}
            dateDeb={statsDateDeb} setDateDeb={setStatsDateDeb}
            dateFin={statsDateFin} setDateFin={setStatsDateFin}
            onLoad={loadStats} S={S}
            selectedSociete={selectedSociete} creances={creances}
          />}
          {page==="carnet"&&<CarnetDeBord
            taches={taches} societes={societes} isAdmin={isAdmin} user={user}
            tacheOnglet={tacheOnglet} setTacheOnglet={setTacheOnglet}
            tacheFiltres={tacheFiltres} setTacheFiltres={setTacheFiltres}
            showNewTache={showNewTache} setShowNewTache={setShowNewTache}
            newTache={newTache} setNewTache={setNewTache}
            addTache={addTache} updateTacheStatut={updateTacheStatut} deleteTache={deleteTache}
            TYPES_ACTION={TYPES_ACTION} S={S} utilisateurs={[...new Set(taches.map(t=>t.assignee_nom).filter(Boolean))]}
            creances={creances} clientSearch={clientSearch} setClientSearch={setClientSearch}
            showClientList={showClientList} setShowClientList={setShowClientList}
            onSyncHistorique={syncHistoriqueVersCarnet}
            selectedSociete={selectedSociete}
          />}
          {page==="config"&&isAdmin&&<div className="fi">
            <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
              {[{k:"societes",l:"🏢 Sociétés"},{k:"comptes",l:"🏦 Comptes bancaires"},{k:"users",l:"👥 Utilisateurs"},{k:"categories",l:"🏷️ Catégories"},{k:"regles",l:"⚡ Règles auto"}].map(({k,l})=>(
                <button key={k} className="bh" onClick={()=>setConfigTab(k)} style={{...configTab===k?S.btnPrimary:S.btnSecondary,padding:"9px 18px"}}>{l}</button>
              ))}
            </div>

            {/* SOCIÉTÉS */}
            {configTab==="societes"&&<>
              {/* Modal édition société */}
              {editSociete&&<Modal title={`Modifier — ${editSociete.nom}`} onClose={()=>setEditSociete(null)}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  {[{k:"nom",l:"Nom"},{k:"code",l:"Code"},{k:"email_recouvrement",l:"Email recouvrement",t:"email"},{k:"logo_url",l:"URL du logo"}].map(({k,l,t="text"})=>(
                    <div key={k}><label style={S.label}>{l}</label><input style={S.input} type={t} value={editSociete[k]||""} onChange={e=>setEditSociete(p=>({...p,[k]:e.target.value}))} /></div>
                  ))}
                  <div style={{gridColumn:"1/-1"}}><label style={S.label}>Signature email</label><textarea style={{...S.input,height:80,resize:"vertical"}} value={editSociete.signature_email||""} onChange={e=>setEditSociete(p=>({...p,signature_email:e.target.value}))} placeholder="Service comptabilité&#10;Tél : 0590...&#10;Email : ..." /></div>
                </div>
                {editSociete.logo_url&&<div style={{marginTop:12,padding:10,background:"#F9FAFB",borderRadius:8,textAlign:"center"}}>
                  <img src={editSociete.logo_url} alt="Logo" style={{maxHeight:60,maxWidth:200,objectFit:"contain"}} onError={e=>e.target.style.display="none"} />
                </div>}
                <div style={{display:"flex",gap:8,marginTop:16}}>
                  <button className="bh" onClick={updateSociete} style={{...S.btnPrimary,flex:1}}>Sauvegarder</button>
                  <button className="bh" onClick={()=>setEditSociete(null)} style={S.btnSecondary}>Annuler</button>
                </div>
              </Modal>}

              <div style={{...S.card,marginBottom:18}}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>+ Ajouter une société</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 100px 1fr 1fr 1fr auto",gap:10,alignItems:"end"}}>
                  <div><label style={S.label}>Nom</label><input style={S.input} placeholder="Ex: TLD Remorquage" value={newSociete.nom} onChange={e=>setNewSociete(p=>({...p,nom:e.target.value}))} /></div>
                  <div><label style={S.label}>Code</label><input style={S.input} placeholder="TLD" value={newSociete.code} onChange={e=>setNewSociete(p=>({...p,code:e.target.value}))} /></div>
                  <div><label style={S.label}>Email recouvrement</label><input style={S.input} type="email" placeholder="recouvrement@..." value={newSociete.email_recouvrement} onChange={e=>setNewSociete(p=>({...p,email_recouvrement:e.target.value}))} /></div>
                  <div><label style={S.label}>Signature email</label><input style={S.input} placeholder="Service comptabilité..." value={newSociete.signature_email} onChange={e=>setNewSociete(p=>({...p,signature_email:e.target.value}))} /></div>
                  <div><label style={S.label}>URL Logo</label><input style={S.input} placeholder="https://..." value={newSociete.logo_url||""} onChange={e=>setNewSociete(p=>({...p,logo_url:e.target.value}))} /></div>
                  <div><label style={{...S.label,color:"transparent"}}>.</label><button className="bh" onClick={addSociete} style={S.btnPrimary}>Ajouter</button></div>
                </div>
              </div>
              <div style={S.card}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Logo","Nom","Code","Email recouvrement","Signature",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{societes.map(s=>(
                    <tr key={s.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,width:50}}>
                        {s.logo_url?<img src={s.logo_url} alt={s.nom} style={{height:32,maxWidth:60,objectFit:"contain"}} onError={e=>e.target.style.display="none"} />:<div style={{width:32,height:32,background:"#F97316",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:13}}>{s.nom[0]}</div>}
                      </td>
                      <td style={{...S.td,fontWeight:600}}>{s.nom}</td>
                      <td style={{...S.td,color:"#6B7280"}}>{s.code}</td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{s.email_recouvrement||"—"}</td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.signature_email||"—"}</td>
                      <td style={S.td}><button className="bh" onClick={()=>setEditSociete({...s})} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>✎ Modifier</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>}

            {/* COMPTES BANCAIRES */}
            {configTab==="comptes"&&<>
              <div style={{...S.card,marginBottom:18}}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>+ Ajouter un compte bancaire</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr auto",gap:10,alignItems:"end"}}>
                  <div><label style={S.label}>Libellé</label><input style={S.input} placeholder="Ex: Compte principal" value={newCompte.libelle} onChange={e=>setNewCompte(p=>({...p,libelle:e.target.value}))} /></div>
                  <div><label style={S.label}>Banque</label><input style={S.input} placeholder="BNP, Crédit Agricole..." value={newCompte.banque} onChange={e=>setNewCompte(p=>({...p,banque:e.target.value}))} /></div>
                  <div><label style={S.label}>N° compte comptable</label><input style={S.input} placeholder="512000" value={newCompte.numero_compte_comptable} onChange={e=>setNewCompte(p=>({...p,numero_compte_comptable:e.target.value}))} /></div>
                  <div><label style={S.label}>IBAN</label><input style={S.input} placeholder="FR76..." value={newCompte.iban} onChange={e=>setNewCompte(p=>({...p,iban:e.target.value}))} /></div>
                  <div><label style={S.label}>Société</label><select style={S.input} value={newCompte.societe_id} onChange={e=>setNewCompte(p=>({...p,societe_id:e.target.value}))}><option value="">— Sélectionner —</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
                  <div><label style={{...S.label,color:"transparent"}}>.</label><button className="bh" onClick={addCompte} style={S.btnPrimary}>Ajouter</button></div>
                </div>
              </div>
              <div style={S.card}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Libellé","Banque","N° Compte compta","IBAN","Société",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{comptesBancaires.map(c=>(
                    <tr key={c.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,fontWeight:600}}>{c.libelle}</td>
                      <td style={{...S.td,color:"#6B7280"}}>{c.banque||"—"}</td>
                      <td style={{...S.td,fontFamily:"monospace",fontSize:12}}>{c.numero_compte_comptable||"—"}</td>
                      <td style={{...S.td,fontFamily:"monospace",fontSize:12,color:"#6B7280"}}>{c.iban||"—"}</td>
                      <td style={{...S.td,fontSize:12}}>{getSoc(c.societe_id)}</td>
                      <td style={S.td}><button className="bh" onClick={()=>deleteCompte(c.id)} style={S.btnDanger}>✕</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>}

            {/* UTILISATEURS */}
            {configTab==="users"&&<>
              <div style={{...S.card,marginBottom:18}}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:14}}>+ Créer un utilisateur</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 140px auto",gap:10,alignItems:"end"}}>
                  {[{k:"nom",l:"Nom",p:"Prénom Nom"},{k:"email",l:"Email",p:"email@..."},{k:"mot_de_passe",l:"Mot de passe",p:"••••••••",t:"password"}].map(({k,l,p,t="text"})=>(
                    <div key={k}><label style={S.label}>{l}</label><input style={S.input} type={t} placeholder={p} value={newUser[k]} onChange={e=>setNewUser(p=>({...p,[k]:e.target.value}))} /></div>
                  ))}
                  <div><label style={S.label}>Rôle</label><select style={S.input} value={newUser.role} onChange={e=>setNewUser(p=>({...p,role:e.target.value}))}><option value="administrateur">Administrateur</option><option value="utilisateur">Utilisateur</option></select></div>
                  <div><label style={S.label}>Société (utilisateur)</label><select style={S.input} value={newUser.societe_id||""} onChange={e=>setNewUser(p=>({...p,societe_id:e.target.value||null}))}><option value="">— Toutes (admin) —</option>{societes.map(s=><option key={s.id} value={s.id}>{s.nom}</option>)}</select></div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <label style={{...S.label,color:"transparent"}}>.</label>
                    <button className="bh" onClick={()=>addUser(false)} style={{...S.btnPrimary,fontSize:12,padding:"8px 12px"}}>Créer</button>
                    <button className="bh" onClick={()=>addUser(true)} title="Créer le compte et envoyer les identifiants par email" style={{...S.btnSecondary,fontSize:11,padding:"6px 10px"}}>✉️ Créer + Envoyer</button>
                  </div>
                </div>
              </div>
              <div style={S.card}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr>{["Nom","Email","Rôle","Société",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{users.map(u=>(
                    <tr key={u.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                      <td style={{...S.td,fontWeight:500}}>{u.nom}</td>
                      <td style={{...S.td,color:"#6B7280"}}>{u.email}</td>
                      <td style={S.td}><span style={tag(u.role)}>{u.role}</span></td>
                      <td style={{...S.td,fontSize:12,color:"#6B7280"}}>{u.societe_id?getSoc(u.societe_id):<span style={{color:"#9CA3AF",fontStyle:"italic"}}>Toutes</span>}</td>
                      <td style={S.td}>{u.id!==user.id&&<button className="bh" onClick={()=>deleteUser(u.id)} style={S.btnDanger}>✕</button>}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>}

            {/* CATÉGORIES */}
            {configTab==="regles"&&<div style={S.card}>
              <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>⚡ Règles de catégorisation automatique</div>
              <div style={{fontSize:12,color:"#6B7280",marginBottom:18}}>Appliquées automatiquement lors des imports CSV. Si le libellé contient le mot-clé, la catégorie est assignée.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 120px 200px auto",gap:10,alignItems:"end",marginBottom:20}}>
                <div><label style={S.label}>Si le libellé contient...</label><input style={S.input} placeholder='Ex: LOYER, SALAIRE, PRELEVEMENT...' value={newRegle.mot_cle} onChange={e=>setNewRegle(p=>({...p,mot_cle:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addRegle()} /></div>
                <div><label style={S.label}>Type</label>
                  <select style={S.input} value={newRegle.type} onChange={e=>setNewRegle(p=>({...p,type:e.target.value,categorie:e.target.value==="RECETTE"?"Ventes / CA":"Divers dépenses"}))}>
                    <option value="DÉPENSE">DÉPENSE</option>
                    <option value="RECETTE">RECETTE</option>
                  </select>
                </div>
                <div><label style={S.label}>Alors catégorie =</label>
                  <select style={S.input} value={newRegle.categorie} onChange={e=>setNewRegle(p=>({...p,categorie:e.target.value}))}>
                    {(newRegle.type==="RECETTE"?catsRecettes:catsDepenses).map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label style={{...S.label,color:"transparent"}}>.</label><button className="bh" onClick={addRegle} style={S.btnPrimary}>+ Ajouter</button></div>
              </div>
              {regles.length===0&&<div style={{textAlign:"center",color:"#9CA3AF",padding:24,fontSize:13}}>Aucune règle configurée — les transactions seront catégorisées manuellement</div>}
              {regles.length>0&&<table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr>{["Si libellé contient","Type","Alors catégorie","Priorité",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>{regles.map((r,i)=>(
                  <tr key={r.id} className="rh" style={{borderTop:"1px solid #F3F4F6"}}>
                    <td style={{...S.td,fontWeight:600}}>
                      <span style={{background:"#F3F4F6",borderRadius:4,padding:"2px 8px",fontFamily:"monospace",fontSize:12}}>{r.mot_cle}</span>
                    </td>
                    <td style={S.td}><span style={tag(r.type)}>{r.type}</span></td>
                    <td style={{...S.td,color:"#374151"}}>{r.categorie}</td>
                    <td style={{...S.td,color:"#9CA3AF",fontSize:12}}>#{i+1}</td>
                    <td style={S.td}><button className="bh" onClick={()=>deleteRegle(r.id)} style={S.btnDanger}>✕</button></td>
                  </tr>
                ))}</tbody>
              </table>}
              <div style={{marginTop:16,padding:12,background:"#FFF7ED",borderRadius:8,fontSize:12,color:"#92400E"}}>
                💡 Les règles sont appliquées dans l'ordre. La première règle qui correspond est utilisée. Exemple : si vous ajoutez "LOYER" → Loyer, tous les relevés contenant "LOYER" seront classés automatiquement.
              </div>
            </div>}

            {configTab==="categories"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
              <div style={S.card}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>Catégories Recettes</div>
                <div style={{fontSize:12,color:"#6B7280",marginBottom:14}}>Les catégories par défaut ne peuvent pas être supprimées</div>
                {DEFAULT_CAT_RECETTES.map(c=>(
                  <div key={c} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #F9FAFB"}}>
                    <span style={{fontSize:13,color:"#374151"}}>{c}</span>
                    <span style={{fontSize:10,color:"#9CA3AF"}}>Défaut</span>
                  </div>
                ))}
                {(categoriesCustom.recettes||[]).map(c=>(
                  <div key={c} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #F9FAFB"}}>
                    <span style={{fontSize:13,color:"#374151"}}>{c}</span>
                    <button className="bh" onClick={()=>removeCatRecette(c)} style={{...S.btnDanger,padding:"2px 8px",fontSize:11}}>✕</button>
                  </div>
                ))}
                <div style={{display:"flex",gap:8,marginTop:14}}>
                  <input style={{...S.input,flex:1}} placeholder="Nouvelle catégorie recette..." value={newCatRecette} onChange={e=>setNewCatRecette(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCatRecette()} />
                  <button className="bh" onClick={addCatRecette} style={S.btnPrimary}>+</button>
                </div>
              </div>
              <div style={S.card}>
                <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:4}}>Catégories Dépenses</div>
                <div style={{fontSize:12,color:"#6B7280",marginBottom:14}}>Les catégories par défaut ne peuvent pas être supprimées</div>
                {DEFAULT_CAT_DEPENSES.map(c=>(
                  <div key={c} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #F9FAFB"}}>
                    <span style={{fontSize:13,color:"#374151"}}>{c}</span>
                    <span style={{fontSize:10,color:"#9CA3AF"}}>Défaut</span>
                  </div>
                ))}
                {(categoriesCustom.depenses||[]).map(c=>(
                  <div key={c} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #F9FAFB"}}>
                    <span style={{fontSize:13,color:"#374151"}}>{c}</span>
                    <button className="bh" onClick={()=>removeCatDepense(c)} style={{...S.btnDanger,padding:"2px 8px",fontSize:11}}>✕</button>
                  </div>
                ))}
                <div style={{display:"flex",gap:8,marginTop:14}}>
                  <input style={{...S.input,flex:1}} placeholder="Nouvelle catégorie dépense..." value={newCatDepense} onChange={e=>setNewCatDepense(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCatDepense()} />
                  <button className="bh" onClick={addCatDepense} style={S.btnPrimary}>+</button>
                </div>
              </div>
            </div>}
          </div>}

        </div>
      </div>
    </div>
  );
}
export default App;
